// Phase 2 Round 5 extraction (verbatim).
import React from 'react';
import {
  useAppDraftGuard,
  useAppLocation as useLocation,
  useAppNavigate as useNavigate,
} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {resolveHousingForReport, setHousingAnchorFromReport} from '../lib/layerHousing.js';
import {wcfSendEmail} from '../lib/email.js';
import {uploadDailyPhoto, MAX_PHOTOS_PER_REPORT} from '../lib/dailyPhotos.js';
import {newClientSubmissionId} from '../lib/clientSubmissionId.js';
import {useOfflineSubmit} from '../lib/useOfflineSubmit.js';
import {formatBroilerBatchLabel} from '../lib/broilerBatchMeta.js';
import {todayCentralISO} from '../lib/dateUtils.js';
import {
  checkDailyDuplicate,
  checkInSubmissionDuplicates,
  dailyReportPath,
  duplicateUpdatePatch,
  formatDuplicateError,
  friendlyDailyDbError,
  identityKey,
} from '../lib/dailyDuplicateCheck.js';
import {updateDailyReport} from '../lib/dailyReportsApi.js';
import type {DailyReportAuthState} from '../lib/dailyReportsApi.js';
import DuplicateReportNotice from './DuplicateReportNotice.jsx';
import './duplicateRecovery.css';
import {
  crossLabelDuplicateMessage,
  decideDuplicates,
  isPristineForm,
  partialFailureMessage,
  shouldAdvanceDate,
} from './duplicateRecovery.js';
import type {DuplicateHit} from './duplicateRecovery.js';
import {
  isOperationConflict,
  isOwnOperationRow,
  operationIdFor,
  operationRows,
  retainOrMintOperation,
  retryDailyReportAnchors,
  submitDailyReportOperation,
} from '../lib/dailyReportOperationApi.js';
import type {DailyAnchorIntent, DailyOperationKind, RetainedDailyOperation} from '../lib/dailyReportOperationApi.js';
import {queuedReportActor} from '../lib/queuedReportApi.js';
import {lastAuthenticatedUserId} from '../lib/authIdentityMemory.js';
import DailyPhotoCapture from './DailyPhotoCapture.jsx';
import StuckSubmissionsModal from './StuckSubmissionsModal.jsx';
import AppSetupModal from './AppSetupModal.jsx';
import LockedSubmitter from './LockedSubmitter.jsx';
import {renderCattleIconLabel} from '../components/CattleIcon.jsx';
import {getProgramColor} from '../lib/programColors.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import PlannerIcon, {PlannerIconLabel} from '../components/PlannerIcon.jsx';
import {ANIMAL_ICON_KEYS} from '../lib/plannerIcons.js';
import {sb} from '../lib/supabase.js';
import type {BroilerBatchMetaEntry} from '../lib/broilerBatchMeta.js';
import type {CSSProperties} from 'react';
import type {SessionSubmitter} from './webformsRoutes.js';
import {eggGroupNameDefaults} from './eggGroupDefaults.js';
import {
  filterActiveLayerGroups,
  isActiveLayerGroupStatus,
  isLayerGroupStatusFailed,
  isLayerGroupStatusResolved,
  PENDING_LAYER_GROUP_STATUS,
  rejectStaleLayerGroupSelections,
  selectableLayerGroupNames,
} from '../lib/layerGroupStatus.js';
import type {LayerGroupStatusResolution} from '../lib/layerGroupStatus.js';
import type {StuckSubmissionRow} from './StuckSubmissionsModal.tsx';
import {loadQueuedReportRecovery, recoveryText, type QueuedReportRecovery} from '../lib/offlineSubmissionRecovery.js';
import {
  HUB_DRAFT_KIND,
  hydrateFlatHubForm,
  buildHubInputPayload,
  clearHubDraftCas,
  freezeHubInputs,
  assertHubDraftActor,
  discardHubDraft,
  draftHasFormAnswers,
  hubDraftCsidFor,
  hubDraftInstanceIdOf,
  listHubDrafts,
  loadHubDraft,
  loadHubDraftCustody,
  newHubDraftInstanceId,
  preserveHubDraft,
  saveHubDraftCas,
  withRecoveredInputChoice,
  hubCarrierRemovalOf,
} from './webformHubRecovery.js';
import type {HubDraft, HubDraftClearResult, PreservedHubDraft, RecoveredInputRow} from './webformHubRecovery.js';
import type {LocalDraftStamp, QueuedSubmission} from '../lib/offlineQueue.js';
import {
  HUB_CARRIER_PENDING_MESSAGE,
  carryHubDraft,
  clearActiveHubSession,
  describeHubDraftRow,
  discardHubCarrierEntry,
  getActiveHubSession,
  isHubCarrierMemoryRow,
  listHubCarrierRows,
  readHubCarrierSnapshot,
  recordActiveHubSession,
  releaseHubCarrierEntry,
  restoreHubCarrierEntry,
  settleHubCarrier,
  subscribeHubCarrier,
} from './hubDraftCarrier.js';
import type {HubCarrierEntry} from './hubDraftCarrier.js';
import type {SowTagsByGroup} from '../lib/pigSowGroups.js';
import {SOWS_GROUP_VALUE, SOW_GROUP_NUMBERS, sowGroupLabel} from '../lib/pigSowGroups.js';
import {daysSinceLastMove, daysSinceMoveText} from '../lib/pigDaysSinceMove.js';
import {
  buildSowRowPayloads,
  combinedSowState,
  extraGroupsSowsRefusal,
  isSowsSectionMode,
  sowPartialMessage,
  sowSectionFieldValues,
  sowSectionsOf,
  withSowSectionField,
} from './pigSowSubmit.js';
import type {SowRowOutcome, SowSection} from './pigSowSubmit.js';
import {groupMovedChoice, groupMovedRefusal} from './pigGroupMoved.js';

// The hub consumes operator-edited jsonb/config records from several legacy
// tables. These records intentionally remain open at this boundary; the
// component narrows only the fields used by each form path. The nested config
// shapes are named so array callbacks remain typed while the legacy payload
// fields stay open for the six independently evolving forms.
type WebformHubField = {
  id: string;
  label?: string;
  enabled?: boolean;
  required?: boolean;
};
type WebformHubSection = {fields?: WebformHubField[]};
type WebformHubConfigForm = {
  id: string;
  sections?: WebformHubSection[];
  allowAddGroup?: boolean;
};
export type WebformHubRecord = {
  [key: string]: any;
  id?: string;
  name?: string;
  status?: string;
  value?: string;
  label?: string;
  webforms?: WebformHubConfigForm[];
  sections?: WebformHubSection[];
  fields?: WebformHubField[];
  broilerGroups?: Array<string | null | undefined>;
  layerGroups?: WebformHubRecord[];
};
export type WebformHubLookupConfig = {
  fullConfig: WebformHubRecord | null;
  broilerGroups: Array<string | null | undefined>;
  broilerBatchMeta: BroilerBatchMetaEntry[];
  webformSettings: WebformHubRecord;
  activePigGroups: WebformHubRecord[];
  housingBatchMap: Record<string, any>;
};
// Bridge-injected pig daily inputs (webform.hub.pigContext). `moveHistory` is
// keyed by group label; a missing label means its read failed.
export type WebformHubPigContext = {
  sowTagsByGroup: SowTagsByGroup | null;
  moveHistory: Record<string, {dates: string[]; complete: boolean}>;
};
export type WebformHubResourceProps = {
  webformHubLookupConfig?: WebformHubLookupConfig | null;
  webformHubFeedInputs?: WebformHubRecord[];
  // The two layer directories. They are bridge-injected, NOT entry props: the
  // authenticated seam reads layer_batches / layer_housings in its own scope so
  // this presenter no longer consumes App's boot arrays.
  layerBatches?: WebformHubRecord[];
  layerHousings?: WebformHubRecord[];
  // The two app_store documents, on the same terms. `layerGroups` is the
  // ppp-layer-groups-v1 blob and `webformsConfig` the ppp-webforms-v1 admin
  // document; both used to arrive as WebformsEntry props off App's global boot
  // and are now read by the bridge through layer.eggDailys.groups and
  // webform.pigDailys.config. Do not reinstate either as an entry prop — a prop
  // hand-off in a domain entry is what the provenance derivation scores as
  // consumption, and it would make the app_store retirement a false claim.
  layerGroups?: WebformHubRecord[];
  // The canonical read's three-state verdict for the blob above, and the retry
  // that clears a failed one. The ROWS alone cannot express "not answered yet":
  // an empty array and an unreachable document look identical, and this
  // surface's layer dropdown is otherwise built from mirrors that would happily
  // supply a retired group in that moment.
  layerGroupStatus?: LayerGroupStatusResolution;
  onRetryLayerGroupStatus?: (() => void) | null;
  webformsConfig?: WebformHubRecord;
  // The pig daily form's sow tags per sow group and each group's recent
  // group-moved = Yes dates, read by the authenticated bridge through
  // webform.hub.pigContext (this presenter may not read app_store or
  // pig_dailys). Null while pending or when unavailable.
  pigContext?: WebformHubPigContext | null;
  refreshPigContext?: () => Promise<unknown>;
  refreshEggDailysProjection?: () => Promise<unknown>;
  refreshLayerDailysProjection?: () => Promise<unknown>;
  // The signed-in caller's role and id, injected by the authenticated bridge
  // so this presenter stays auth-free. Used only to tell a submitter whether a
  // report blocking theirs is their own and still editable by them.
  reportEditor?: DailyReportAuthState;
};
type WebformHubFormValues = WebformHubRecord;
// One refused hen-count anchor, kept so the success screen can say which
// housing did not move and retry JUST that anchor (never the report).
type AnchorFailure = {
  label: string;
  count: number;
  date: string;
  reason: string;
  operationId?: string;
  rowIndex?: number;
};
type WebformHubPhotoStatusSetter = React.Dispatch<React.SetStateAction<string[]>>;
type WebformHubHook = ReturnType<typeof useOfflineSubmit>;
type WebformHubInputRow = RecoveredInputRow;
type WebformHubCattleForm = WebformHubRecord & {
  date: string;
  teamMember: string;
  herd: string;
  feeds: WebformHubInputRow[];
  minerals: WebformHubInputRow[];
};
type WebformHubSheepForm = WebformHubRecord & {
  date: string;
  teamMember: string;
  flock: string;
  feeds: WebformHubInputRow[];
  minerals: WebformHubInputRow[];
};

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return String(error);
}
type WebformHubProps = WebformHubResourceProps & {
  sessionSubmitter?: SessionSubmitter;
  hideWeighIns?: boolean;
  hideCattleLog?: boolean;
  /**
   * Form ids whose per-program History link may be shown. Decided by the
   * caller from the access module, like hideWeighIns/hideCattleLog above: a
   * link this caller would be bounced from must not be offered at all.
   */
  dailyHistoryPrograms?: readonly string[] | null;
};

const EMPTY_WEBFORM_HUB_LOOKUP_CONFIG: WebformHubLookupConfig = {
  fullConfig: null,
  broilerGroups: [],
  broilerBatchMeta: [],
  webformSettings: {},
  activePigGroups: [],
  housingBatchMap: {},
};

const DAILY_FORM_PROGRAMS = {
  broiler: 'broiler',
  layer: 'layer',
  egg: 'egg',
  pig: 'pig',
  cattle: 'cattle',
  sheep: 'sheep',
};

const WebformHub = ({
  webformHubLookupConfig = EMPTY_WEBFORM_HUB_LOOKUP_CONFIG,
  webformHubFeedInputs = [],
  layerBatches = [],
  layerHousings = [],
  layerGroups = [],
  layerGroupStatus = PENDING_LAYER_GROUP_STATUS,
  onRetryLayerGroupStatus = null,
  webformsConfig,
  pigContext = null,
  refreshPigContext,
  refreshEggDailysProjection,
  refreshLayerDailysProjection,
  reportEditor = null,
  sessionSubmitter,
  hideWeighIns = false,
  hideCattleLog = false,
  dailyHistoryPrograms = null,
}: WebformHubProps) => {
  const {useState, useEffect} = React;
  const activeSessionForInit = (() => {
    const active = getActiveHubSession();
    const liveSubject = lastAuthenticatedUserId();
    if (active && liveSubject && active.owner === liveSubject) {
      return active;
    }
    return null;
  })();
  // Lane 1 CP1: on the authenticated path the submitter is the signed-in user,
  // locked across all six daily sub-forms. The roster dropdowns are replaced by
  // the signed-in identity and a single effect keeps every form's teamMember
  // pinned (covers initial state, the form-switch prefill, and post-submit
  // reset without touching each path).
  const lockedName = sessionSubmitter?.name || '';
  const submitterLocked = !!lockedName;
  const loadedConfig = webformHubLookupConfig?.fullConfig || null;
  const [showAppSetup, setShowAppSetup] = useState(false);
  const broilerGroupsFromDb = webformHubLookupConfig?.broilerGroups || [];
  const broilerMeta = webformHubLookupConfig?.broilerBatchMeta || [];
  const wfSettings = webformHubLookupConfig?.webformSettings || {};
  const pigGroupsFromDb = webformHubLookupConfig?.activePigGroups || [];
  const housingBatchMap = webformHubLookupConfig?.housingBatchMap || {};
  const todayStr = todayCentralISO;

  // URL-driven activeForm: /dailys → selector, /dailys/<sub> → sub-form.
  // switchForm(X) pushes a history entry so the browser back button
  // traverses selector ↔ sub-form correctly. App's compatibility URL adapter treats
  // every /dailys/* path as view='webformhub' (see the adapter branch).
  // Legacy /webforms/* aliases redirect to /dailys/* in App before
  // this component sees pathname.
  const location = useLocation();
  const navigate = useNavigate();
  const SUB_FORMS = ['broiler', 'layer', 'pig', 'cattle', 'egg', 'sheep'];
  const activeForm = (() => {
    const parts = location.pathname.split('/');
    const sub = parts[2];
    return SUB_FORMS.includes(sub) ? sub : null;
  })() as keyof typeof DAILY_FORM_PROGRAMS;
  const setActiveForm = (f: string | null) => {
    if (recoveryOperation.current) return;
    navigate(f ? `/dailys/${f}` : '/dailys');
  };
  const [done, _setDone] = useState(false);
  const doneRef = React.useRef(false);
  const draftSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const setDone = React.useCallback((val: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof val === 'function' ? val(doneRef.current) : val;
    doneRef.current = next;
    if (next && draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    _setDone(next);
  }, []);
  const [submitPending, setSubmitting] = useState(false);
  const [recoveryWorking, setRecoveryWorking] = useState(false);
  const recoveryOperation = React.useRef<'opening' | 'preserving' | 'submitting' | null>(null);
  const submitting = submitPending || recoveryWorking;
  const [recovery, setRecovery] = useState<QueuedReportRecovery | null>(null);
  const [localDraftCsid, setLocalDraftCsid] = useState<string | null>(() => activeSessionForInit?.csid ?? null);
  const [localDrafts, setLocalDrafts] = useState<StuckSubmissionRow[]>([]);
  const knownHubDraftCsids = React.useRef(new Set<string>());
  const priorHubDraft = React.useRef<
    Array<{
      snapshot: HubDraft;
      recovery: QueuedReportRecovery | null;
      localDraftCsid: string | null;
      // Custody travels with the fields: Cancel Recovery restores the prior
      // editing session object (its CAS chain is untouched while another
      // session owned the mount) and its pending owner.
      session: {instanceId: string; csid: string; stamp: LocalDraftStamp | null; attempt: number} | null;
      subject: string | null;
      // A teardown carrier opened from memory: Cancel offers it again.
      carrier: HubCarrierEntry | null;
    }>
  >([]);
  // A resumed LEGACY draft (unstamped preserve-path row) has no CAS chain to
  // adopt, and the stamped submit clear can never remove it. Once this
  // session durably commits the resumed answers — or a submit sends them —
  // the stale row retires through the actor-owned discard path. The arm is
  // EPOCH-BOUND: armedEdits is the edit token at the handoff, and only a
  // save that started after the resumed content's own schedule (a later
  // token) can have made THOSE answers durable — a pre-resume ACK landing in
  // the handoff race must never arm or retire the row. Cancel Recovery pops
  // it: without a durable save the row IS the only copy of those answers.
  const pendingLegacyRetireRef = React.useRef<{
    csid: string;
    contentIdentity: string | null;
    armedEdits: number;
  } | null>(null);
  // An opened removal TOMBSTONE (a teardown carrier of an emptied saved
  // draft) retires only once its intent has settled through this mount:
  // the exact owned clear of the source revision deleted it, or a save that
  // started after the Open replaced it durably. Same epoch rule as above.
  const pendingCarrierRetireRef = React.useRef<{
    csid: string;
    stamp: LocalDraftStamp;
    sourceCsid: string;
    armedEdits: number;
  } | null>(null);
  const preservedHubDraft = React.useRef<PreservedHubDraft | null>(null);
  // ── Draft editing session ─────────────────────────────────────────────────
  // The session's identity is stable for the mount: its draft key derives
  // from the instance id, so a save NEVER mints a submission id, a second tab
  // or a remount forks to its own key instead of overwriting, and every
  // write is a stamped CAS over answers + original photo bytes. `stamp` is
  // the last durably written revision OF THIS SESSION; a late save can
  // commit only under it, and a submit clears only the exact revision it
  // submitted.
  const hubAliveRef = React.useRef(true);
  React.useEffect(() => {
    hubAliveRef.current = true;
    return () => {
      hubAliveRef.current = false;
    };
  }, []);
  const draftSessionRef = React.useRef<{
    instanceId: string;
    csid: string;
    stamp: LocalDraftStamp | null;
    attempt: number;
  } | null>(
    activeSessionForInit
      ? {
          instanceId: hubDraftInstanceIdOf(activeSessionForInit.csid) || activeSessionForInit.csid,
          csid: activeSessionForInit.csid,
          stamp: activeSessionForInit.stamp,
          attempt: 0,
        }
      : null,
  );
  type CapturedDraft = {
    owner: string;
    csid: string | null;
    stamp: LocalDraftStamp | null;
    instanceId: string | null;
    editsAtCapture: number;
    formKind: string;
    formSnapshot: WebformHubRecord;
    extraSnapshot: unknown[];
    photoSnapshot: File[];
  };
  const submittedDraftRef = React.useRef<CapturedDraft | null>(null);
  const programmaticResetRef = React.useRef(false);
  // Monotonic token for "the RAM content changed": bumped by every scheduled
  // edit, captured by every save. Completion compares tokens, so an in-flight
  // save can never mark newer unsaved content durable or paint Saved over it.
  const draftEditsRef = React.useRef(0);
  const [draftStatus, setDraftStatus] = React.useState<{
    state: 'idle' | 'saving' | 'saved' | 'failed';
    detail?: string;
    at?: string;
  }>({state: 'idle'});
  const draftSavingRef = React.useRef(false);
  // The in-flight save's own promise: Open JOINS this ACK instead of racing
  // it, so custody never transfers over an unsettled prior chain.
  const draftSavingPromiseRef = React.useRef<Promise<boolean> | null>(null);
  const draftDirtyRef = React.useRef(Boolean(activeSessionForInit?.hasUnsettledWork));
  // The author captured when the pending work was SCHEDULED — not re-read at
  // save time, which would commit the previous author's RAM answers under a
  // switched account. A scheduled owned-clear carries its author the same way.
  const clearIntentRef = React.useRef<{
    target: {csid: string; stamp: LocalDraftStamp};
    capturedSubject: string | null;
    verify: () => Promise<string | null>;
  } | null>(null);
  const scheduledSubjectRef = React.useRef<string | null>(activeSessionForInit?.owner ?? null);
  // The newest OWNED snapshot: taken only when an edit is scheduled under its
  // own author (never a quarantined foreign/unresolved one), with the session
  // it belongs to. Teardown hands it to the carrier if it is still unsettled.
  const carrierCandidateRef = React.useRef<{
    owner: string | null;
    snapshot: HubDraft;
    edits: number;
    removal: boolean;
    session: {instanceId: string; csid: string; stamp: LocalDraftStamp | null; attempt: number} | null;
  } | null>(
    activeSessionForInit?.snapshot
      ? {
          owner: activeSessionForInit.owner,
          snapshot: activeSessionForInit.snapshot,
          edits: 0,
          removal: false,
          session: {
            instanceId: hubDraftInstanceIdOf(activeSessionForInit.csid) || activeSessionForInit.csid,
            csid: activeSessionForInit.csid,
            stamp: activeSessionForInit.stamp,
            attempt: 0,
          },
        }
      : null,
  );
  const verify = () => readLiveHubSubject();
  const clearPendingRef = React.useRef(false);
  // The in-flight owned-clear's OWN promise: concurrent flushes/clears JOIN
  // it instead of re-entering with lost custody.
  const clearInFlightPromiseRef = React.useRef<Promise<boolean> | null>(null);
  // Captured at SCHEDULE time: the exact durable base the scheduled clear
  // may remove. An N1 save advancing the session stamp makes the old clear's
  // CAS a 'kept' — it can never delete the newer revision.
  const clearTargetRef = React.useRef<{csid: string; stamp: LocalDraftStamp} | null>(null);
  // True while a save/clear failure is unresolved: the unsafe-to-leave
  // predicate counts it even when the fields are empty (a failed owned-clear
  // must not look safe just because there is no content left).
  const draftUnsettledFailureRef = React.useRef(false);
  function draftSession() {
    if (!draftSessionRef.current) {
      const instanceId = newHubDraftInstanceId();
      draftSessionRef.current = {instanceId, csid: hubDraftCsidFor(instanceId), stamp: null, attempt: 0};
    }
    return draftSessionRef.current;
  }
  async function quiesceAutosaveBarrier() {
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    while (draftSavingPromiseRef.current) {
      const p = draftSavingPromiseRef.current;
      await p;
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
      if (draftSavingPromiseRef.current === p) {
        draftSavingPromiseRef.current = null;
      }
    }
  }
  // The confirmed submit's exact revision AND the session/edit identity it
  // came from, captured when Submit starts. The clear afterwards deletes only
  // that stamp, and local custody is retired only when THIS session, with no
  // newer edits, still holds the key.
  function captureSubmittedDraft(formKind?: string) {
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    const session = draftSessionRef.current;
    const currentStamp = session?.stamp ?? null;
    const targetKind = formKind || activeForm;
    const owner = scheduledSubjectRef.current ?? lastAuthenticatedUserId();
    if (!targetKind || !owner) {
      submittedDraftRef.current = null;
      return null;
    }
    const currentDraft = captureHubDraft();
    const snap = (currentDraft.forms as Record<string, WebformHubRecord>)[targetKind];
    const extras = (currentDraft.extras as Record<string, unknown[]>)[targetKind] || [];
    const photos = (currentDraft.photos as Record<string, File[]>)[targetKind] || [];
    const captured: CapturedDraft = {
      owner,
      csid: session?.csid ?? null,
      stamp: currentStamp,
      instanceId: session?.instanceId ?? null,
      editsAtCapture: draftEditsRef.current,
      formKind: targetKind,
      formSnapshot: snap ? JSON.parse(JSON.stringify(snap)) : {},
      extraSnapshot: JSON.parse(JSON.stringify(extras)),
      photoSnapshot: [...photos],
    };
    submittedDraftRef.current = captured;
    return captured;
  }
  async function prepareSubmit(formKind: string): Promise<string | false> {
    const captured = captureSubmittedDraft(formKind);
    const frozenOwner = captured?.owner ?? scheduledSubjectRef.current ?? lastAuthenticatedUserId();
    if (!frozenOwner) {
      setErr('Sign in before saving this report. Your answers are still on this form.');
      return false;
    }
    await quiesceAutosaveBarrier();
    if (!hubAliveRef.current) return false;
    if (submittedDraftRef.current && draftSessionRef.current?.stamp) {
      submittedDraftRef.current.stamp = draftSessionRef.current.stamp;
      submittedDraftRef.current.csid = draftSessionRef.current.csid;
      submittedDraftRef.current.instanceId = draftSessionRef.current.instanceId;
    }
    if (!(await assertIdentityCurrent(frozenOwner))) {
      return false;
    }
    return frozenOwner;
  }
  async function assertIdentityCurrent(expectedOwner: string | false | null): Promise<boolean> {
    if (!hubAliveRef.current || !expectedOwner) return false;
    const liveSubject = await readLiveHubSubject();
    if (liveSubject && liveSubject !== expectedOwner) {
      setErr('The signed-in account changed during submission. Operation aborted; your edits were kept.');
      return false;
    }
    const current = await queuedReportActor(sb);
    if (current && current !== expectedOwner) {
      setErr('The signed-in account changed during submission. Operation aborted; your edits were kept.');
      return false;
    }
    return true;
  }
  async function assertSubmissionCurrency(expectedOwner: string | false | null): Promise<boolean> {
    if (!hubAliveRef.current || !expectedOwner) return false;
    const liveSubject = await readLiveHubSubject();
    if (!liveSubject || liveSubject !== expectedOwner) {
      setErr('The signed-in account changed or expired during submission. Operation aborted; your edits were kept.');
      return false;
    }
    const current = await queuedReportActor(sb);
    if (!current || current !== expectedOwner) {
      setErr('The signed-in account changed during submission. Operation aborted; your edits were kept.');
      return false;
    }
    if (!hubAliveRef.current) return false;
    return true;
  }
  // The Hub's writer identity is the signed-in account's subject, proven by
  // the SDK session and re-read after every await; the surface is
  // login-required with name-only submitters, so the subject IS the author
  // binding (no role/preview/epoch dimensions exist here).
  async function readLiveHubSubject(): Promise<string | null> {
    try {
      const {data} = await sb.auth.getSession();
      const subject = data.session?.user?.id ?? null;
      return subject && hubAliveRef.current ? subject : null;
    } catch {
      return null;
    }
  }
  // The done-effect's post-clear decision, lifted out so tests drive the real
  // branch: only the exact submitted revision's own session — unchanged, with
  // no newer edits — is retired. 'kept' means a newer revision owns the key
  function otherFormsHaveContent(submittedKind?: string): boolean {
    const forms = formsRef.current;
    const allKeys = ['broiler', 'layer', 'pig', 'egg', 'cattle', 'sheep'] as const;
    const keys = submittedKind ? allKeys.filter((k) => k !== submittedKind) : allKeys;
    const hasFormsContent = keys.some(
      (key) => !isPristineForm(forms[key], freshFormsRef.current?.[key] || {}, draftExtrasRef.current[key]),
    );
    if (hasFormsContent) return true;
    const photos = {broiler: bPhotos, layer: lPhotos, pig: pPhotos, cattle: cPhotos, sheep: shPhotos};
    return (['broiler', 'layer', 'pig', 'cattle', 'sheep'] as const)
      .filter((k) => k !== submittedKind)
      .some((k) => (photos[k] || []).length > 0);
  }

  // and stays; anything newer in RAM keeps its dirty custody and persists.
  // On 'deleted' the session's durable base is gone with the row: its stamp
  // resets, so its next save starts fresh instead of conflicting forever.
  async function finishSubmittedDraftClear(
    result: 'deleted' | 'kept' | 'absent' | 'not-current' | 'failed',
    captured: CapturedDraft,
  ) {
    if (result !== 'deleted' && result !== 'absent') return;
    const session = draftSessionRef.current;
    if (session) {
      if (
        session.stamp &&
        captured.stamp &&
        session.stamp.mountId === captured.stamp.mountId &&
        session.stamp.revision === captured.stamp.revision
      ) {
        session.stamp = null;
      }
      if (
        (!captured.instanceId || session.instanceId === captured.instanceId) &&
        (!captured.stamp || draftEditsRef.current === captured.editsAtCapture)
      ) {
        draftSessionRef.current = null;
      }
    }
    const settledUnchanged =
      !session ||
      ((!captured.instanceId || session.instanceId === captured.instanceId) &&
        (!captured.stamp || draftEditsRef.current === captured.editsAtCapture));
    if (settledUnchanged) {
      lastDispositionRef.current = null;
      scheduledSubjectRef.current = null;
      carrierCandidateRef.current = null;
      setLocalDraftCsid((current) => (captured.csid && current === captured.csid ? null : current));
      clearActiveHubSession();
      draftDirtyRef.current = false;
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
      clearIntentRef.current = null;
      clearPendingRef.current = false;
      setDraftStatus({state: 'idle'});
    }
  }
  const [err, setErr] = useState('');
  const [lastGroup, setLastGroup] = useState('');
  // A submission blocked by reports that already exist for the same date and
  // group. Cleared at the start of every submit and whenever an input changes.
  const [dupHits, setDupHits] = useState<DuplicateHit[] | null>(null);
  const dupHitsRef = React.useRef<DuplicateHit[] | null>(null);
  dupHitsRef.current = dupHits;
  // The existing-report ids the user just agreed to update. Taken (and
  // cleared) at the very start of the submit it triggers, which re-validates
  // the CURRENT form and re-runs the duplicate checks; the update happens only
  // if those checks find exactly these reports (decideDuplicates).
  const confirmedDupIdsRef = React.useRef<ReadonlySet<string> | null>(null);
  function takeDuplicateConfirmation(): ReadonlySet<string> | null {
    const confirmed = confirmedDupIdsRef.current;
    confirmedDupIdsRef.current = null;
    return confirmed;
  }
  function blockOnDuplicates(hits: DuplicateHit[]) {
    const cross = hits.length > 0 ? crossLabelDuplicateMessage(hits[0]) : null;
    setErr(cross || formatDuplicateError(hits[0].table, hits[0].record));
    setDupHits(hits);
  }
  // Acts on the duplicate checks of the current, validated records. Returns the
  // records still to insert plus the reports updated in place, or null when the
  // submit stops here (notice shown, confirmation no longer matching, or an
  // update failed). Nothing is inserted unless every confirmed update landed.
  async function actOnDuplicates(
    table: string,
    recs: WebformHubRecord[],
    hits: DuplicateHit[],
    confirmed: ReadonlySet<string> | null,
    expectedOwner?: string | null,
  ): Promise<{rest: WebformHubRecord[]; updated: DuplicateHit[]} | null> {
    const decision = decideDuplicates(reportEditor, hits, confirmed);
    if (decision.kind === 'insert') return {rest: recs, updated: []};
    if (decision.kind === 'block') {
      setSubmitting(false);
      blockOnDuplicates(decision.hits);
      return null;
    }
    if (decision.kind === 'stale') {
      setSubmitting(false);
      setErr('The form changed after you chose to update. Check it and submit again.');
      return null;
    }
    const updated: DuplicateHit[] = [];
    for (const h of decision.hits) {
      const label = String(h.record.date || '') + (h.record.batch_label ? ' · ' + String(h.record.batch_label) : '');
      try {
        if (expectedOwner && !(await assertSubmissionCurrency(expectedOwner))) {
          setSubmitting(false);
          return null;
        }
        await updateDailyReport(sb, h.table, h.existing.id, duplicateUpdatePatch(h.record), {entityLabel: label});
      } catch (e) {
        setSubmitting(false);
        setErr(partialFailureMessage(updated, friendlyDailyDbError(errorMessage(e), h.table, h.record), 'update', h));
        return null;
      }
      updated.push(h);
    }
    const updatedKeys = new Set(updated.map((h) => identityKey(h.table, h.record)));
    return {rest: recs.filter((r) => !updatedKeys.has(identityKey(table, r))), updated};
  }

  // Broiler form state
  const EMPTY_B: WebformHubRecord = {
    date: todayStr(),
    teamMember: '',
    batchLabel: '',
    feedType: '',
    feedLbs: '',
    gritLbs: '',
    groupMoved: true,
    watererChecked: true,
    mortalityCount: '',
    mortalityReason: '',
    comments: '',
  };
  const [bForm, setBForm] = useState(
    () => (activeSessionForInit?.snapshot?.forms?.broiler as WebformHubRecord) ?? EMPTY_B,
  );

  // Layer form state
  const EMPTY_L: WebformHubRecord = {
    date: todayStr(),
    teamMember: '',
    batchLabel: '',
    feedType: '',
    feedLbs: '',
    gritLbs: '',
    layerCount: '',
    groupMoved: true,
    watererChecked: true,
    mortalityCount: '',
    mortalityReason: '',
    comments: '',
  };
  const [lForm, setLForm] = useState(
    () => (activeSessionForInit?.snapshot?.forms?.layer as WebformHubRecord) ?? EMPTY_L,
  );

  // Pig form state
  const pigGroupList = [];
  const EMPTY_P: WebformHubRecord = {
    date: todayStr(),
    teamMember: '',
    batchLabel: '',
    feedLbs: '',
    pigCount: '',
    // Blank until answered (owner request 2026-11-04); submitPig refuses null.
    groupMoved: null,
    nippleDrinkerMoved: true,
    nippleDrinkerWorking: true,
    troughsMoved: true,
    fenceWalked: true,
    fenceVoltage: '',
    issues: '',
  };
  const [pForm, setPForm] = useState(() => (activeSessionForInit?.snapshot?.forms?.pig as WebformHubRecord) ?? EMPTY_P);

  // Egg form state. The four group-name defaults are PERSISTED data — they are
  // what submitEgg writes to egg_dailys.group1_name..group4_name — so their
  // derivation lives in its own module, bound to a differential oracle that
  // pins all four across the move from App's boot blob to a scoped query. See
  // src/webforms/eggGroupDefaults.ts.
  const layerGroupsResolved = isLayerGroupStatusResolved(layerGroupStatus);
  const layerGroupsFailed = isLayerGroupStatusFailed(layerGroupStatus);
  // Gated on the READ, not on the row count. These four strings are PERSISTED
  // data - submitEgg writes them to egg_dailys.group1_name..group4_name - so
  // seeding them from an unanswered or failed read would write four blanks that
  // are indistinguishable from a farm with no active groups. The arrival effect
  // below already applies them the moment they exist.
  const eggGroupDefaults = layerGroupsResolved ? eggGroupNameDefaults(layerGroups) : ['', '', '', ''];
  const EMPTY_E: WebformHubRecord = {
    date: todayStr(),
    teamMember: '',
    g1n: eggGroupDefaults[0],
    g1c: '',
    g2n: eggGroupDefaults[1],
    g2c: '',
    g3n: eggGroupDefaults[2],
    g3c: '',
    g4n: eggGroupDefaults[3],
    g4c: '',
    dozensOnHand: '',
    dozensAllocatedCoastalRancher: '',
    comments: '',
  };
  const [eForm, setEForm] = useState(() => (activeSessionForInit?.snapshot?.forms?.egg as WebformHubRecord) ?? EMPTY_E);
  // WHY THIS EFFECT EXISTS, AND WHY IT IS NOT A REPAIR.
  //
  // Those four names are DEFAULTS and useState reads them exactly once. While
  // `layerGroups` arrived as an App prop, App's `if (!dataLoaded)` gate had
  // already resolved the ppp-layer-groups-v1 blob before this component first
  // rendered, so reading them at mount was correct. The bridge reads the blob
  // through layer.eggDailys.groups now, which has NOT resolved on the first
  // frame, so the same mount read would leave all four blank forever — and on
  // live production the fourth of them is written to egg_dailys.group4_name on
  // about one report a day. It is applied on arrival instead.
  //
  // AT MOST ONCE, AND ONLY OVER AN UNTOUCHED FORM. The ref makes it a one-shot,
  // and the updater declines if any of the four already holds a value, so an
  // operator's selection can never be overwritten by a late resolve. The
  // remaining window is an operator who blanks ALL FOUR selects inside the
  // read's own latency; nothing else reaches this state.
  //
  // resetAndAnother is deliberately untouched: it spreads EMPTY_E, which is
  // recomputed on every render and therefore already carries the resolved
  // defaults by the time any report has been submitted.
  const eggDefaultsAppliedRef = React.useRef(Boolean(activeSessionForInit?.snapshot?.forms?.egg));
  useEffect(() => {
    if (eggDefaultsAppliedRef.current) return;
    const g1 = EMPTY_E.g1n;
    const g2 = EMPTY_E.g2n;
    const g3 = EMPTY_E.g3n;
    const g4 = EMPTY_E.g4n;
    if (!g1 && !g2 && !g3 && !g4) return;
    eggDefaultsAppliedRef.current = true;
    setEForm((f) => (f.g1n || f.g2n || f.g3n || f.g4n ? f : {...f, g1n: g1, g2n: g2, g3n: g3, g4n: g4}));
  }, [EMPTY_E.g1n, EMPTY_E.g2n, EMPTY_E.g3n, EMPTY_E.g4n]);

  // Cattle form state + active feeds list
  const cattleFeedInputs = (webformHubFeedInputs || []).filter((f) => f.status !== 'inactive');
  const EMPTY_C: WebformHubCattleForm = {
    date: todayStr(),
    teamMember: '',
    herd: '',
    feeds: [{feedId: '', qty: '', isCreep: false}],
    minerals: [{feedId: '', lbs: ''}],
    fenceVoltage: '',
    waterChecked: true,
    mortalityCount: '',
    mortalityReason: '',
    issues: '',
  };
  const [cForm, setCForm] = useState<WebformHubCattleForm>(
    () => (activeSessionForInit?.snapshot?.forms?.cattle as WebformHubCattleForm) ?? EMPTY_C,
  );
  // Sheep form state — cattle-parity shape (feeds + minerals jsonb). Feed
  // master comes from cattleFeedInputs filtered by sheep herd_scope.
  const EMPTY_S: WebformHubSheepForm = {
    date: todayStr(),
    teamMember: '',
    flock: '',
    feeds: [{feedId: '', qty: ''}],
    minerals: [{feedId: '', lbs: ''}],
    fenceVoltageKv: '',
    waterersWorking: true,
    comments: '',
  };
  const [sForm, setSForm] = useState<WebformHubSheepForm>(
    () => (activeSessionForInit?.snapshot?.forms?.sheep as WebformHubSheepForm) ?? EMPTY_S,
  );
  // Merged move history after submit (owner request 2026-11-04 P6) so
  // "Days since last move" updates immediately without waiting for a reload.
  const [submittedMoves, setSubmittedMoves] = useState<Record<string, string[]>>({});
  function recordSubmittedMove(label: string, date: string) {
    const key = String(label || '').trim();
    const d = String(date || '').slice(0, 10);
    if (!key || !d) return;
    setSubmittedMoves((prev) => {
      const existing = prev[key] || [];
      if (existing.includes(d)) return prev;
      return {...prev, [key]: [d, ...existing]};
    });
  }

  // The authenticated bridge owns this lookup; the config fallback remains for
  // installations whose legacy App payload has not populated the mirror yet.
  const broilerGroups = (
    (broilerGroupsFromDb.length > 0 && broilerGroupsFromDb) ||
    (loadedConfig?.broilerGroups?.length ? loadedConfig.broilerGroups : []) ||
    []
  ).filter((group): group is string => typeof group === 'string');
  // Auto-derived layer dropdown — replaces hand-maintained cfg.layerGroups.
  // Rule: show batch NAME for active batches without housings yet (pre-housing phase),
  // plus all currently-active housing NAMES.
  // Falls back to legacy cfg.layerGroups if the new tables haven't loaded yet.
  //
  // AND THE CANONICAL READ GATES ALL THREE OF ITS SOURCES.
  //
  // layer_batches, layer_housings and cfg.layerGroups each carry their own
  // `status`, and the third of those is a webform_config MIRROR republished by
  // the daily maintenance job. So after a group is switched to Inactive on
  // /layer/groups, every one of them can still say `active` for up to a day.
  // The canonical blob is injected by this surface's authenticated bridge, so
  // the veto costs no additional read and takes effect on the next render
  // instead of the next cron run.
  //
  // It is gated on the read's STATE rather than on its rows: while that read is
  // pending or failed there are no rows, an empty veto index suppresses
  // nothing, and this list would be assembled purely from the stale mirrors.
  // Once RESOLVED, a housing the blob has never heard of is untouched - and
  // that allowance is exactly why it waits for `resolved`, since until then
  // "no row for this name" is indistinguishable from "no blob".
  const layerGroupNames = (() => {
    const ab = (layerBatches || []).filter((b) => b.status === 'active');
    const ah = (layerHousings || []).filter((h) => h.status === 'active');
    const derived =
      ab.length === 0 && ah.length === 0
        ? loadedConfig?.layerGroups
            ?.filter((g) => isActiveLayerGroupStatus(g.status))
            .map((g) => String(g.name ?? g)) || []
        : [
            ...ab.filter((b) => !ah.some((h) => h.batch_id === b.id)).map((b) => String(b.name ?? '')),
            ...ah.map((h) => String(h.housing_name ?? '')),
          ];
    // No `keep` label: every control this list feeds creates a NEW report.
    return selectableLayerGroupNames(derived, layerGroupStatus);
  })();
  // THE EGG FORM'S GROUPS ARE A DIFFERENT SET, AND CONFLATING THEM REJECTS
  // VALID REPORTS.
  //
  // `layerGroupNames` above is derived from layer_batches / layer_housings —
  // the physical housings a LAYER daily is filed against. The egg form's four
  // slots are named from the canonical ppp-layer-groups-v1 rows, which is why
  // `eggGroupNameDefaults` reads that blob and not this list. On production
  // those two sets differ: the blob holds canonical egg groups that have no
  // active batch or housing of their own. Feeding the egg selector and, worse,
  // the egg submit check from the LAYER list therefore both hides a valid group
  // and refuses an ordinary egg report as "stale".
  //
  // So the egg list is derived from the same rows the defaults are: the
  // explicitly active canonical rows, in blob order, with the duplicate-name
  // fail-closed rule `filterActiveLayerGroups` already applies. It is empty
  // until the canonical read RESOLVES, for the same reason every other gated
  // list is — a pending or failed read cannot vouch for any name.
  const eggGroupNames = layerGroupsResolved
    ? filterActiveLayerGroups(layerGroups as Array<{name?: string; status?: string}>)
        .map((g) => String(g.name ?? ''))
        .filter((name) => name !== '')
    : [];
  // ONE notice, rendered under the layer group select and under the egg group
  // grid. Without it a gated list is indistinguishable from a farm that has no
  // layer groups, which is the failure mode this whole gate exists to avoid.
  const layerGroupsNotice = layerGroupsResolved ? null : (
    <div
      data-layer-groups-state={layerGroupsFailed ? 'failed' : 'pending'}
      role={layerGroupsFailed ? 'alert' : undefined}
      style={{
        marginTop: 6,
        fontSize: 12,
        color: layerGroupsFailed ? '#b91c1c' : 'var(--ink-muted)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <span>
        {layerGroupsFailed ? 'Layer groups are unavailable, so no group can be selected.' : 'Loading layer groups…'}
      </span>
      {layerGroupsFailed && onRetryLayerGroupStatus && (
        <button
          disabled={submitting}
          type="button"
          data-layer-groups-retry="1"
          onClick={onRetryLayerGroupStatus}
          style={{
            padding: '3px 10px',
            borderRadius: 10,
            border: '1px solid var(--border-strong)',
            background: 'white',
            color: 'var(--ink)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
  // Lookup helper: resolve batch_label → batch_id at submit time so historical
  // attribution stays correct even if a housing gets reassigned later.
  function resolveBatchId(label: string) {
    if (!label) return null;
    const t = String(label).toLowerCase().trim();
    const byBatch = (layerBatches || []).find(
      (b) =>
        String(b.name || '')
          .toLowerCase()
          .trim() === t,
    );
    if (byBatch) return byBatch.id;
    const byHousing = (layerHousings || []).find(
      (h) =>
        String(h.housing_name || '')
          .toLowerCase()
          .trim() === t,
    );
    return byHousing?.batch_id || null;
  }
  const cfg = loadedConfig || webformsConfig || {};
  const isEnabled = (formId: string, fieldId: string) => {
    const wf = (cfg.webforms || []).find((w) => w.id === formId);
    if (!wf) return true;
    const allFields = (wf.sections || []).flatMap((s) => s.fields || []);
    const f = allFields.find((f) => f.id === fieldId);
    return f ? f.enabled !== false : true;
  };

  // Keep every sub-form's submitter pinned to the signed-in identity on the
  // authenticated path. One effect covers initial state, the form-switch
  // prefill, and post-submit reset for all six forms.
  useEffect(() => {
    if (!submitterLocked) return;
    const pin = (f: WebformHubFormValues) => (f.teamMember === lockedName ? f : {...f, teamMember: lockedName});
    setBForm(pin);
    setLForm(pin);
    setPForm(pin);
    setEForm(pin);
    setCForm((f) => (f.teamMember === lockedName ? f : {...f, teamMember: lockedName}));
    setSForm((f) => (f.teamMember === lockedName ? f : {...f, teamMember: lockedName}));
  }, [
    submitterLocked,
    lockedName,
    bForm.teamMember,
    lForm.teamMember,
    pForm.teamMember,
    eForm.teamMember,
    cForm.teamMember,
    sForm.teamMember,
  ]);
  const isRequired = (formId: string, fieldId: string) => {
    const wf = (cfg.webforms || []).find((w) => w.id === formId);
    if (!wf) return false;
    const allFields = (wf.sections || []).flatMap((s) => s.fields || []);
    const f = allFields.find((f) => f.id === fieldId);
    return f ? f.required === true : false;
  };
  const reqStar = (formId: string, fieldId: string) =>
    isRequired(formId, fieldId) ? React.createElement('span', {style: {color: '#dc2626', marginLeft: 2}}, '*') : null;
  // Extra group sections for Add Group feature (array of {batchLabel})
  const [extraBroilerGroups, setExtraBroilerGroups] = useState<WebformHubRecord[]>(
    () => activeSessionForInit?.snapshot?.extras?.broiler ?? [],
  );
  const [extraLayerGroups, setExtraLayerGroups] = useState<WebformHubRecord[]>(
    () => activeSessionForInit?.snapshot?.extras?.layer ?? [],
  );
  const [extraPigGroups, setExtraPigGroups] = useState<WebformHubRecord[]>(
    () => activeSessionForInit?.snapshot?.extras?.pig ?? [],
  );

  // Daily-report photos (per program). Files held client-side until submit;
  // uploaded sequentially after required-field validation passes; aborted
  // entirely on any photo failure (locked decision: no partial-photo row
  // insert). Egg dailys excluded — mig 030 omitted egg_dailys.photos.
  const [bPhotos, setBPhotos] = useState<File[]>(() => activeSessionForInit?.snapshot?.photos?.broiler ?? []);
  const [bPhotoStatuses, setBPhotoStatuses] = useState<string[]>([]);
  const [lPhotos, setLPhotos] = useState<File[]>(() => activeSessionForInit?.snapshot?.photos?.layer ?? []);
  const [lPhotoStatuses, setLPhotoStatuses] = useState<string[]>([]);
  const [pPhotos, setPPhotos] = useState<File[]>(() => activeSessionForInit?.snapshot?.photos?.pig ?? []);
  const [pPhotoStatuses, setPPhotoStatuses] = useState<string[]>([]);
  const [cPhotos, setCPhotos] = useState<File[]>(() => activeSessionForInit?.snapshot?.photos?.cattle ?? []);
  const [cPhotoStatuses, setCPhotoStatuses] = useState<string[]>([]);
  const [shPhotos, setShPhotos] = useState<File[]>(() => activeSessionForInit?.snapshot?.photos?.sheep ?? []);
  const [shPhotoStatuses, setShPhotoStatuses] = useState<string[]>([]);

  function captureHubDraft(): HubDraft {
    const forms: HubDraft['forms'] = {
      broiler: bForm,
      layer: lForm,
      pig: pForm,
      egg: eForm,
      cattle: freezeHubInputs(cForm, cattleFeedInputs, 'cattle'),
      sheep: freezeHubInputs(sForm, cattleFeedInputs, 'sheep'),
    };
    // Duplicate checks already captured accepted feed/mineral snapshots. Keep
    // those exact values when Open saves the draft; today's master may differ.
    for (const form of ['cattle', 'sheep'] as const) {
      const hit = dupHitsRef.current?.find((item) => item.table === `${form}_dailys`);
      if (hit) {
        const r = hit.record;
        const hydrated = hydrateFlatHubForm(
          {formKind: `${form}_dailys`, record: {...r, feeds: r.feedsJ, minerals: r.mineralsJ}},
          lockedName,
        );
        forms[form] = {...forms[form], feeds: hydrated.values.feeds, minerals: hydrated.values.minerals};
      }
    }
    return {
      activeForm,
      forms,
      extras: {broiler: extraBroilerGroups, layer: extraLayerGroups, pig: extraPigGroups},
      photos: {broiler: bPhotos, layer: lPhotos, pig: pPhotos, cattle: cPhotos, sheep: shPhotos},
      done: doneRef.current || done,
      doneState,
      lastGroup,
      anchorFailures,
    };
  }
  function restoreHubDraft(snapshot: HubDraft) {
    eggDefaultsAppliedRef.current = true;
    setBForm(snapshot.forms.broiler);
    setLForm(snapshot.forms.layer);
    setPForm(snapshot.forms.pig);
    setEForm(snapshot.forms.egg);
    setCForm(snapshot.forms.cattle as WebformHubCattleForm);
    setSForm(snapshot.forms.sheep as WebformHubSheepForm);
    setExtraBroilerGroups(snapshot.extras.broiler);
    setExtraLayerGroups(snapshot.extras.layer);
    setExtraPigGroups(snapshot.extras.pig);
    setBPhotos(snapshot.photos.broiler);
    setLPhotos(snapshot.photos.layer);
    setPPhotos(snapshot.photos.pig);
    setCPhotos(snapshot.photos.cattle);
    setShPhotos(snapshot.photos.sheep);
    setBPhotoStatuses([]);
    setLPhotoStatuses([]);
    setPPhotoStatuses([]);
    setCPhotoStatuses([]);
    setShPhotoStatuses([]);
    setDone(snapshot.done);
    setDoneState(snapshot.doneState);
    setLastGroup(snapshot.lastGroup);
    setAnchorFailures(snapshot.anchorFailures || []);
    navigate(snapshot.activeForm ? `/dailys/${snapshot.activeForm}` : '/dailys');
  }
  function adoptHubDraftSilently(snapshot: HubDraft) {
    eggDefaultsAppliedRef.current = true;
    if (snapshot.forms?.broiler) setBForm(snapshot.forms.broiler);
    if (snapshot.forms?.layer) setLForm(snapshot.forms.layer);
    if (snapshot.forms?.pig) setPForm(snapshot.forms.pig);
    if (snapshot.forms?.egg) setEForm(snapshot.forms.egg);
    if (snapshot.forms?.cattle) setCForm(snapshot.forms.cattle as WebformHubCattleForm);
    if (snapshot.forms?.sheep) setSForm(snapshot.forms.sheep as WebformHubSheepForm);
    if (snapshot.extras?.broiler) setExtraBroilerGroups(snapshot.extras.broiler);
    if (snapshot.extras?.layer) setExtraLayerGroups(snapshot.extras.layer);
    if (snapshot.extras?.pig) setExtraPigGroups(snapshot.extras.pig);
    if (snapshot.photos?.broiler) setBPhotos(snapshot.photos.broiler);
    if (snapshot.photos?.layer) setLPhotos(snapshot.photos.layer);
    if (snapshot.photos?.pig) setPPhotos(snapshot.photos.pig);
    if (snapshot.photos?.cattle) setCPhotos(snapshot.photos.cattle);
    if (snapshot.photos?.sheep) setShPhotos(snapshot.photos.sheep);
    setBPhotoStatuses([]);
    setLPhotoStatuses([]);
    setPPhotoStatuses([]);
    setCPhotoStatuses([]);
    setShPhotoStatuses([]);
    setDone(false);
    setDoneState('synced');
    setLastGroup(snapshot.lastGroup ?? '');
    setAnchorFailures(snapshot.anchorFailures || []);
  }
  function resetSilently() {
    setBForm(EMPTY_B);
    setLForm(EMPTY_L);
    setPForm(EMPTY_P);
    setEForm(EMPTY_E);
    setCForm(EMPTY_C);
    setSForm(EMPTY_S);
    setExtraBroilerGroups([]);
    setExtraLayerGroups([]);
    setExtraPigGroups([]);
    setBPhotos([]);
    setLPhotos([]);
    setPPhotos([]);
    setCPhotos([]);
    setShPhotos([]);
    setBPhotoStatuses([]);
    setLPhotoStatuses([]);
    setPPhotoStatuses([]);
    setCPhotoStatuses([]);
    setShPhotoStatuses([]);
  }
  async function refreshLocalDrafts() {
    const durable = await listHubDrafts(sb);
    // The live account's own teardown carriers still held only in memory
    // (writing, or their write failed). A durable copy lists once.
    const held = (await listHubCarrierRows(sb)).filter((row) => !durable.some((saved) => saved.csid === row.csid));
    for (const r of durable) knownHubDraftCsids.current.add(r.csid);
    for (const r of held) knownHubDraftCsids.current.add(r.csid);

    // Exclude the draft currently being edited in this session (not stuck/needs-help)
    const activeCsid =
      localDraftCsid ||
      draftSessionRef.current?.csid ||
      carrierCandidateRef.current?.session?.csid ||
      submittedDraftRef.current?.csid;
    const unadoptedDurable = activeCsid ? durable.filter((row) => row.csid !== activeCsid) : durable;
    const unadoptedHeld = activeCsid ? held.filter((row) => row.csid !== activeCsid) : held;

    // Coalesce carrier rows with their underlying source draft rows:
    // A non-removal carrier entry supersedes its source draft row so that exactly ONE record appears per draft.
    // (A removal tombstone keeps the source row so describeHubDraftRow can show "superseded: you removed these answers".)
    const carrierSources = new Set<string>();
    for (const row of [...unadoptedDurable, ...unadoptedHeld]) {
      const carrierMeta = (row.payload as {carrier?: {sourceCsid?: string; removal?: unknown}} | null)?.carrier;
      if (carrierMeta?.sourceCsid && !carrierMeta.removal) {
        carrierSources.add(carrierMeta.sourceCsid);
      }
    }
    const deduplicatedDurable = unadoptedDurable.filter((row) => !carrierSources.has(row.csid));

    // One account's work only: a sign-in change between the two owner-
    // filtered reads lists nothing rather than a mix.
    const combined = [...deduplicatedDurable, ...unadoptedHeld];
    const owners = new Set(combined.map((row) => row.actor_profile_id));
    setLocalDrafts(owners.size > 1 ? [] : combined);
  }
  const recoveryActorId = reportEditor && reportEditor.user?.id;
  useEffect(() => {
    void refreshLocalDrafts().catch((error) => setErr(errorMessage(error)));
  }, [recoveryActorId]);
  // A carrier settling (written, or failed and kept in memory) changes what
  // this account may Open.
  useEffect(
    () => subscribeHubCarrier(() => void refreshLocalDrafts().catch((error) => setErr(errorMessage(error)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshLocalDrafts reads the stable client and setters
    [],
  );

  async function verifyActiveHubSession(): Promise<boolean> {
    const active = getActiveHubSession();
    if (!active) return true;
    try {
      const liveSubject = await readLiveHubSubject();
      if (!hubAliveRef.current) return false;
      if (!liveSubject || liveSubject !== active.owner) {
        clearActiveHubSession();
        resetSilently();
        setLocalDraftCsid(null);
        draftSessionRef.current = null;
        carrierCandidateRef.current = null;
        draftDirtyRef.current = false;
        await refreshLocalDrafts();
        return false;
      }

      const custody = await loadHubDraftCustody(sb, active.csid);
      if (!hubAliveRef.current) return false;
      const stampMatches =
        custody &&
        active.stamp &&
        custody.stamp &&
        custody.stamp.mountId === active.stamp.mountId &&
        custody.stamp.revision === active.stamp.revision;

      if (!custody || !stampMatches) {
        if (active.hasUnsettledWork && active.snapshot) {
          carryHubDraft({
            owner: active.owner,
            snapshot: active.snapshot,
            edits: 1,
            removal: false,
            session: {
              csid: active.csid,
              stamp: active.stamp,
            },
            pending: [],
          });
        }
        clearActiveHubSession();
        resetSilently();
        setLocalDraftCsid(null);
        draftSessionRef.current = null;
        carrierCandidateRef.current = null;
        draftDirtyRef.current = false;
        await refreshLocalDrafts();
        return false;
      }

      const loadedDraft = await loadHubDraft(sb, active.csid);
      if (!hubAliveRef.current) return false;
      const effectiveSnapshot = active.hasUnsettledWork && active.snapshot ? active.snapshot : loadedDraft;
      if (effectiveSnapshot) {
        adoptHubDraftSilently(effectiveSnapshot);
        const instanceId = hubDraftInstanceIdOf(active.csid) || active.csid;
        draftSessionRef.current = {
          instanceId,
          csid: active.csid,
          stamp: active.stamp,
          attempt: 0,
        };
        scheduledSubjectRef.current = active.owner;
        setLocalDraftCsid(active.csid);
        carrierCandidateRef.current = {
          owner: active.owner,
          snapshot: effectiveSnapshot,
          edits: draftEditsRef.current,
          removal: false,
          session: draftSessionRef.current,
        };
        if (active.hasUnsettledWork) {
          draftDirtyRef.current = true;
          scheduleDraftSave();
        } else {
          draftDirtyRef.current = false;
        }
        recordActiveHubSession({
          owner: active.owner,
          csid: active.csid,
          stamp: active.stamp,
          snapshot: effectiveSnapshot,
          hasUnsettledWork: active.hasUnsettledWork,
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  const activeSessionVerifiedRef = React.useRef(false);
  useEffect(() => {
    if (activeSessionVerifiedRef.current) return;
    activeSessionVerifiedRef.current = true;
    void verifyActiveHubSession();
  }, []);

  // The CURRENT meaningful work's captured owner gates Open independently of
  // the resumed row's actor: A's retained/quarantined — or already SAVED —
  // RAM is never preserved under B just because B's target row and SDK
  // session are B. dirty=false alone is not permission: saved work keeps its
  // author until a settled submit or owned-clear releases custody. With no
  // captured owner, only UNSETTLED state blocks (fail closed over an
  // ordinary null gap); a settled submit or owned-clear is the
  // pristine-release path that lets a fresh actor through the existing rules.
  function openBlockedByPendingOwner(rowActor: string): boolean {
    const pendingOwner = scheduledSubjectRef.current;
    if (pendingOwner !== null) return pendingOwner !== rowActor;
    // No captured owner: only UNSETTLED state blocks (fail closed over an
    // ordinary null gap). A session whose content is saved-and-clean is the
    // settled state the existing rules already treat as safe custody — the
    // drain leaves exactly that behind.
    return draftUnsettledFailureRef.current || draftDirtyRef.current;
  }
  async function openQueued(csid: string) {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'opening';
    setRecoveryWorking(true);
    try {
      if (csid === localDraftCsid || (draftSessionRef.current && csid === draftSessionRef.current.csid)) {
        setWfStuckOpen(false);
        setErr('');
        return;
      }
      const local =
        localDrafts.find((row) => row.csid === csid) ||
        (knownHubDraftCsids.current.has(csid) || csid.startsWith('webform_hub_draft')
          ? ({
              csid,
              form_kind: HUB_DRAFT_KIND,
              payload: {},
              record: {},
              status: 'queued' as const,
              retry_count: 0,
              last_error: null,
              created_at: Date.now(),
              last_attempt_at: null,
              actor_profile_id: scheduledSubjectRef.current ?? lastAuthenticatedUserId() ?? undefined,
            } as QueuedSubmission)
          : null);
      if (local) {
        // The row actor is known synchronously: refuse BEFORE any await, so a
        // foreign owner's Open never loads, preserves, or adopts anything.
        if (openBlockedByPendingOwner(local.actor_profile_id || '')) {
          setErr(
            'This device holds unsent work from another signed-in account. It stays on the form; open this draft after that work is settled under its own account.',
          );
          return;
        }
      }

      // Settle the mount's own pending save FIRST — join the in-flight ACK,
      // then flush anything scheduled — so an ADOPTION transfers custody only
      // from a quiescent prior chain with its stamp consistent. (The queued
      // path layers custody by preserve; its guard is the per-await currency
      // fencing below.)
      if (local) {
        if (draftSavingPromiseRef.current) await draftSavingPromiseRef.current;
        const needPersist = hubDraftHasContent();
        const settled = needPersist ? await flushDraftSave() : true;
        if (needPersist && (!settled || draftDirtyRef.current)) {
          setErr('Could not preserve this draft on this device. Your answers are still on the form.');
          return;
        }
      }
      // Currency captured for every await that follows: a late Open ack must
      // not replace a newer edit or a foreign current owner.
      const atOpen = {
        session: draftSessionRef.current,
        subject: scheduledSubjectRef.current,
        edits: draftEditsRef.current,
      };
      const currencyHeld = () =>
        draftSessionRef.current === atOpen.session &&
        scheduledSubjectRef.current === atOpen.subject &&
        draftEditsRef.current === atOpen.edits;
      const stale = () => {
        setErr('This page changed while the draft was opening. Your current work is kept; try again.');
      };
      // A teardown carrier still WRITING is joined, bounded, INSIDE the
      // currency fence: typing or an owner change during the join makes this
      // Open stale, and the current form, session, owner and the carrier stay
      // as they are. Once settled, a durable carrier opens from storage below
      // like any saved draft; one whose write failed opens from memory (its
      // owner verified). Still unsettled: refused truthfully, nothing changed.
      const held = local && isHubCarrierMemoryRow(local) ? await settleHubCarrier(csid) : 'gone';
      if (!currencyHeld()) return stale();
      if (held === 'pending') {
        setErr(HUB_CARRIER_PENDING_MESSAGE);
        return;
      }
      const fromMemory = held === 'memory';
      // Custody is read BEFORE the content: a row that advances between the
      // two reads loads NEWER answers than the adopted stamp, and the next
      // save's CAS then refuses truthfully instead of silently overwriting
      // the newer revision.
      const custody = local && !fromMemory ? await loadHubDraftCustody(sb, csid) : null;
      if (!currencyHeld()) return stale();
      const carried = fromMemory ? await readHubCarrierSnapshot(sb, csid) : null;
      const loaded = carried
        ? carried.snapshot
        : local
          ? await loadHubDraft(sb, csid)
          : await loadQueuedReportRecovery(sb, csid);
      // A removal tombstone: the owner's emptied form over a saved draft whose
      // owned clear had not settled when the page closed.
      const removal = !local ? null : carried ? carried.meta.removal : hubCarrierRemovalOf(loaded);
      const actor = local ? local.actor_profile_id || '' : (loaded as QueuedReportRecovery).actorProfileId;
      // Queued rows learn their actor only here — fence BEFORE the preserve.
      if (!local && openBlockedByPendingOwner(actor)) {
        setErr(
          'This device holds unsent work from another signed-in account. It stays on the form; open this draft after that work is settled under its own account.',
        );
        return;
      }
      if (!currencyHeld()) return stale();
      const mapped = !local ? hydrateFlatHubForm(loaded as QueuedReportRecovery, lockedName) : null;
      const previous = captureHubDraft();
      if (!currencyHeld()) return stale();
      // Also preserve the prior mounted draft durably before replacing its
      // fields. Cancel restores it immediately; a refresh cannot erase it.
      if (hubDraftHasContent()) {
        preservedHubDraft.current = await preserveHubDraft(sb, previous, preservedHubDraft.current, actor);
        await assertHubDraftActor(sb, actor);
      }
      if (!currencyHeld()) return stale();
      priorHubDraft.current.push({
        snapshot: previous,
        recovery,
        localDraftCsid,
        session: draftSessionRef.current,
        subject: scheduledSubjectRef.current,
        carrier: carried,
      });
      pendingCarrierRetireRef.current = null; // a different draft now owns the mount
      if (local) {
        if (removal) adoptCarrierRemoval(custody, removal, csid, actor);
        else adoptResumedDraft(csid, custody, actor);
        // Custody of a memory-held carrier moves into the form with its fields.
        if (carried) releaseHubCarrierEntry(carried);
        restoreHubDraft(loaded as HubDraft);
        setRecovery(null);
        setLocalDraftCsid(csid);
      } else {
        // The reviewed queued content becomes the mount's editing work under
        // its verified actor: the pending owner binds here, so any later
        // cross-actor Open is fenced exactly like typed work.
        scheduledSubjectRef.current = actor || scheduledSubjectRef.current;
        const queued = loaded as QueuedReportRecovery;
        const setters = {
          broiler: setBForm,
          pig: setPForm,
          cattle: (value: WebformHubRecord) => setCForm(value as WebformHubCattleForm),
          sheep: (value: WebformHubRecord) => setSForm(value as WebformHubSheepForm),
        };
        const photoSetters = {broiler: setBPhotos, pig: setPPhotos, cattle: setCPhotos, sheep: setShPhotos};
        const form = mapped!.form as keyof typeof setters;
        setters[form](mapped!.values);
        photoSetters[form](queued.photos);
        if (form === 'broiler') setExtraBroilerGroups([]);
        if (form === 'pig') setExtraPigGroups([]);
        setRecovery(queued);
        setLocalDraftCsid(null);
        setDone(false);
        navigate(`/dailys/${form}`);
      }
      setDupHits(null);
      setErr('');
      setWfStuckOpen(false);
      await refreshLocalDrafts();
    } catch (error) {
      setErr(errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
    }
  }
  function cancelRecovery() {
    if (recoveryOperation.current) return;
    const previous = priorHubDraft.current.pop();
    if (previous) {
      restoreHubDraft(previous.snapshot);
      // Custody returns with the fields: the prior session object (its stamp
      // chain was untouched while the resumed draft owned the mount) and its
      // pending owner. A pending legacy retirement is NOT restored — without
      // a durable save the resumed row is the only copy of those answers,
      // and the prior session's later saves must never discard it.
      draftSessionRef.current = previous.session ?? null;
      scheduledSubjectRef.current = previous.subject ?? null;
      pendingLegacyRetireRef.current = null;
      // An opened tombstone is not retired by a Cancel; a carrier opened
      // from memory is offered again, still only to its owner.
      pendingCarrierRetireRef.current = null;
      if (previous.carrier) restoreHubCarrierEntry(previous.carrier);
    }
    setRecovery(previous?.recovery || null);
    setLocalDraftCsid(previous?.localDraftCsid || null);
    setDupHits(null);
    setErr('');
  }
  // Resuming a local draft transfers custody of the editing session along
  // with the fields. An INSTANCE draft (a stamped session's own key) is
  // ADOPTED: its instance id and current durable stamp become the mount's
  // session, so the next autosave continues that row's CAS chain instead of
  // forking a second key that would leave the resumed row stale on the list
  // forever. Anything else — a legacy preserve-path row, or a stamp-less
  // row — has no chain to continue: the mount keeps its own session, the
  // verified live account becomes the pending owner (a stale prior owner
  // must not quarantine the resumed draft's first autosave), and the resumed
  // row retires only AFTER this session has made the answers durable. No
  // duplicate is created, and nothing is deleted here.
  function adoptResumedDraft(
    csid: string,
    custody: {stamp: LocalDraftStamp | null; contentIdentity: string | null} | null,
    actor: string,
  ) {
    // The handoff invalidates the prior custody's coalesce timer
    // synchronously: a pre-resume scheduled save must never fire over the
    // resumed state.
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    const instanceId = custody ? hubDraftInstanceIdOf(csid) : null;
    if (instanceId && custody?.stamp) {
      draftSessionRef.current = {instanceId, csid, stamp: custody.stamp, attempt: 0};
      scheduledSubjectRef.current = actor || null;
      // An earlier legacy resume still pending retirement stays pending: the
      // next durable save of THIS session retires it too.
      return;
    }
    scheduledSubjectRef.current = actor || scheduledSubjectRef.current;
    pendingLegacyRetireRef.current = custody
      ? {csid, contentIdentity: custody.contentIdentity, armedEdits: draftEditsRef.current}
      : null;
  }
  // Opening a removal TOMBSTONE returns the captured owned clear to this
  // mount as its pending work: the session continues the SOURCE chain at the
  // exact stamp the owner had emptied, under the verified owner. The restore's
  // own scheduled edit then re-arms the ordinary owned clear, which removes
  // exactly that revision with the live owner verified — a newer source
  // revision is 'kept', never cleared or adopted. Nothing is cleared here.
  // The tombstone row itself retires only after that settles.
  function adoptCarrierRemoval(
    custody: {stamp: LocalDraftStamp | null; contentIdentity: string | null} | null,
    removal: {csid: string; stamp: LocalDraftStamp},
    csid: string,
    actor: string,
  ) {
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    const instanceId = hubDraftInstanceIdOf(removal.csid);
    draftSessionRef.current = instanceId ? {instanceId, csid: removal.csid, stamp: removal.stamp, attempt: 0} : null;
    scheduledSubjectRef.current = actor || null;
    pendingLegacyRetireRef.current = null;
    pendingCarrierRetireRef.current = custody?.stamp
      ? {csid, stamp: custody.stamp, sourceCsid: removal.csid, armedEdits: draftEditsRef.current}
      : null;
  }

  // The opened tombstone has done its job once its source revision is gone
  // through this mount: the owned clear deleted it, or a save that started
  // after the Open replaced it (the epoch rule of the legacy retirement). It
  // retires by its OWN exact stamp under the live account; a kept/failed
  // result retains it, still listed for its owner.
  async function retireOpenedCarrier(
    authority: {kind: 'durable-save'; revAtStart: number} | {kind: 'cleared'; csid: string},
  ): Promise<void> {
    const retire = pendingCarrierRetireRef.current;
    if (!retire) return;
    if (authority.kind === 'durable-save' && authority.revAtStart <= retire.armedEdits) return;
    if (authority.kind === 'cleared' && authority.csid !== retire.sourceCsid) return;
    pendingCarrierRetireRef.current = null;
    const result = await clearHubDraftCas(sb, {
      csid: retire.csid,
      stamp: retire.stamp,
      stillCurrent: () => hubAliveRef.current,
    });
    if (result === 'deleted') setLocalDraftCsid((current) => (current === retire.csid ? null : current));
    void refreshLocalDrafts().catch(() => {});
  }
  // A superseded legacy row retires through the actor-owned discard path —
  // the same gate the drafts list uses — and only while it STILL holds the
  // content that was resumed (identity re-read immediately before removal).
  // A legacy row is never rewritten (only stamped instance keys CAS, and
  // preserve mints fresh csids), so the identity check plus the discard's
  // live-actor gate close the window between the two reads. A foreign,
  // stamped, or newer row is preserved; any failure retains the row
  // truthfully — a harmless list duplicate, never lost data, and never a
  // blocker for the session that now holds the answers durably.
  //
  // The EPOCH GUARD: a 'durable-save' authority must have STARTED after the
  // resumed content's own schedule (revAtStart past the arm token) — a
  // pre-resume ACK that lands during the handoff race saved the OLD answers
  // and can neither arm nor retire the row. A settled submit carries the
  // form's answers server-side by construction, so it retires directly.
  async function retireSupersededLegacyDraft(
    authority: {kind: 'durable-save'; revAtStart: number} | {kind: 'settled-submit'},
  ): Promise<void> {
    const retire = pendingLegacyRetireRef.current;
    if (!retire) return;
    if (authority.kind === 'durable-save' && authority.revAtStart <= retire.armedEdits) return;
    pendingLegacyRetireRef.current = null;
    try {
      const custody = await loadHubDraftCustody(sb, retire.csid);
      // Gone, foreign, or stamped elsewhere: nothing of ours to remove.
      if (!custody || custody.stamp !== null || custody.contentIdentity !== retire.contentIdentity) return;
      await discardHubDraft(sb, retire.csid);
      void refreshLocalDrafts();
    } catch {
      // Retained; the list stays truthful.
    }
  }
  function finishFlatRecovery(formKind: string) {
    if (recovery?.formKind !== formKind) return;
    setRecovery(null);
    priorHubDraft.current = [];
  }
  async function openDuplicateReport(path: string) {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'preserving';
    setRecoveryWorking(true);
    try {
      // Settle the editing session's draft before leaving. The stamped CAS
      // save is idempotent, so this is the same durable snapshot the
      // lifecycle already keeps current — minted only when content actually
      // changed. Pristine forms have nothing to preserve and navigate
      // directly; a failed save blocks the navigation truthfully.
      const needPersist = hubDraftHasContent();
      const settled = needPersist ? await flushDraftSave() : true;
      if (needPersist && (!settled || draftDirtyRef.current)) {
        setErr('Could not preserve this draft on this device. Your answers are still on the form.');
        return;
      }
      navigate(path);
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
    }
  }
  const recoveryBanner =
    recovery || localDraftCsid ? (
      <div
        role="status"
        data-hub-recovery={recovery?.csid || localDraftCsid}
        className="wcf-dup-help"
        style={{marginBottom: 12}}
      >
        {recovery
          ? 'Review these saved answers and photos before submitting a replacement. The queued report stays on this device until verified.'
          : 'This is a saved local draft. Review it before submitting; nothing is sent automatically.'}
        {recovery?.legacyIdentity && (
          <p>The original account is unknown. A replacement is submitted as {lockedName}.</p>
        )}
        <button type="button" className="wcf-dup-btn" disabled={submitting} onClick={cancelRecovery}>
          Cancel Recovery
        </button>
      </div>
    ) : null;

  // Common photo-upload helper. Validates required fields BEFORE any upload
  // (caller's job, this just runs the upload chain). Returns metadata array
  // on success; throws on first failure so the parent submit aborts.
  async function uploadPhotosOrAbort(
    formKind: string,
    csid: string,
    photos: File[],
    setStatuses: WebformHubPhotoStatusSetter,
    expectedOwner?: string | null,
  ) {
    if (!photos || photos.length === 0) return [];
    if (photos.length > MAX_PHOTOS_PER_REPORT) {
      throw new Error(`Up to ${MAX_PHOTOS_PER_REPORT} photos per submission`);
    }
    setStatuses(photos.map(() => 'pending'));
    const meta = [];
    for (let i = 0; i < photos.length; i++) {
      setStatuses((prev) => prev.map((s, j) => (j === i ? 'uploading' : s)));
      try {
        const m = await uploadDailyPhoto(sb, formKind, csid, `photo-${i + 1}`, photos[i], {
          assertCurrent: expectedOwner ? () => assertSubmissionCurrency(expectedOwner) : undefined,
        });
        meta.push(m);
        setStatuses((prev) => prev.map((s, j) => (j === i ? 'uploaded' : s)));
      } catch (e) {
        setStatuses((prev) => prev.map((s, j) => (j === i ? 'failed' : s)));
        throw e;
      }
    }
    return meta;
  }
  const allowAddGroup = (formId: string) => {
    // Check webform_settings key first (most up-to-date from admin panel)
    if (wfSettings?.allowAddGroup && formId in wfSettings.allowAddGroup) {
      return wfSettings.allowAddGroup[formId] === true;
    }
    // Fall back to full_config webforms array
    const wf = (cfg.webforms || []).find((w) => w.id === formId);
    return wf?.allowAddGroup === true;
  };

  // ── Phase 1D-B — WebformHub photo offline queue ────────────────────────
  // Mount one useOfflineSubmit hook per included form-kind. submit handlers
  // call hook.submit(payload) for single-row paths only. Multi-row Add-Group
  // (broiler/pig) stays direct-insert (Codex amendment 4). Layer + egg are
  // OUT of scope for 1D-B (layer's setHousingAnchorFromReport is load-bearing
  // and routes through 1D-C; egg has no photos column per mig 030).
  const broilerHook = useOfflineSubmit('poultry_dailys');
  const pigHook = useOfflineSubmit('pig_dailys');
  const cattleHook = useOfflineSubmit('cattle_dailys');
  const sheepHook = useOfflineSubmit('sheep_dailys');

  // Aggregated stuck rows across the four hooks. Sorted newest-first
  // (created_at desc, retry_count desc tiebreaker per Codex Q4).
  // On Hub home (!activeForm), draft-only rows never auto-open the modal and
  // are not counted there. On sub-form pages (activeForm), drafts are included
  // to maintain reload-resume behavior. Genuine stuck submissions still auto-open everywhere.
  const allStuck = React.useMemo(() => {
    const rows = [
      ...broilerHook.stuckRows,
      ...pigHook.stuckRows,
      ...cattleHook.stuckRows,
      ...sheepHook.stuckRows,
      ...(activeForm ? localDrafts : []),
    ];
    return rows.slice().sort((a, b) => {
      const tDelta = (b.created_at || 0) - (a.created_at || 0);
      if (tDelta !== 0) return tDelta;
      return (b.retry_count || 0) - (a.retry_count || 0);
    });
  }, [broilerHook.stuckRows, pigHook.stuckRows, cattleHook.stuckRows, sheepHook.stuckRows, activeForm, localDrafts]);

  // Map form_kind -> hook so retry/discard dispatches to the owning hook.
  const hookByFormKind: Record<string, WebformHubHook> = {
    poultry_dailys: broilerHook,
    pig_dailys: pigHook,
    cattle_dailys: cattleHook,
    sheep_dailys: sheepHook,
  };

  const [wfStuckOpen, setWfStuckOpen] = useState(false);
  const [doneState, setDoneState] = useState('synced'); // 'synced' | 'queued' | 'stuck'
  // Hen-count anchors the server refused after the report itself was saved.
  const [anchorFailures, setAnchorFailures] = useState<AnchorFailure[]>([]);
  // B3(c), migration 234: every ONLINE create here (multi-group broiler/pig,
  // layer, egg) is one operation per user intent, held per form kind until the
  // server confirms it. A retry of the same answers reuses the SAME ids, so the
  // server replays the original outcome instead of inserting again.
  const dailyOps = React.useRef<Partial<Record<DailyOperationKind, RetainedDailyOperation>>>({});
  function holdDailyOperation(kind: DailyOperationKind, recs: WebformHubRecord[], photos?: readonly unknown[]) {
    const op = retainOrMintOperation(dailyOps.current[kind], kind, recs, photos);
    dailyOps.current[kind] = op;
    operationRows(op, recs).forEach((stamped, i) => Object.assign(recs[i], stamped));
    return op;
  }
  async function sendDailyOperation(
    kind: DailyOperationKind,
    op: RetainedDailyOperation,
    rows: WebformHubRecord[],
    anchors?: DailyAnchorIntent[],
    expectedActor?: string | null,
  ) {
    const boundActor = expectedActor ?? null;
    if (!boundActor || !(await assertSubmissionCurrency(boundActor))) {
      return {
        data: null,
        error: {message: 'The signed-in account changed during submission. Operation aborted; your edits were kept.'},
      };
    }
    const actor = boundActor;
    const out = await submitDailyReportOperation(
      sb,
      kind,
      operationIdFor(
        op,
        rows.map((r) => String(r.id)),
      ),
      rows,
      actor,
      anchors,
    );
    // Confirmed, or refused as different saved work: this intent is finished.
    // Any other failure keeps the operation so Submit again replays it.
    if (!out.error || isOperationConflict(out.error)) dailyOps.current[kind] = undefined;
    return out;
  }
  const [anchorRetrying, setAnchorRetrying] = useState(false);
  const initialStuckShownRef = React.useRef(false);
  useEffect(() => {
    const hasGenuineStuck = allStuck.some((r) => r.form_kind !== HUB_DRAFT_KIND);
    const hasFormDraft =
      Boolean(activeForm) && localDrafts.some((d) => draftHasFormAnswers(d, activeForm, freshFormsRef.current));
    const shouldOpen = hasGenuineStuck || hasFormDraft;
    if (shouldOpen && !initialStuckShownRef.current) {
      initialStuckShownRef.current = true;
      setWfStuckOpen(true);
    }
  }, [allStuck, activeForm, localDrafts]);

  async function retryAnyStuck(csid: string) {
    if (recoveryOperation.current) return;
    const row = allStuck.find((r) => r.csid === csid);
    if (!row) return;
    const hook = hookByFormKind[row.form_kind];
    if (hook) await hook.retryStuck(csid);
  }
  async function discardAnyStuck(csid: string) {
    if (recoveryOperation.current) return;
    const row = allStuck.find((r) => r.csid === csid);
    if (!row) return;
    if (row.form_kind === HUB_DRAFT_KIND) {
      try {
        const active = getActiveHubSession();
        if (active?.csid === csid) {
          clearActiveHubSession();
        }
        // A memory-held carrier (joined if still writing) is discarded by its
        // verified owner; anything durable goes through the owner discard.
        const held = isHubCarrierMemoryRow(row) ? await settleHubCarrier(csid) : 'gone';
        if (held === 'pending') throw new Error(HUB_CARRIER_PENDING_MESSAGE);
        if (held === 'memory') await discardHubCarrierEntry(sb, csid);
        else await discardHubDraft(sb, csid);
        await refreshLocalDrafts();
      } catch (error) {
        setErr(errorMessage(error));
      }
      return;
    }
    const hook = hookByFormKind[row.form_kind];
    if (hook) await hook.discardStuck(csid);
  }

  // Lifted stuck-modal element. Codex amendment 1: include {stuckModalEl}
  // in EVERY return branch (done, form selector, broiler, layer, pig,
  // cattle, egg, sheep). Bottom-mount-only would miss most paths.
  const stuckModalEl =
    wfStuckOpen && allStuck.length > 0 ? (
      <StuckSubmissionsModal
        onOpen={openQueued}
        busy={submitting}
        canRetry={(row) => row.form_kind !== HUB_DRAFT_KIND}
        error={err}
        rows={allStuck}
        formLabel="daily report"
        describeRow={(row: StuckSubmissionRow) => {
          const fk = row.form_kind;
          if (fk === HUB_DRAFT_KIND) return describeHubDraftRow(row, allStuck);
          const rec = (row.record || row.payload || {}) as WebformHubRecord;
          const dateStr = rec.date || '?';
          const who =
            fk === 'poultry_dailys' || fk === 'pig_dailys'
              ? rec.batch_label || '?'
              : fk === 'cattle_dailys'
                ? rec.herd || '?'
                : fk === 'sheep_dailys'
                  ? rec.flock || '?'
                  : '?';
          const pretty =
            fk === 'poultry_dailys'
              ? 'Broiler'
              : fk === 'pig_dailys'
                ? 'Pig'
                : fk === 'cattle_dailys'
                  ? 'Cattle'
                  : fk === 'sheep_dailys'
                    ? 'Sheep'
                    : fk;
          const photoCount = Array.isArray(rec.photos) ? rec.photos.length : 0;
          const photoSuffix = photoCount > 0 ? ` · ${photoCount} photo${photoCount === 1 ? '' : 's'}` : '';
          return `${pretty} · ${who} · ${dateStr}${photoSuffix} (not yet sent)`;
        }}
        onRetry={async (csid) => {
          await retryAnyStuck(csid);
        }}
        onDiscard={async (csid) => {
          await discardAnyStuck(csid);
        }}
        onClose={() => setWfStuckOpen(false)}
      />
    ) : null;

  const inputStyle = {
    fontFamily: 'inherit',
    fontSize: 14,
    padding: '10px 12px',
    border: '1px solid #D2D6DB',
    borderRadius: 10,
    width: '100%',
    outline: 'none',
    background: 'white',
    color: '#1A1F24',
    boxSizing: 'border-box',
  } as CSSProperties;
  const labelStyle = {
    display: 'block',
    fontSize: 13,
    color: '#1A1F24',
    marginBottom: 5,
    fontWeight: 500,
  } as CSSProperties;
  const activeProgramColor = getProgramColor(DAILY_FORM_PROGRAMS[activeForm]);
  const sectionStyle: CSSProperties = {
    background: 'white',
    borderRadius: 12,
    padding: '16px',
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,.08)',
  };
  const YN = ({val, onChange}: {val: boolean | null; onChange: (value: boolean) => void}) => (
    <div style={{display: 'flex', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-strong)'}}>
      {[
        {v: true, l: 'Yes'},
        {v: false, l: 'No'},
      ].map(({v, l}, i) => {
        const selected = val === v;
        return (
          <React.Fragment key={l}>
            {i > 0 && <div style={{width: 1, background: 'var(--border-strong)', flexShrink: 0}} />}
            <button
              disabled={submitting}
              type="button"
              onClick={() => onChange(v)}
              style={{
                flex: 1,
                padding: '10px 0',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: selected ? 700 : 500,
                cursor: 'pointer',
                background: selected ? activeProgramColor : 'white',
                color: selected ? '#ffffff' : '#1A1F24',
              }}
            >
              {l}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
  const Toggle = ({opts, val, onChange}: {opts: string[]; val: string; onChange: (value: string) => void}) => (
    <div style={{display: 'flex', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-strong)'}}>
      {opts.map((o, i) => {
        const selected = val === o;
        return (
          <React.Fragment key={o}>
            {i > 0 && <div style={{width: 1, background: 'var(--border-strong)', flexShrink: 0}} />}
            <button
              disabled={submitting}
              type="button"
              onClick={() => onChange(val === o ? '' : o)}
              style={{
                flex: 1,
                padding: '9px 0',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: selected ? 700 : 500,
                cursor: 'pointer',
                background: selected ? activeProgramColor : 'white',
                color: selected ? '#ffffff' : '#1A1F24',
              }}
            >
              {o}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Validate that all required fields (from admin config) have values.
  // Returns an error message string if a required field is missing, or null if all good.
  // formId = 'broiler-dailys' / 'layer-dailys' / 'pig-dailys' / 'egg-dailys'
  // formValues = an object mapping fieldId → current value from the relevant form state
  function validateRequiredFields(formId: string, formValues: WebformHubFormValues) {
    const wf = (cfg.webforms || []).find((w) => w.id === formId);
    if (!wf) return null;
    const allFields = (wf.sections || []).flatMap((s) => s.fields || []);
    const required = allFields.filter((f) => f.required === true && f.enabled !== false);
    for (const f of required) {
      // feed_type is only required when feed_lbs > 0 (conditional rule)
      if (f.id === 'feed_type' && !(parseFloat(formValues['feed_lbs']) > 0)) continue;
      // mortality_reason is only required when mortality_count > 0 (conditional rule)
      if (f.id === 'mortality_reason' && !(parseInt(formValues['mortality_count']) > 0)) continue;
      const v = formValues[f.id];
      // Empty string, null, undefined → missing. Boolean false IS valid (e.g. "Group moved? No").
      const missing = v === '' || v == null || (typeof v === 'string' && v.trim() === '');
      if (missing) {
        return (f.label || f.id) + ' is required.';
      }
    }
    // Mortality reason is always required when mortality_count > 0, regardless of admin config
    if (parseInt(formValues['mortality_count']) > 0) {
      const mr = formValues['mortality_reason'];
      if (!mr || String(mr).trim() === '') return 'Mortality reason is required when mortalities are reported.';
    }
    return null;
  }

  async function submitBroiler() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('broiler');
      if (!submissionOwner) return;
      const confirmed = takeDuplicateConfirmation();
      setDupHits(null);
      // Hardcoded system field check (always required regardless of config)
      if (!bForm.date || !bForm.teamMember || !bForm.batchLabel) {
        setErr('Please fill in all required fields.');
        return;
      }
      // Config-driven required field check
      const valuesByFieldId = {
        date: bForm.date,
        team_member: bForm.teamMember,
        batch_label: bForm.batchLabel,
        feed_type: bForm.feedType,
        feed_lbs: bForm.feedLbs,
        grit_lbs: bForm.gritLbs,
        group_moved: bForm.groupMoved,
        waterer_checked: bForm.watererChecked,
        mortality_count: bForm.mortalityCount,
        mortality_reason: bForm.mortalityReason,
        comments: bForm.comments,
      };
      const reqErr = validateRequiredFields('broiler-dailys', valuesByFieldId);
      if (reqErr) {
        setErr(reqErr);
        return;
      }
      // Conditional rule: feed_type is required when feed_lbs > 0 (main + each extra group)
      if (parseFloat(bForm.feedLbs) > 0 && !bForm.feedType) {
        setErr('Feed Type is required when Feed (lbs) is entered.');
        return;
      }
      if (parseInt(bForm.mortalityCount) > 0 && !bForm.mortalityReason.trim()) {
        setErr('Mortality reason is required when mortalities are reported.');
        return;
      }
      for (const eg of extraBroilerGroups.filter((g) => g.batchLabel)) {
        if (parseFloat(eg.feedLbs) > 0 && !eg.feedType) {
          setErr('Feed Type is required for ' + eg.batchLabel + ' when Feed (lbs) is entered.');
          return;
        }
        if (parseInt(eg.mortalityCount) > 0 && !(eg.mortalityReason || '').trim()) {
          setErr('Mortality reason is required for ' + eg.batchLabel + ' when mortalities are reported.');
          return;
        }
      }
      setErr('');

      // Photos + Add-Group is a footgun: photos would attach to the primary
      // row only and operators would reasonably expect them to cover the
      // whole submission. Block before any upload until the daily_submissions
      // parent-table/RPC design lands. Mirrors the same constraint we put on
      // Add Feed (parent-submission deferred).
      const extraBroilerCount = extraBroilerGroups.filter((g) => g.batchLabel).length;
      if (bPhotos.length > 0 && extraBroilerCount > 0) {
        setErr(
          'Photos can only be attached when submitting one group at a time. ' +
            'Submit this report without extra groups, or submit each group ' +
            'separately with its own photos.',
        );
        return;
      }

      setSubmitting(true);

      const extraCount = extraBroilerGroups.filter((g) => g.batchLabel).length;
      if (recovery?.formKind === 'poultry_dailys' && extraCount > 0) {
        setErr('Submit the recovered report as one group. Its queued answers are still retained.');
        return;
      }

      // ── Multi-row Add-Group path (NO photos): preserved direct-insert.
      // Photos + extras is rejected above, so this branch always carries
      // photos: [] across every row. Hook is single-row only.
      if (extraCount > 0) {
        const t0 = Date.now();
        const buildExtraRec = (g: WebformHubRecord, i: number): WebformHubRecord => ({
          id: String(t0 + i + 1) + Math.random().toString(36).slice(2, 6),
          client_submission_id: newClientSubmissionId(),
          submitted_at: new Date().toISOString(),
          date: bForm.date,
          team_member: bForm.teamMember,
          batch_label: g.batchLabel,
          feed_type: g.feedType || null,
          feed_lbs: g.feedLbs !== '' ? parseFloat(g.feedLbs) : null,
          grit_lbs: g.gritLbs !== '' ? parseFloat(g.gritLbs) : null,
          group_moved: g.groupMoved !== false,
          waterer_checked: g.watererChecked !== false,
          mortality_count: g.mortalityCount !== '' ? parseInt(g.mortalityCount) : 0,
          mortality_reason: g.mortalityReason || null,
          comments: g.comments || null,
          photos: [],
        });
        const primary: WebformHubRecord = {
          id: String(t0) + Math.random().toString(36).slice(2, 6),
          client_submission_id: newClientSubmissionId(),
          submitted_at: new Date().toISOString(),
          date: bForm.date,
          team_member: bForm.teamMember,
          batch_label: bForm.batchLabel,
          feed_type: bForm.feedType,
          feed_lbs: bForm.feedLbs !== '' ? parseFloat(bForm.feedLbs) : null,
          grit_lbs: bForm.gritLbs !== '' ? parseFloat(bForm.gritLbs) : null,
          group_moved: bForm.groupMoved,
          waterer_checked: bForm.watererChecked,
          mortality_count: bForm.mortalityCount !== '' ? parseInt(bForm.mortalityCount) : 0,
          mortality_reason: bForm.mortalityReason || null,
          comments: bForm.comments || null,
          photos: [],
        };
        const recs = [primary, ...extraBroilerGroups.filter((g) => g.batchLabel).map(buildExtraRec)];
        const broilerOp = holdDailyOperation('poultry_dailys', recs);
        const hits: DuplicateHit[] = [];
        try {
          const inSubB = checkInSubmissionDuplicates('poultry_dailys', recs);
          if (inSubB) {
            setSubmitting(false);
            setErr(formatDuplicateError('poultry_dailys', inSubB));
            return;
          }
          for (const r of recs) {
            const existing = await checkDailyDuplicate(sb, 'poultry_dailys', r);
            // This operation's own committed row (a retry after a lost response) is not a duplicate.
            if (existing && !isOwnOperationRow(broilerOp, existing.id))
              hits.push({table: 'poultry_dailys', record: r, existing});
          }
        } catch (e) {
          setSubmitting(false);
          setErr(errorMessage(e) || 'Could not verify duplicate report.');
          return;
        }
        const resolved = await actOnDuplicates('poultry_dailys', recs, hits, confirmed, submissionOwner);
        if (!resolved) return;
        const {rest, updated} = resolved;
        if (!(await assertSubmissionCurrency(submissionOwner))) return;
        const {data: broilerResult, error} =
          rest.length > 0
            ? await sendDailyOperation('poultry_dailys', broilerOp, rest, undefined, submissionOwner)
            : {data: null, error: null};
        setSubmitting(false);
        if (error) {
          const reason = friendlyDailyDbError(error, 'poultry_dailys', rest[0]);
          setErr(updated.length > 0 ? partialFailureMessage(updated, reason, 'insert') : 'Could not save: ' + reason);
          return;
        }
        // Multi-row email behavior preserved (Codex amendment 4) -- once per
        // operation: a replay already sent it.
        (broilerResult?.replayed ? [] : rest)
          .filter((r) => r.feed_type === 'STARTER' && parseFloat(r.feed_lbs) > 0)
          .forEach((r) => {
            wcfSendEmail('starter_feed_check', {batch_label: r.batch_label, feed_lbs: r.feed_lbs});
          });
        setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? bForm.batchLabel) as string);
        applySuccessDisposition('broiler');
        setDoneState('synced');
        completed = true;
        setDone(true);
        return;
      }

      // ── Single-row path (with or without photos): through the hook.
      // Phase 1D-B Codex amendment 2: queued/stuck DROPS the starter_feed_check
      // email; only state:'synced' fires it. Locked by Playwright negative test.
      setBPhotoStatuses(bPhotos.length > 0 ? bPhotos.map(() => 'pending') : []);
      try {
        const payload: WebformHubRecord = {
          date: bForm.date,
          team_member: bForm.teamMember,
          batch_label: bForm.batchLabel,
          feed_type: bForm.feedType,
          feed_lbs: bForm.feedLbs !== '' ? parseFloat(bForm.feedLbs) : null,
          grit_lbs: bForm.gritLbs !== '' ? parseFloat(bForm.gritLbs) : null,
          group_moved: bForm.groupMoved,
          waterer_checked: bForm.watererChecked,
          mortality_count: bForm.mortalityCount !== '' ? parseInt(bForm.mortalityCount) : 0,
          mortality_reason: bForm.mortalityReason || null,
          comments: bForm.comments || null,
        };
        if (bPhotos.length > 0) payload.photos = bPhotos; // raw File[]; hook compresses + sanitizes
        const existing = await checkDailyDuplicate(sb, 'poultry_dailys', payload);
        const resolved = await actOnDuplicates(
          'poultry_dailys',
          [payload],
          existing ? [{table: 'poultry_dailys', record: payload, existing}] : [],
          confirmed,
          submissionOwner,
        );
        if (!resolved) return;
        if (resolved.updated.length > 0) {
          // Written onto the existing report; there was nothing else to send.
          setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? bForm.batchLabel) as string);
          applySuccessDisposition('broiler');
          setDoneState('synced');
          completed = true;
          setDone(true);
          return;
        }
        if (!(await assertIdentityCurrent(submissionOwner))) return;
        const result = await broilerHook.submit(payload, {
          ...(recovery?.formKind === 'poultry_dailys' ? {replacesCsid: recovery.csid} : {}),
          expectedActor: submissionOwner,
          assertCurrent: () => assertSubmissionCurrency(submissionOwner),
        });
        finishFlatRecovery('poultry_dailys');
        if (result.state === 'synced') {
          if (payload.feed_type === 'STARTER' && parseFloat(payload.feed_lbs) > 0) {
            wcfSendEmail('starter_feed_check', {batch_label: payload.batch_label, feed_lbs: payload.feed_lbs});
          }
        }
        if (bPhotos.length > 0) {
          const statusFor = result.state === 'synced' ? 'uploaded' : result.state === 'queued' ? 'queued' : 'failed';
          setBPhotoStatuses(bPhotos.map(() => statusFor));
        }
        if (result.state === 'stuck') setWfStuckOpen(true);
        setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? bForm.batchLabel) as string);
        applySuccessDisposition('broiler');
        setDoneState(result.state);
        completed = true;
        setDone(true);
      } catch (e) {
        setErr(
          'Could not save: ' +
            friendlyDailyDbError(e, 'poultry_dailys', {date: bForm.date, batch_label: bForm.batchLabel}),
        );
        if (bPhotos.length > 0) setBPhotoStatuses(bPhotos.map(() => 'failed'));
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }
  async function submitLayer() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('layer');
      if (!submissionOwner) return;
      const confirmed = takeDuplicateConfirmation();
      setDupHits(null);
      if (!lForm.date || !lForm.teamMember || !lForm.batchLabel) {
        setErr('Please fill in all required fields.');
        return;
      }
      // A removed <option> does not clear the select's state, so a form opened
      // while the group was still offered can still be holding it. Refuse at the
      // write boundary as well as in the option list.
      const staleGroups = rejectStaleLayerGroupSelections(
        [lForm.batchLabel, ...extraLayerGroups.map((g) => String(g.batchLabel ?? ''))],
        layerGroupNames,
        layerGroupStatus,
      );
      if (staleGroups) {
        setErr(staleGroups);
        return;
      }
      const valuesByFieldId = {
        date: lForm.date,
        team_member: lForm.teamMember,
        batch_label: lForm.batchLabel,
        feed_type: lForm.feedType,
        feed_lbs: lForm.feedLbs,
        grit_lbs: lForm.gritLbs,
        layer_count: lForm.layerCount,
        group_moved: lForm.groupMoved,
        waterer_checked: lForm.watererChecked,
        mortality_count: lForm.mortalityCount,
        mortality_reason: lForm.mortalityReason,
        comments: lForm.comments,
      };
      const reqErr = validateRequiredFields('layer-dailys', valuesByFieldId);
      if (reqErr) {
        setErr(reqErr);
        return;
      }
      // Conditional rule: feed_type is required when feed_lbs > 0 (main + each extra group)
      if (parseFloat(lForm.feedLbs) > 0 && !lForm.feedType) {
        setErr('Feed Type is required when Feed (lbs) is entered.');
        return;
      }
      if (parseInt(lForm.mortalityCount) > 0 && !lForm.mortalityReason.trim()) {
        setErr('Mortality reason is required when mortalities are reported.');
        return;
      }
      for (const eg of extraLayerGroups.filter((g) => g.batchLabel)) {
        if (parseFloat(eg.feedLbs) > 0 && !eg.feedType) {
          setErr('Feed Type is required for ' + eg.batchLabel + ' when Feed (lbs) is entered.');
          return;
        }
        if (parseInt(eg.mortalityCount) > 0 && !(eg.mortalityReason || '').trim()) {
          setErr('Mortality reason is required for ' + eg.batchLabel + ' when mortalities are reported.');
          return;
        }
      }
      setErr('');

      // See submitBroiler for why we block photos + extra-groups in v1.
      const extraLayerCount = extraLayerGroups.filter((g) => g.batchLabel).length;
      if (lPhotos.length > 0 && extraLayerCount > 0) {
        setErr(
          'Photos can only be attached when submitting one group at a time. ' +
            'Submit this report without extra groups, or submit each group ' +
            'separately with its own photos.',
        );
        return;
      }

      setSubmitting(true);

      // `photos` holds the chosen files until the upload below replaces them with
      // stored paths, so the duplicate decision sees that this group has photos.
      // id / client_submission_id / submitted_at come from the operation below.
      const rec: WebformHubRecord = {
        date: lForm.date,
        team_member: lForm.teamMember,
        batch_label: lForm.batchLabel,
        batch_id: resolveBatchId(lForm.batchLabel),
        feed_type: lForm.feedType,
        feed_lbs: lForm.feedLbs !== '' ? parseFloat(lForm.feedLbs) : null,
        grit_lbs: lForm.gritLbs !== '' ? parseFloat(lForm.gritLbs) : null,
        layer_count: lForm.layerCount !== '' ? parseInt(lForm.layerCount) : null,
        group_moved: lForm.groupMoved,
        waterer_checked: lForm.watererChecked,
        mortality_count: lForm.mortalityCount !== '' ? parseInt(lForm.mortalityCount) : 0,
        mortality_reason: lForm.mortalityReason || null,
        comments: lForm.comments || null,
        photos: lPhotos,
      };
      const allRecs: WebformHubRecord[] = [
        rec,
        ...extraLayerGroups
          .filter((g) => g.batchLabel)
          .map((g) => ({
            ...rec,
            batch_label: g.batchLabel,
            batch_id: resolveBatchId(g.batchLabel),
            feed_type: g.feedType || null,
            feed_lbs: g.feedLbs !== '' ? parseFloat(g.feedLbs) : null,
            grit_lbs: g.gritLbs !== '' ? parseFloat(g.gritLbs) : null,
            layer_count: g.layerCount !== '' ? parseInt(g.layerCount) : null,
            group_moved: g.groupMoved !== false,
            waterer_checked: g.watererChecked !== false,
            mortality_count: g.mortalityCount !== '' ? parseInt(g.mortalityCount) : 0,
            mortality_reason: g.mortalityReason || null,
            comments: g.comments || null,
            photos: [],
          })),
      ];
      const layerOp = holdDailyOperation('layer_dailys', allRecs, lPhotos);
      const csid = String(layerOp.csids[0]);
      const hits: DuplicateHit[] = [];
      try {
        const inSubL = checkInSubmissionDuplicates('layer_dailys', allRecs);
        if (inSubL) {
          setSubmitting(false);
          setErr(formatDuplicateError('layer_dailys', inSubL));
          return;
        }
        for (const r of allRecs) {
          const existing = await checkDailyDuplicate(sb, 'layer_dailys', r);
          if (existing && !isOwnOperationRow(layerOp, existing.id))
            hits.push({table: 'layer_dailys', record: r, existing});
        }
      } catch (e) {
        setSubmitting(false);
        setErr(errorMessage(e) || 'Could not verify duplicate report.');
        return;
      }
      const resolved = await actOnDuplicates('layer_dailys', allRecs, hits, confirmed, submissionOwner);
      if (!resolved) return;
      const {rest: recs, updated} = resolved;
      // Both endings run this: hen-count anchors follow every group, whether it
      // was written onto its existing report or inserted, then the form resets.
      async function finishLayerSubmit(insertedFailures: AnchorFailure[] = []) {
        // Mortality reports do NOT touch current_count (Model X). But if a hen count
        // is provided, it becomes the new verified anchor for that housing.
        // The RESULT is read, and that is the point (migration 225 lane). The
        // report is already saved by now; the anchor is a second write that can
        // legitimately be refused -- the server re-checks that the housing is
        // still active, and for a light user that the report actually supports
        // this count. Discarding the result reported "Report Submitted!" while
        // the hen count silently never moved, which is what the projected-count
        // math then reads. Failures are collected and shown, with a retry that
        // re-runs ONLY the anchor, never the report.
        //
        // Migration 234: groups INSERTED by this submit were anchored by the
        // server inside the report's own transaction (insertedFailures carries
        // what it could not anchor). Only groups written onto an EXISTING report
        // -- an edit, not a create -- are anchored here.
        const failures: AnchorFailure[] = [...insertedFailures];
        for (const r of updated.map((h) => h.record as WebformHubRecord)) {
          const lc = parseInt(r.layer_count);
          if (!isNaN(lc) && lc >= 0 && r.batch_label) {
            let result;
            try {
              if (!(await assertSubmissionCurrency(submissionOwner))) return;
              result = await setHousingAnchorFromReport(sb, r.batch_label, lc, r.date);
            } catch (e) {
              result = {ok: false, reason: 'db-error', error: errorMessage(e)};
            }
            if (!result || result.ok !== true) {
              failures.push({
                label: r.batch_label,
                count: lc,
                date: r.date,
                reason: (result && result.reason) || 'db-error',
              });
            }
          }
        }
        setAnchorFailures(failures);
        setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? lForm.batchLabel) as string);
        applySuccessDisposition('layer');
        setDoneState('synced');
        completed = true;
        setDone(true);
      }
      // Every group was written onto its existing report: nothing to insert.
      if (recs.length === 0) {
        setSubmitting(false);
        void refreshLayerDailysProjection?.().catch(() => {});
        await finishLayerSubmit();
        return;
      }

      // An updated group never carries photos (decideDuplicates), so any files
      // here belong to a group that is about to be inserted.
      let photoMeta;
      try {
        // Once per operation: a retry reuses the stored metadata (the bucket
        // refuses a second write to the same path, and different metadata would
        // be different work to the server).
        photoMeta =
          (layerOp.photoMeta as Awaited<ReturnType<typeof uploadPhotosOrAbort>> | null) ??
          (await uploadPhotosOrAbort('layer_dailys', csid, lPhotos, setLPhotoStatuses, submissionOwner));
        layerOp.photoMeta = photoMeta;
      } catch (e) {
        setSubmitting(false);
        const reason = 'Photo upload failed: ' + errorMessage(e);
        setErr(
          updated.length > 0
            ? partialFailureMessage(updated, reason, 'insert')
            : reason + '. Submission aborted — try again.',
        );
        return;
      }
      rec.photos = photoMeta;
      // Hen-count anchors ride with the operation. The housing MATCH stays in the
      // browser (layerHousing's rule); the server anchors inside the report's
      // transaction, never overwrites a newer count, and checkpoints failures.
      const anchorIntents: DailyAnchorIntent[] = [];
      const unresolved: AnchorFailure[] = [];
      for (let i = 0; i < recs.length; i++) {
        const r = recs[i];
        const lc = parseInt(r.layer_count);
        if (isNaN(lc) || lc < 0 || !r.batch_label) continue;
        const match = await resolveHousingForReport(sb, r.batch_label);
        if (match.ok) anchorIntents.push({row_index: i, housing_id: String(match.target.id)});
        else unresolved.push({label: r.batch_label, count: lc, date: r.date, reason: match.reason});
      }
      if (!(await assertSubmissionCurrency(submissionOwner))) return;
      const {data: layerResult, error} = await sendDailyOperation(
        'layer_dailys',
        layerOp,
        recs,
        anchorIntents,
        submissionOwner,
      );
      setSubmitting(false);
      if (error) {
        if (photoMeta.length > 0) {
          console.error(
            '[WebformHub] layer_dailys insert failed AFTER photo upload — orphan storage paths:',
            photoMeta.map((p) => p.path),
          );
        }
        const reason = friendlyDailyDbError(error, 'layer_dailys', recs[0]);
        setErr(updated.length > 0 ? partialFailureMessage(updated, reason, 'insert') : 'Could not save: ' + reason);
        return;
      }
      void refreshLayerDailysProjection?.().catch(() => {});
      // Check starter feed threshold for STARTER layer records (same alert as
      // broilers), once per operation: a replay already sent it.
      (layerResult?.replayed ? [] : recs)
        .filter((r) => r.feed_type === 'STARTER' && parseFloat(r.feed_lbs) > 0)
        .forEach((r) => {
          wcfSendEmail('starter_feed_check', {batch_label: r.batch_label, feed_lbs: r.feed_lbs, table: 'layer_dailys'});
        });
      // A 'superseded' anchor is not a failure: a newer hen count is already
      // recorded for that housing and must not be overwritten.
      const serverFailures: AnchorFailure[] = (layerResult?.anchors || [])
        .filter((a) => a.status === 'failed')
        .map((a) => {
          const r = recs[a.row_index];
          return {
            label: String(r?.batch_label ?? ''),
            count: parseInt(r?.layer_count),
            date: String(r?.date ?? ''),
            reason: 'update-failed',
            operationId: String(layerResult?.operation_id ?? ''),
            rowIndex: a.row_index,
          };
        });
      await finishLayerSubmit([...unresolved, ...serverFailures]);
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }
  // SOWS in three sow groups (owner request 2026-11-04): one pig_dailys row per
  // group, 'SOWS · Group n'. Every section is validated, then ALL THREE rows
  // are duplicate-checked before anything is sent, then each row goes through
  // the offline hook in group order with its own client_submission_id - so a
  // SOWS report still queues offline exactly as the one-row SOWS report did.
  // The three rows are not atomic: if one fails part-way, the message names
  // every group by what happened to it, and the form is kept so Submit again
  // sends the rest (already-saved groups then show as duplicates). Photos go
  // on Group 1's row. Returns true when the report is complete.
  async function submitSowGroups(submissionOwner: string, confirmed: ReadonlySet<string> | null): Promise<boolean> {
    const date = String(pForm.date);
    const team = String(pForm.teamMember);
    const sections = sowSectionsOf(pForm);
    for (const [i, n] of SOW_GROUP_NUMBERS.entries()) {
      const reqErr = validateRequiredFields('pig-dailys', sowSectionFieldValues(date, team, n, sections[i]));
      if (reqErr) {
        setErr(`Sow Group ${n}: ${reqErr}`);
        return false;
      }
    }
    const movedErr = groupMovedRefusal(
      SOW_GROUP_NUMBERS.map((n, i) => ({label: sowGroupLabel(n), groupMoved: sections[i].groupMoved})),
    );
    if (movedErr) {
      setErr(movedErr);
      return false;
    }
    if (extraPigGroups.some((g) => g.batchLabel)) {
      setErr('Submit SOWS on its own: remove the additional groups and submit them as a separate report.');
      return false;
    }
    setErr('');
    setSubmitting(true);
    const payloads = buildSowRowPayloads(date, team, sections) as WebformHubRecord[];
    if (pPhotos.length > 0) payloads[0].photos = pPhotos;
    setPPhotoStatuses(pPhotos.length > 0 ? pPhotos.map(() => 'pending') : []);
    const hits: DuplicateHit[] = [];
    try {
      const inSub = checkInSubmissionDuplicates('pig_dailys', payloads);
      if (inSub) {
        setSubmitting(false);
        setErr(formatDuplicateError('pig_dailys', inSub));
        return false;
      }
      for (const r of payloads) {
        const existing = await checkDailyDuplicate(sb, 'pig_dailys', r);
        if (existing) hits.push({table: 'pig_dailys', record: r, existing});
      }
      const legacyExisting = await checkDailyDuplicate(sb, 'pig_dailys', {
        date: pForm.date,
        batch_label: SOWS_GROUP_VALUE,
      });
      if (legacyExisting) {
        hits.push({
          table: 'pig_dailys',
          record: payloads[0] || {date: pForm.date, batch_label: sowGroupLabel(1)},
          existing: legacyExisting,
        });
      }
    } catch (e) {
      setSubmitting(false);
      setErr(errorMessage(e) || 'Could not verify duplicate report.');
      return false;
    }
    const resolved = await actOnDuplicates('pig_dailys', payloads, hits, confirmed, submissionOwner);
    if (!resolved) return false;
    const toSend = new Set(resolved.rest);
    const outcomes: SowRowOutcome[] = payloads.map((p) => ({
      label: String(p.batch_label),
      state: toSend.has(p) ? 'not-sent' : 'updated',
    }));
    if (toSend.size > 0 && !(await assertIdentityCurrent(submissionOwner))) {
      setSubmitting(false);
      return false;
    }
    for (const [i, p] of payloads.entries()) {
      if (!toSend.has(p)) continue;
      try {
        const result = await pigHook.submit(p, {
          expectedActor: submissionOwner,
          assertCurrent: () => assertSubmissionCurrency(submissionOwner),
        });
        outcomes[i] = {label: outcomes[i].label, state: result.state};
        if (
          (result.state === 'synced' || result.state === 'queued') &&
          p.group_moved === true &&
          p.date &&
          p.batch_label
        ) {
          recordSubmittedMove(String(p.batch_label), String(p.date));
        }
        if (p.photos && pPhotos.length > 0) {
          const statusFor = result.state === 'synced' ? 'uploaded' : result.state === 'queued' ? 'queued' : 'failed';
          setPPhotoStatuses(pPhotos.map(() => statusFor));
        }
      } catch (e) {
        outcomes[i] = {label: outcomes[i].label, state: 'failed'};
        if (p.photos && pPhotos.length > 0) setPPhotoStatuses(pPhotos.map(() => 'failed'));
        setSubmitting(false);
        setErr(sowPartialMessage(outcomes, friendlyDailyDbError(e, 'pig_dailys', p)));
        return false;
      }
    }
    setSubmitting(false);
    const state = combinedSowState(outcomes);
    if (state === 'stuck') setWfStuckOpen(true);
    setLastGroup(SOWS_GROUP_VALUE);
    refreshPigContext?.();
    applySuccessDisposition('pig');
    setDoneState(state);
    setDone(true);
    return true;
  }
  async function submitPig() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('pig');
      if (!submissionOwner) return;
      const confirmed = takeDuplicateConfirmation();
      setDupHits(null);
      if (!pForm.date || !pForm.teamMember || !pForm.batchLabel) {
        setErr('Please fill in all required fields.');
        return;
      }
      if (isSowsSectionMode(pForm.batchLabel, recovery?.formKind === 'pig_dailys')) {
        completed = await submitSowGroups(submissionOwner, confirmed);
        return;
      }
      const valuesByFieldId = {
        date: pForm.date,
        team_member: pForm.teamMember,
        batch_label: pForm.batchLabel,
        feed_lbs: pForm.feedLbs,
        pig_count: pForm.pigCount,
        fence_voltage: pForm.fenceVoltage,
        group_moved: pForm.groupMoved,
        nipple_drinker_moved: pForm.nippleDrinkerMoved,
        nipple_drinker_working: pForm.nippleDrinkerWorking,
        troughs_moved: pForm.troughsMoved,
        fence_walked: pForm.fenceWalked,
        issues: pForm.issues,
      };
      const reqErr = validateRequiredFields('pig-dailys', valuesByFieldId);
      if (reqErr) {
        setErr(reqErr);
        return;
      }
      // "Was group moved?" starts blank and is ALWAYS required, whatever the
      // admin config says: submit_queued_report would default a missing answer
      // to Yes, so every row must carry an explicit true or false.
      const movedErr = groupMovedRefusal([
        {label: pForm.batchLabel, groupMoved: pForm.groupMoved},
        ...extraPigGroups
          .filter((g) => g.batchLabel)
          .map((g) => ({label: String(g.batchLabel), groupMoved: g.groupMoved})),
      ]);
      if (movedErr) {
        setErr(movedErr);
        return;
      }
      const sowsExtraErr = extraGroupsSowsRefusal(extraPigGroups);
      if (sowsExtraErr) {
        setErr(sowsExtraErr);
        return;
      }
      setErr('');

      // See submitBroiler for why we block photos + extra-groups in v1.
      const extraPigCount = extraPigGroups.filter((g) => g.batchLabel).length;
      if (pPhotos.length > 0 && extraPigCount > 0) {
        setErr(
          'Photos can only be attached when submitting one group at a time. ' +
            'Submit this report without extra groups, or submit each group ' +
            'separately with its own photos.',
        );
        return;
      }

      setSubmitting(true);

      const extraCount = extraPigGroups.filter((g) => g.batchLabel).length;
      if (recovery?.formKind === 'pig_dailys' && extraCount > 0) {
        setErr('Submit the recovered report as one group. Its queued answers are still retained.');
        return;
      }

      // ── Multi-row Add-Group path (NO photos): preserved direct-insert.
      if (extraCount > 0) {
        const t0 = Date.now();
        const buildExtraRec = (g: WebformHubRecord, i: number): WebformHubRecord => ({
          id: String(t0 + i + 1) + Math.random().toString(36).slice(2, 6),
          client_submission_id: newClientSubmissionId(),
          submitted_at: new Date().toISOString(),
          date: pForm.date,
          team_member: pForm.teamMember,
          batch_label: g.batchLabel,
          batch_id: g.batchLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          feed_lbs: g.feedLbs !== '' ? parseFloat(g.feedLbs) : null,
          pig_count: g.pigCount !== '' ? parseInt(g.pigCount) : null,
          fence_voltage: g.fenceVoltage !== '' ? parseFloat(g.fenceVoltage) : null,
          group_moved: g.groupMoved === true,
          nipple_drinker_moved: g.nippleDrinkerMoved !== false,
          nipple_drinker_working: g.nippleDrinkerWorking !== false,
          troughs_moved: g.troughsMoved !== false,
          fence_walked: g.fenceWalked !== false,
          issues: g.issues || null,
          photos: [],
        });
        const primary: WebformHubRecord = {
          id: String(t0) + Math.random().toString(36).slice(2, 6),
          client_submission_id: newClientSubmissionId(),
          submitted_at: new Date().toISOString(),
          date: pForm.date,
          team_member: pForm.teamMember,
          batch_label: pForm.batchLabel,
          batch_id:
            recovery?.formKind === 'pig_dailys' && pForm.batchLabel === recovery.record.batch_label
              ? recoveryText(recovery.record.batch_id)
              : pForm.batchLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          feed_lbs: pForm.feedLbs !== '' ? parseFloat(pForm.feedLbs) : null,
          pig_count: pForm.pigCount !== '' ? parseInt(pForm.pigCount) : null,
          group_moved: pForm.groupMoved === true,
          nipple_drinker_moved: pForm.nippleDrinkerMoved,
          nipple_drinker_working: pForm.nippleDrinkerWorking,
          troughs_moved: pForm.troughsMoved,
          fence_walked: pForm.fenceWalked,
          fence_voltage: pForm.fenceVoltage !== '' ? parseFloat(pForm.fenceVoltage) : null,
          issues: pForm.issues || null,
          photos: [],
        };
        const recs = [primary, ...extraPigGroups.filter((g) => g.batchLabel).map(buildExtraRec)];
        const pigOp = holdDailyOperation('pig_dailys', recs);
        const hits: DuplicateHit[] = [];
        try {
          const inSubP = checkInSubmissionDuplicates('pig_dailys', recs);
          if (inSubP) {
            setSubmitting(false);
            setErr(formatDuplicateError('pig_dailys', inSubP));
            return;
          }
          for (const r of recs) {
            const existing = await checkDailyDuplicate(sb, 'pig_dailys', r);
            if (existing && !isOwnOperationRow(pigOp, existing.id))
              hits.push({table: 'pig_dailys', record: r, existing});
            if (r.batch_label === SOWS_GROUP_VALUE) {
              for (const n of SOW_GROUP_NUMBERS) {
                const groupExisting = await checkDailyDuplicate(sb, 'pig_dailys', {
                  date: r.date,
                  batch_label: sowGroupLabel(n),
                });
                if (groupExisting && !isOwnOperationRow(pigOp, groupExisting.id)) {
                  hits.push({table: 'pig_dailys', record: r, existing: groupExisting});
                }
              }
            }
          }
        } catch (e) {
          setSubmitting(false);
          setErr(errorMessage(e) || 'Could not verify duplicate report.');
          return;
        }
        const resolved = await actOnDuplicates('pig_dailys', recs, hits, confirmed, submissionOwner);
        if (!resolved) return;
        const {rest, updated} = resolved;
        if (!(await assertSubmissionCurrency(submissionOwner))) return;
        const {error} =
          rest.length > 0
            ? await sendDailyOperation('pig_dailys', pigOp, rest, undefined, submissionOwner)
            : {error: null};
        setSubmitting(false);
        if (error) {
          const reason = friendlyDailyDbError(error, 'pig_dailys', rest[0]);
          setErr(updated.length > 0 ? partialFailureMessage(updated, reason, 'insert') : 'Could not save: ' + reason);
          return;
        }
        for (const r of rest) {
          if (r.group_moved === true && r.date && r.batch_label) {
            recordSubmittedMove(String(r.batch_label), String(r.date));
          }
        }
        refreshPigContext?.();
        setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? pForm.batchLabel) as string);
        applySuccessDisposition('pig');
        setDoneState('synced');
        completed = true;
        setDone(true);
        return;
      }

      // ── Single-row path: through the hook.
      setPPhotoStatuses(pPhotos.length > 0 ? pPhotos.map(() => 'pending') : []);
      try {
        const payload: WebformHubRecord = {
          date: pForm.date,
          team_member: pForm.teamMember,
          batch_id:
            recovery?.formKind === 'pig_dailys' && pForm.batchLabel === recovery.record.batch_label
              ? recoveryText(recovery.record.batch_id)
              : pForm.batchLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          batch_label: pForm.batchLabel,
          pig_count: pForm.pigCount !== '' ? parseInt(pForm.pigCount) : null,
          feed_lbs: pForm.feedLbs !== '' ? parseFloat(pForm.feedLbs) : null,
          group_moved: pForm.groupMoved === true,
          nipple_drinker_moved: pForm.nippleDrinkerMoved,
          nipple_drinker_working: pForm.nippleDrinkerWorking,
          troughs_moved: pForm.troughsMoved,
          fence_walked: pForm.fenceWalked,
          fence_voltage: pForm.fenceVoltage !== '' ? parseFloat(pForm.fenceVoltage) : null,
          issues: pForm.issues || null,
        };
        if (pPhotos.length > 0) payload.photos = pPhotos;
        const existing = await checkDailyDuplicate(sb, 'pig_dailys', payload);
        const hits: DuplicateHit[] = existing ? [{table: 'pig_dailys', record: payload, existing}] : [];
        if (payload.batch_label === SOWS_GROUP_VALUE) {
          for (const n of SOW_GROUP_NUMBERS) {
            const groupExisting = await checkDailyDuplicate(sb, 'pig_dailys', {
              date: payload.date,
              batch_label: sowGroupLabel(n),
            });
            if (groupExisting) {
              hits.push({table: 'pig_dailys', record: payload, existing: groupExisting});
            }
          }
        }
        const resolved = await actOnDuplicates('pig_dailys', [payload], hits, confirmed, submissionOwner);
        if (!resolved) return;
        if (resolved.updated.length > 0) {
          // Written onto the existing report; there was nothing else to send.
          if (payload.group_moved === true && payload.date && payload.batch_label) {
            recordSubmittedMove(String(payload.batch_label), String(payload.date));
          }
          refreshPigContext?.();
          setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? pForm.batchLabel) as string);
          applySuccessDisposition('pig');
          setDoneState('synced');
          completed = true;
          setDone(true);
          return;
        }
        if (!(await assertIdentityCurrent(submissionOwner))) return;
        const result = await pigHook.submit(payload, {
          ...(recovery?.formKind === 'pig_dailys' ? {replacesCsid: recovery.csid} : {}),
          expectedActor: submissionOwner,
          assertCurrent: () => assertSubmissionCurrency(submissionOwner),
        });
        finishFlatRecovery('pig_dailys');
        if (pPhotos.length > 0) {
          const statusFor = result.state === 'synced' ? 'uploaded' : result.state === 'queued' ? 'queued' : 'failed';
          setPPhotoStatuses(pPhotos.map(() => statusFor));
        }
        if (result.state === 'stuck') setWfStuckOpen(true);
        if (
          (result.state === 'synced' || result.state === 'queued') &&
          payload.group_moved === true &&
          payload.date &&
          payload.batch_label
        ) {
          recordSubmittedMove(String(payload.batch_label), String(payload.date));
        }
        refreshPigContext?.();
        setLastGroup((submittedDraftRef.current?.formSnapshot?.batchLabel ?? pForm.batchLabel) as string);
        applySuccessDisposition('pig');
        setDoneState(result.state);
        completed = true;
        setDone(true);
      } catch (e) {
        setErr(
          'Could not save: ' + friendlyDailyDbError(e, 'pig_dailys', {date: pForm.date, batch_label: pForm.batchLabel}),
        );
        if (pPhotos.length > 0) setPPhotoStatuses(pPhotos.map(() => 'failed'));
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }
  async function submitEgg() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('egg');
      if (!submissionOwner) return;
      setDupHits(null);
      if (!eForm.date || !eForm.teamMember) {
        setErr('Please fill in date and team member.');
        return;
      }
      // A removed <option> does not clear the select's state, so a form opened
      // while the group was still offered can still be holding it. Refuse at the
      // write boundary as well as in the option list.
      const staleGroups = rejectStaleLayerGroupSelections(
        [eForm.g1n, eForm.g2n, eForm.g3n, eForm.g4n],
        eggGroupNames,
        layerGroupStatus,
      );
      if (staleGroups) {
        setErr(staleGroups);
        return;
      }
      // An egg_group field is considered "filled" if either a group name OR an egg count was provided
      const grpFilled = (n: any, c: any) => (n && String(n).trim() !== '') || (c !== '' && c != null);
      const valuesByFieldId = {
        date: eForm.date,
        team_member: eForm.teamMember,
        dozens_on_hand: eForm.dozensOnHand,
        dozens_allocated_coastal_rancher: eForm.dozensAllocatedCoastalRancher,
        comments: eForm.comments,
        group1_pair: grpFilled(eForm.g1n, eForm.g1c) ? 'filled' : '',
        group2_pair: grpFilled(eForm.g2n, eForm.g2c) ? 'filled' : '',
        group3_pair: grpFilled(eForm.g3n, eForm.g3c) ? 'filled' : '',
        group4_pair: grpFilled(eForm.g4n, eForm.g4c) ? 'filled' : '',
      };
      const reqErr = validateRequiredFields('egg-dailys', valuesByFieldId);
      if (reqErr) {
        setErr(reqErr);
        return;
      }
      setErr('');
      setSubmitting(true);
      const g1 = eForm.g1c !== '' ? parseInt(eForm.g1c) : 0;
      const g2 = eForm.g2c !== '' ? parseInt(eForm.g2c) : 0;
      const g3 = eForm.g3c !== '' ? parseInt(eForm.g3c) : 0;
      const g4 = eForm.g4c !== '' ? parseInt(eForm.g4c) : 0;
      const daily_dozen_count = Math.floor((g1 + g2 + g3 + g4) / 12);
      const rec: WebformHubRecord = {
        id: String(Date.now()) + Math.random().toString(36).slice(2, 6),
        submitted_at: new Date().toISOString(),
        date: eForm.date,
        team_member: eForm.teamMember,
        group1_name: eForm.g1n || null,
        group1_count: eForm.g1c !== '' ? g1 : null,
        group2_name: eForm.g2n || null,
        group2_count: eForm.g2c !== '' ? g2 : null,
        group3_name: eForm.g3n || null,
        group3_count: eForm.g3c !== '' ? g3 : null,
        group4_name: eForm.g4n || null,
        group4_count: eForm.g4c !== '' ? g4 : null,
        daily_dozen_count,
        dozens_on_hand: eForm.dozensOnHand !== '' ? parseFloat(eForm.dozensOnHand) : null,
        dozens_allocated_coastal_rancher:
          eForm.dozensAllocatedCoastalRancher !== '' ? parseFloat(eForm.dozensAllocatedCoastalRancher) : null,
        comments: eForm.comments || null,
      };
      const eggOp = holdDailyOperation('egg_dailys', [rec]);
      try {
        const existing = await checkDailyDuplicate(sb, 'egg_dailys', rec);
        if (existing && !isOwnOperationRow(eggOp, existing.id)) {
          setSubmitting(false);
          blockOnDuplicates([{table: 'egg_dailys', record: rec, existing}]);
          return;
        }
      } catch (e) {
        setSubmitting(false);
        setErr(errorMessage(e) || 'Could not verify duplicate report.');
        return;
      }
      if (!(await assertSubmissionCurrency(submissionOwner))) return;
      const {data: eggResult, error} = await sendDailyOperation('egg_dailys', eggOp, [rec], undefined, submissionOwner);
      setSubmitting(false);
      if (error) {
        setErr('Could not save: ' + friendlyDailyDbError(error, 'egg_dailys', rec));
        return;
      }
      void refreshEggDailysProjection?.().catch(() => {});
      // Send egg report email (fire-and-forget), once per operation.
      if (!eggResult?.replayed)
        wcfSendEmail('egg_report', {
          date: rec.date,
          team_member: rec.team_member,
          dozens_on_hand: rec.dozens_on_hand,
          dozens_allocated_coastal_rancher: rec.dozens_allocated_coastal_rancher,
          daily_dozen_count: rec.daily_dozen_count,
        });
      applySuccessDisposition('egg');
      setDoneState('synced');
      completed = true;
      setDone(true);
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }
  async function submitCattle() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('cattle');
      if (!submissionOwner) return;
      setDupHits(null);
      // Build values-by-field-id so admin-configured "required" toggles are honored.
      // For jsonb arrays (feeds, minerals) we flag 'filled' when at least one row has a qty/lbs.
      const anyFeed = (cForm.feeds || []).some(
        (r) => r.feedId && r.qty !== '' && r.qty != null && parseFloat(r.qty) > 0,
      );
      const anyMineral = (cForm.minerals || []).some(
        (r) => r.feedId && r.lbs !== '' && r.lbs != null && parseFloat(r.lbs) > 0,
      );
      const valuesByFieldId = {
        date: cForm.date,
        team_member: cForm.teamMember,
        herd: cForm.herd,
        feeds: anyFeed ? 'filled' : '',
        minerals: anyMineral ? 'filled' : '',
        fence_voltage: cForm.fenceVoltage,
        water_checked: cForm.waterChecked,
        issues: cForm.issues,
      };
      const reqErr = validateRequiredFields('cattle-dailys', valuesByFieldId);
      if (reqErr) {
        setErr(reqErr);
        return;
      }
      setErr('');
      setSubmitting(true);
      // Build feeds jsonb with nutrition snapshots from the feed master list
      const feedsJ = (cForm.feeds || [])
        .filter((f) => f.feedId && f.qty !== '' && f.qty != null)
        .map((row) => buildHubInputPayload(row, cattleFeedInputs, 'cattle', false))
        .filter(Boolean);
      const mineralsJ = (cForm.minerals || [])
        .filter((m) => m.feedId && m.lbs !== '' && m.lbs != null)
        .map((row) => buildHubInputPayload(row, cattleFeedInputs, 'cattle', true))
        .filter(Boolean);
      setCPhotoStatuses(cPhotos.length > 0 ? cPhotos.map(() => 'pending') : []);
      try {
        const payload: WebformHubRecord = {
          date: cForm.date,
          team_member: cForm.teamMember,
          herd: cForm.herd,
          feedsJ,
          mineralsJ,
          fence_voltage: cForm.fenceVoltage !== '' ? parseFloat(cForm.fenceVoltage) : null,
          water_checked: cForm.waterChecked,
          mortality_count: cForm.mortalityCount !== '' ? parseInt(cForm.mortalityCount) : 0,
          mortality_reason: cForm.mortalityReason || null,
          issues: cForm.issues || null,
        };
        if (cPhotos.length > 0) payload.photos = cPhotos;
        const existing = await checkDailyDuplicate(sb, 'cattle_dailys', payload);
        if (existing) {
          setSubmitting(false);
          blockOnDuplicates([{table: 'cattle_dailys', record: payload, existing}]);
          return;
        }
        if (!(await assertIdentityCurrent(submissionOwner))) return;
        const result = await cattleHook.submit(payload, {
          ...(recovery?.formKind === 'cattle_dailys' ? {replacesCsid: recovery.csid} : {}),
          expectedActor: submissionOwner,
          assertCurrent: () => assertSubmissionCurrency(submissionOwner),
        });
        finishFlatRecovery('cattle_dailys');
        if (cPhotos.length > 0) {
          const statusFor = result.state === 'synced' ? 'uploaded' : result.state === 'queued' ? 'queued' : 'failed';
          setCPhotoStatuses(cPhotos.map(() => statusFor));
        }
        if (result.state === 'stuck') setWfStuckOpen(true);
        setLastGroup((submittedDraftRef.current?.formSnapshot?.herd ?? cForm.herd) as string);
        applySuccessDisposition('cattle');
        setDoneState(result.state);
        completed = true;
        setDone(true);
      } catch (e) {
        setErr('Could not save: ' + friendlyDailyDbError(e, 'cattle_dailys', {date: cForm.date, herd: cForm.herd}));
        if (cPhotos.length > 0) setCPhotoStatuses(cPhotos.map(() => 'failed'));
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }
  async function submitSheep() {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'submitting';
    setRecoveryWorking(true);
    let completed = false;
    try {
      const submissionOwner = await prepareSubmit('sheep');
      if (!submissionOwner) return;
      setDupHits(null);
      // Hardcoded system fields — always required regardless of admin config
      if (!sForm.date || !sForm.teamMember || !sForm.flock) {
        setErr('Date, team member, and flock are required.');
        return;
      }
      setErr('');
      setSubmitting(true);
      // Build feeds jsonb from picker rows — mirrors cattle's shape
      const feedsJ = (sForm.feeds || [])
        .filter((f) => f.feedId && f.qty !== '' && f.qty != null)
        .map((row) => buildHubInputPayload(row, cattleFeedInputs, 'sheep', false))
        .filter(Boolean);
      const mineralsJ = (sForm.minerals || [])
        .filter((m) => m.feedId && m.lbs !== '' && m.lbs != null)
        .map((row) => buildHubInputPayload(row, cattleFeedInputs, 'sheep', true))
        .filter(Boolean);
      setShPhotoStatuses(shPhotos.length > 0 ? shPhotos.map(() => 'pending') : []);
      try {
        const payload: WebformHubRecord = {
          date: sForm.date,
          team_member: sForm.teamMember,
          flock: sForm.flock,
          feedsJ,
          mineralsJ,
          fence_voltage_kv: sForm.fenceVoltageKv !== '' ? parseFloat(sForm.fenceVoltageKv) : null,
          waterers_working: !!sForm.waterersWorking,
          mortality_count: 0,
          comments: sForm.comments || null,
        };
        if (shPhotos.length > 0) payload.photos = shPhotos;
        const existing = await checkDailyDuplicate(sb, 'sheep_dailys', payload);
        if (existing) {
          setSubmitting(false);
          blockOnDuplicates([{table: 'sheep_dailys', record: payload, existing}]);
          return;
        }
        if (!(await assertIdentityCurrent(submissionOwner))) return;
        const result = await sheepHook.submit(payload, {
          ...(recovery?.formKind === 'sheep_dailys' ? {replacesCsid: recovery.csid} : {}),
          expectedActor: submissionOwner,
          assertCurrent: () => assertSubmissionCurrency(submissionOwner),
        });
        finishFlatRecovery('sheep_dailys');
        if (shPhotos.length > 0) {
          const statusFor = result.state === 'synced' ? 'uploaded' : result.state === 'queued' ? 'queued' : 'failed';
          setShPhotoStatuses(shPhotos.map(() => statusFor));
        }
        if (result.state === 'stuck') setWfStuckOpen(true);
        const fl = (submittedDraftRef.current?.formSnapshot?.flock ?? sForm.flock) as string;
        setLastGroup({rams: 'Rams', ewes: 'Ewes', feeders: 'Feeders'}[fl] || fl);
        applySuccessDisposition('sheep');
        setDoneState(result.state);
        completed = true;
        setDone(true);
      } catch (e) {
        setErr('Could not save: ' + friendlyDailyDbError(e, 'sheep_dailys', {date: sForm.date, flock: sForm.flock}));
        if (shPhotos.length > 0) setShPhotoStatuses(shPhotos.map(() => 'failed'));
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      setErr('Could not save: ' + errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryWorking(false);
      if (!completed) {
        submittedDraftRef.current = null;
        if (hubDraftHasContent()) {
          scheduleDraftSave();
        }
      }
      setSubmitting(false);
    }
  }

  // "Update That Report": record exactly which existing reports the user agreed
  // to update, then run the form's normal submit. That submit validates the
  // current form, re-runs the duplicate checks and updates only if they still
  // find exactly these reports; any edit since the notice cleared it anyway.
  function confirmDuplicateUpdate(resubmit: () => Promise<void>) {
    const hits = dupHitsRef.current;
    if (!hits || hits.length === 0) return;
    confirmedDupIdsRef.current = new Set(hits.map((h) => h.existing.id));
    void resubmit();
  }

  const DUPLICATE_HISTORY_FORM: Record<string, string> = {
    poultry_dailys: 'broiler',
    layer_dailys: 'layer',
    egg_dailys: 'egg',
    pig_dailys: 'pig',
    cattle_dailys: 'cattle',
    sheep_dailys: 'sheep',
  };

  // `resubmit` is passed only by the forms whose fields map one to one onto
  // the edit RPC (broiler, layer, pig); the others offer Open only.
  function renderDuplicateNotice(table: string, resubmit: (() => Promise<void>) | null) {
    if (!dupHits || dupHits.length === 0 || dupHits[0].table !== table) return null;
    return (
      <DuplicateReportNotice
        hits={dupHits}
        authState={reportEditor}
        today={todayStr()}
        busy={submitting}
        onUpdate={resubmit && !recovery ? () => confirmDuplicateUpdate(resubmit) : null}
        pathFor={(h) =>
          (dailyHistoryPrograms || []).includes(DUPLICATE_HISTORY_FORM[h.table] || '')
            ? dailyReportPath(h.table, h.existing.id)
            : null
        }
        onOpen={(path) => void openDuplicateReport(path)}
      />
    );
  }

  // Retry ONLY the refused anchors. The report is already saved, so this must
  // never resubmit it -- it calls the same anchor helper with the values the
  // saved report carried, and clears each entry that now succeeds.
  async function retryAnchors() {
    setAnchorRetrying(true);
    const still: AnchorFailure[] = [];
    // Anchors the server checkpointed (migration 234) retry their FROZEN
    // housing, count and date, with the same never-overwrite-a-newer-count
    // guard. One call per operation.
    const byOperation = new Map<string, AnchorFailure[]>();
    for (const a of anchorFailures) {
      if (a.operationId) byOperation.set(a.operationId, [...(byOperation.get(a.operationId) || []), a]);
    }
    for (const [operationId, pending] of byOperation) {
      const {data, error} = await retryDailyReportAnchors(sb, operationId);
      for (const a of pending) {
        const outcome = (data?.anchors || []).find((o) => o.row_index === a.rowIndex);
        if (error || !outcome || outcome.status === 'failed') still.push(a);
      }
    }
    for (const a of anchorFailures) {
      if (a.operationId) continue;
      let result;
      try {
        result = await setHousingAnchorFromReport(sb, a.label, a.count, a.date);
      } catch (e) {
        result = {ok: false, reason: 'db-error', error: errorMessage(e)};
      }
      if (!result || result.ok !== true) still.push({...a, reason: (result && result.reason) || 'db-error'});
    }
    setAnchorFailures(still);
    setAnchorRetrying(false);
    if (!still.length) void refreshLayerDailysProjection?.().catch(() => {});
  }

  function resetAndAnother() {
    setDupHits(null);
    setAnchorFailures([]);
    const team = '';
    const today = todayStr();
    // Each form set up here is the new fresh copy pristine checks compare to.
    function fresh<F extends WebformHubRecord>(key: string, form: F): F {
      if (freshFormsRef.current) freshFormsRef.current[key] = form;
      return form;
    }
    if (activeForm === 'broiler')
      setBForm(fresh('broiler', {...EMPTY_B, date: today, teamMember: team, batchLabel: lastGroup}));
    else if (activeForm === 'layer')
      setLForm(fresh('layer', {...EMPTY_L, date: today, teamMember: team, batchLabel: lastGroup}));
    else if (activeForm === 'pig')
      setPForm(fresh('pig', {...EMPTY_P, date: today, teamMember: team, batchLabel: lastGroup}));
    else if (activeForm === 'cattle') setCForm(fresh('cattle', {...EMPTY_C, date: today, teamMember: team}));
    else if (activeForm === 'sheep')
      setSForm(fresh('sheep', {...EMPTY_S, date: today, teamMember: team, flock: lastGroup ? sForm.flock : ''}));
    else setEForm(fresh('egg', {...EMPTY_E, date: today, teamMember: team}));
    if (activeForm && autoDatesRef.current) autoDatesRef.current[activeForm] = today;
    setDone(false);
    setDoneState('synced'); // Reset so the next submit's done screen starts clean.
    setErr('');
  }

  // Per form: the day it was last auto-dated with, and the form as it was when
  // last freshly set up (mount or Submit Another). A form equal to its fresh
  // copy on every answer is pristine; anything else is a draft. Markers are per
  // form so re-dating one form (Submit Another) never hides a new day from the
  // others.
  const autoDatesRef = React.useRef<Record<string, string> | null>(null);
  if (!autoDatesRef.current) {
    const mountDay = todayStr();
    autoDatesRef.current = {
      broiler: mountDay,
      layer: mountDay,
      pig: mountDay,
      egg: mountDay,
      cattle: mountDay,
      sheep: mountDay,
    };
  }
  interface FreshHubForms {
    broiler: WebformHubRecord;
    layer: WebformHubRecord;
    pig: WebformHubRecord;
    egg: WebformHubRecord;
    cattle: WebformHubCattleForm;
    sheep: WebformHubSheepForm;
    [key: string]: WebformHubRecord | WebformHubCattleForm | WebformHubSheepForm;
  }
  const freshFormsRef = React.useRef<FreshHubForms | null>(null);
  if (!freshFormsRef.current) {
    freshFormsRef.current = {
      broiler: EMPTY_B,
      layer: EMPTY_L,
      pig: EMPTY_P,
      egg: EMPTY_E,
      cattle: EMPTY_C,
      sheep: EMPTY_S,
    };
  }
  const draftExtrasRef = React.useRef<Record<string, {extraGroups: number; photos: number}>>({});
  draftExtrasRef.current = {
    broiler: {extraGroups: extraBroilerGroups.length, photos: bPhotos.length},
    layer: {extraGroups: extraLayerGroups.length, photos: lPhotos.length},
    pig: {extraGroups: extraPigGroups.length, photos: pPhotos.length},
    egg: {extraGroups: 0, photos: 0},
    cattle: {extraGroups: 0, photos: cPhotos.length},
    sheep: {extraGroups: 0, photos: shPhotos.length},
  };

  // The forms as of this render, so the re-dating decision is made outside the
  // state updaters (no side effects inside them).
  const formsRef = React.useRef<Record<string, WebformHubRecord>>({});
  formsRef.current = {broiler: bForm, layer: lForm, pig: pForm, egg: eForm, cattle: cForm, sheep: sForm};

  const extrasRef = React.useRef<Record<string, unknown[]>>({});
  extrasRef.current = {
    broiler: extraBroilerGroups,
    layer: extraLayerGroups,
    pig: extraPigGroups,
    egg: [],
    cattle: [],
    sheep: [],
  };

  const photosRef = React.useRef<Record<string, File[]>>({});
  photosRef.current = {
    broiler: bPhotos,
    layer: lPhotos,
    pig: pPhotos,
    egg: [],
    cattle: cPhotos,
    sheep: shPhotos,
  };

  function normalizeFormForComparison(form: Record<string, unknown> | undefined): string {
    if (!form) return '';
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      if (k === 'feeds' || k === 'minerals') {
        if (Array.isArray(v)) {
          cleaned[k] = v.map((item) => {
            if (item && typeof item === 'object') {
              const rest: Record<string, unknown> = {};
              for (const [prop, val] of Object.entries(item as Record<string, unknown>)) {
                if (!prop.startsWith('recovery')) {
                  rest[prop] = val;
                }
              }
              return rest;
            }
            return item;
          });
          continue;
        }
      }
      cleaned[k] = v;
    }
    return JSON.stringify(cleaned);
  }

  function computeSuccessDisposition<TForm extends Record<string, unknown>>(
    formKind: string,
    currentForm: TForm,
    capturedForm: Record<string, unknown> | undefined,
    freshForm: TForm,
    currentExtras: unknown[],
    capturedExtras: unknown[] | undefined,
    currentPhotos: File[],
    capturedPhotos: File[] | undefined,
  ): {
    form: TForm;
    extras: unknown[];
    photos: File[];
    hasNewerEdits: boolean;
  } {
    const hasNewerFormEdits =
      !capturedForm || normalizeFormForComparison(currentForm) !== normalizeFormForComparison(capturedForm);
    const resolvedForm = hasNewerFormEdits ? currentForm : freshForm;

    const capturedPhotoSet = new Set(capturedPhotos || []);
    const resolvedPhotos = (currentPhotos || []).filter((f) => !capturedPhotoSet.has(f));
    const hasNewerPhotos = resolvedPhotos.length > 0;

    const capturedExtList = capturedExtras || [];
    const resolvedExtras: unknown[] = [];
    for (let i = 0; i < currentExtras.length; i++) {
      const currItem = currentExtras[i];
      const capItem = capturedExtList[i];
      if (i < capturedExtList.length && JSON.stringify(currItem) === JSON.stringify(capItem)) {
        continue;
      }
      resolvedExtras.push(currItem);
    }
    const hasNewerExtras = resolvedExtras.length > 0;

    const hasNewerEdits = hasNewerFormEdits || hasNewerPhotos || hasNewerExtras;
    return {
      form: resolvedForm,
      extras: resolvedExtras,
      photos: resolvedPhotos,
      hasNewerEdits,
    };
  }

  const lastDispositionRef = React.useRef<{
    hasNewerEdits: boolean;
    form: Record<string, unknown>;
    extras: unknown[];
    photos: File[];
  } | null>(null);

  function applySuccessDisposition(formKind: string) {
    const captured = submittedDraftRef.current;
    const currentForm = formsRef.current[formKind] || {};
    const freshForm = freshFormsRef.current?.[formKind] || {};
    const currentExtras = extrasRef.current[formKind] || [];
    const currentPhotos = photosRef.current[formKind] || [];

    const disp = computeSuccessDisposition(
      formKind,
      currentForm,
      captured?.formSnapshot,
      freshForm,
      currentExtras,
      captured?.extraSnapshot,
      currentPhotos,
      captured?.photoSnapshot,
    );
    lastDispositionRef.current = disp;

    const didReset =
      !disp.hasNewerEdits &&
      (!isPristineForm(currentForm, freshForm, draftExtrasRef.current[formKind]) ||
        currentPhotos.length > 0 ||
        currentExtras.length > 0);
    programmaticResetRef.current = didReset;

    formsRef.current[formKind] = disp.form;
    extrasRef.current[formKind] = disp.extras;
    photosRef.current[formKind] = disp.photos;
    if (formKind === 'broiler') {
      setBForm(disp.form);
      setExtraBroilerGroups(disp.extras as typeof extraBroilerGroups);
      setBPhotos(disp.photos);
      if (disp.photos.length === 0) setBPhotoStatuses([]);
    } else if (formKind === 'layer') {
      setLForm(disp.form);
      setExtraLayerGroups(disp.extras as typeof extraLayerGroups);
      setLPhotos(disp.photos);
      if (disp.photos.length === 0) setLPhotoStatuses([]);
    } else if (formKind === 'pig') {
      setPForm(disp.form);
      setExtraPigGroups(disp.extras as typeof extraPigGroups);
      setPPhotos(disp.photos);
      if (disp.photos.length === 0) setPPhotoStatuses([]);
    } else if (formKind === 'egg') {
      setEForm(disp.form);
    } else if (formKind === 'cattle') {
      setCForm(disp.form as WebformHubCattleForm);
      setCPhotos(disp.photos);
      if (disp.photos.length === 0) setCPhotoStatuses([]);
    } else if (formKind === 'sheep') {
      setSForm(disp.form as WebformHubSheepForm);
      setShPhotos(disp.photos);
      if (disp.photos.length === 0) setShPhotoStatuses([]);
    }

    if (!disp.hasNewerEdits) {
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
      if (!otherFormsHaveContent(formKind)) {
        draftDirtyRef.current = false;
      }
    } else {
      scheduleDraftSave();
    }
    return disp;
  }

  // Move every pristine form still on its own automatic date to today. A filled
  // draft or a date the user chose keeps its date; the form offers Use Today.
  function advancePristineDates() {
    const today = todayStr();
    const markers = autoDatesRef.current || {};
    const setters: Record<string, (fn: <F extends {date: string}>(f: F) => F) => void> = {
      broiler: (fn) => setBForm(fn),
      layer: (fn) => setLForm(fn),
      pig: (fn) => setPForm(fn),
      egg: (fn) => setEForm(fn),
      cattle: (fn) => setCForm(fn),
      sheep: (fn) => setSForm(fn),
    };
    for (const key of Object.keys(setters)) {
      const prev = markers[key];
      if (prev === today) continue;
      const form = formsRef.current[key];
      const pristine = isPristineForm(form, freshFormsRef.current?.[key] || {}, draftExtrasRef.current[key]);
      if (shouldAdvanceDate(form, prev, pristine)) {
        // Re-check against the live value: only move a form still on `prev`.
        setters[key](<F extends {date: string}>(f: F): F => (f.date === prev ? {...f, date: today} : f));
      }
      markers[key] = today;
    }
  }

  // Opening a form after the day has turned (a tab left open overnight).
  React.useEffect(() => {
    if (!activeForm) return;
    advancePristineDates();
    // advancePristineDates reads only refs and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeForm]);

  // A phone resuming a tab left open overnight: the effect above never runs,
  // so apply the same rule when the page becomes visible again.
  React.useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') advancePristineDates();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Durable draft lifecycle ───────────────────────────────────────────────
  // Ordinary edits schedule a coalesced save (2.5 s idle); hiding the tab,
  // navigating to a duplicate report and unloading flush immediately.
  // Pristine forms never write: mount, refetch and the pristine-date
  // rollover alone save nothing. pagehide cannot guarantee an awaited
  // transaction, so durability comes from saving DURING editing; the flush
  // there is best effort, and the status line says "saved" only after a
  // transaction actually committed.
  function hubDraftHasContent(): boolean {
    const forms = formsRef.current;
    return (['broiler', 'layer', 'pig', 'egg', 'cattle', 'sheep'] as const).some(
      (key) => !isPristineForm(forms[key], freshFormsRef.current?.[key] || {}, draftExtrasRef.current[key]),
    );
  }
  async function persistDraftNow(
    capturedSubject: string | null,
    verify: () => Promise<string | null>,
  ): Promise<boolean> {
    if (doneRef.current) return false;
    if (!hubAliveRef.current || recoveryOperation.current === 'submitting') return false;
    if (draftSavingRef.current) {
      // Join, don't fork: the in-flight save's completion compares edit
      // tokens, sees this newer content, and schedules the follow-up drain.
      draftDirtyRef.current = true;
      return false;
    }
    if (!hubDraftHasContent()) return false;
    if (!capturedSubject) {
      // Pending/unassigned truthfully: the edit's author was unreadable at
      // edit time. With an ESTABLISHED owner, a later account never adopts
      // these answers.
      draftUnsettledFailureRef.current = true;
      draftDirtyRef.current = true;
      setDraftStatus({state: 'failed', detail: 'The sign-in state is unresolved. Your edits are kept; retry.'});
      return false;
    }
    draftSavingRef.current = true;
    const session = draftSession();
    const attempt = ++session.attempt;
    const revAtStart = draftEditsRef.current;
    setDraftStatus({state: 'saving'});
    const run = (async (): Promise<boolean> => {
      try {
        // SDK VERIFICATION of the captured author: the live session must still
        // be the SAME account that typed this, before and after the save.
        const live = await verify();
        if (live !== capturedSubject) {
          draftUnsettledFailureRef.current = true;
          draftDirtyRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail: 'The signed-in account changed. Your edits are kept; nothing is saved under the new account.',
          });
          return false;
        }
        const result = await saveHubDraftCas(sb, captureHubDraft(), {
          instanceId: session.instanceId,
          expectStamp: session.stamp,
          expectedActor: capturedSubject,
          stillCurrent: () =>
            hubAliveRef.current &&
            !doneRef.current &&
            draftSessionRef.current === session &&
            session.attempt === attempt &&
            lastAuthenticatedUserId() === capturedSubject,
        });
        const postLive = await verify();
        const sameAuthor =
          postLive === capturedSubject && draftSessionRef.current === session && draftEditsRef.current === revAtStart;
        if (!sameAuthor) {
          // Newer RAM content or a changed author: N's durable result is kept
          // (the stamp advances), but nothing paints Saved over it.
          if (result.kind === 'saved') session.stamp = result.stamp;
          if (postLive !== capturedSubject) {
            draftDirtyRef.current = true;
            draftUnsettledFailureRef.current = true; // as the pre-save account check: failed is unsettled
            // The account changed mid-save: quarantine — A's content is never
            // re-saved as B; the next edit under the live author starts a
            // fresh instance.
            draftSessionRef.current = null;
            setDraftStatus({
              state: 'failed',
              detail: 'The signed-in account changed. Your edits are kept; nothing is saved under the new account.',
            });
            return false;
          }
          if (!hubDraftHasContent()) {
            // A1/A2: the RAM was EMPTIED while N flew. A late ACK of a
            // replaced or retired session (or a newer attempt) owns nothing
            // current and changes nothing.
            if (!(hubAliveRef.current && draftSessionRef.current === session && session.attempt === attempt)) {
              return false;
            }
            draftDirtyRef.current = true;
            // Removal authority comes ONLY from N's own acknowledged revision —
            // the one it wrote ('saved') or confirmed as this session's
            // current one ('unchanged') — and covers exactly that stamp. A
            // 'conflict' carries ANOTHER writer's newer stamp (another tab of
            // this account) and grants nothing; neither does a failed or
            // not-current result. Their durable rows and photos stay.
            const ownAck =
              (result.kind === 'saved' || result.kind === 'unchanged') && result.stamp ? result.stamp : null;
            if (ownAck) {
              // Supersede the clear still aimed at the stale captured
              // revision; a clear already in flight settles first.
              if (draftSaveTimer.current) {
                clearTimeout(draftSaveTimer.current);
                draftSaveTimer.current = null;
              }
              clearPendingRef.current = false;
              clearIntentRef.current = null;
              const staleClear = clearInFlightPromiseRef.current;
              if (staleClear) await staleClear;
              if (
                !(hubAliveRef.current && draftSessionRef.current === session && session.attempt === attempt) ||
                hubDraftHasContent()
              ) {
                return false;
              }
              return clearOwnedDraftNow({csid: session.csid, stamp: ownAck}, capturedSubject, verify);
            }
            // No authority: whatever durable revision this session still owns
            // (session.stamp) settles through the owned-clear path — the
            // pending intent, else a prompt flush — whose CAS removes exactly
            // that stamp or keeps a newer one.
            if (!clearIntentRef.current && !draftSaveTimer.current) {
              draftSaveTimer.current = setTimeout(() => {
                draftSaveTimer.current = null;
                void flushDraftSave();
              }, 400);
            }
            return false;
          }
          draftDirtyRef.current = true;
          // Same author, newer revision: the bounded drain PRECEDES the
          // far-out coalesce timer — N just landed, so N1 follows immediately.
          if (recoveryOperation.current === 'submitting') return false;
          if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
          draftSaveTimer.current = setTimeout(() => {
            if (recoveryOperation.current === 'submitting') return;
            if (doneRef.current && !otherFormsHaveContent(activeForm)) return;
            draftSaveTimer.current = null;
            void persistDraftNow(capturedSubject, verify);
          }, 400);
          return false;
        }
        if (result.kind === 'saved' || result.kind === 'unchanged') {
          if (result.stamp) session.stamp = result.stamp;
          draftUnsettledFailureRef.current = false;
          draftDirtyRef.current = false;
          setDraftStatus({state: 'saved', at: new Date().toLocaleTimeString()});
          // First durable commit since a legacy resume: the answers and their
          // original photo bytes now live under this session's key (one
          // transaction), so the stale unstamped row can retire — but only a
          // save that began after the handoff's own schedule counts. A no-op
          // outside that handoff; a failed retirement retains the row.
          void retireSupersededLegacyDraft({kind: 'durable-save', revAtStart});
          void retireOpenedCarrier({kind: 'durable-save', revAtStart});
          return true;
        }
        if (result.kind === 'conflict') {
          draftUnsettledFailureRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail:
              'A newer saved draft already exists for this account. Your edits are still on the form; nothing was overwritten.',
          });
          return false;
        }
        if (result.kind === 'not-current') {
          draftUnsettledFailureRef.current = true;
          setDraftStatus({state: 'failed', detail: 'The page changed while saving. Your edits are still on the form.'});
          return false;
        }
        draftUnsettledFailureRef.current = true;
        setDraftStatus({state: 'failed', detail: result.message});
        return false;
      } finally {
        draftSavingRef.current = false;
      }
    })();
    draftSavingPromiseRef.current = run;
    void run.finally(() => {
      if (draftSavingPromiseRef.current === run) draftSavingPromiseRef.current = null;
    });
    return run;
  }
  function scheduleDraftSave() {
    if (doneRef.current) {
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
      return;
    }
    if (!hubAliveRef.current) return;
    if (programmaticResetRef.current) {
      programmaticResetRef.current = false;
      return;
    }
    if (recoveryOperation.current === 'submitting') return;
    const session = draftSessionRef.current;
    if (!hubDraftHasContent() && !session?.stamp) {
      // Pristine mount: nothing pending, nothing owned — writes nothing.
      if (!session && !draftSaveTimer.current && !draftDirtyRef.current) return;
      // A meaningful→pristine transition is still a RAM change: the token
      // moves, so no in-flight ACK can paint Saved over the emptied form.
      draftEditsRef.current += 1;
      if (draftSavingRef.current) {
        // A1: the FIRST save is in flight. Its revision may land durably and
        // contradict the emptied RAM: dirty (and unsafe to leave) until the
        // ACK's completion settles the removal of its own revision.
        draftDirtyRef.current = true;
        // Emptied by the pending owner itself: a teardown before that ACK
        // settles carries the removal (the carrier resolves the landed stamp).
        // Any other subject never replaces the owner's candidate.
        const owner = scheduledSubjectRef.current;
        if (owner !== null && lastAuthenticatedUserId() === owner) {
          carrierCandidateRef.current = {
            owner,
            snapshot: captureHubDraft(),
            edits: draftEditsRef.current,
            removal: true,
            session,
          };
        }
        setDraftStatus((prev) => (prev.state === 'saving' ? prev : {state: 'saving'}));
        return;
      }
      // Nothing of this mount is durable and nothing is in flight (typed and
      // removed before the first send, or after an own delete): cancel the
      // never-sent save — there is no ACK to wait for.
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
      // Emptied by its own author with nothing of it durable or in flight:
      // nothing to carry. A foreign or unresolved emptying keeps the owner's.
      if (scheduledSubjectRef.current === null || lastAuthenticatedUserId() === scheduledSubjectRef.current) {
        carrierCandidateRef.current = null;
      }
      if (!draftUnsettledFailureRef.current) {
        draftDirtyRef.current = false;
        if (!session) scheduledSubjectRef.current = null; // nothing was ever owned
        setDraftStatus({state: 'idle'});
      }
      return;
    }
    // A meaningful edit is pending work: the status says so immediately, not
    // only when the save eventually fires. THE AUTHOR BINDS HERE,
    // SYNCHRONOUSLY: the App auth subscription has already delivered the
    // account that typed this (authIdentityMemory). The async SDK read is
    // only a VERIFICATION of the captured actor — its resolution can never
    // reassign authorship (a queued A read resolving under B adopts
    // nothing). An unknown subject keeps the work pending/unassigned
    // truthfully.
    setDraftStatus((prev) => (prev.state === 'saving' ? prev : {state: 'saving'}));
    const capturedSubject = lastAuthenticatedUserId();
    // WHOLE-RAM CUSTODY: pending scheduled work keeps the account it was
    // typed under. A second actor's edit cannot relabel that snapshot — it
    // is quarantined truthfully instead, and the original author's pending
    // work continues to own the key.
    const pendingOwner = scheduledSubjectRef.current;
    if (pendingOwner !== null && capturedSubject !== pendingOwner) {
      // An established owner is never relinquished by a missing or switched
      // subject: the RAM stays with its original author, unsaved.
      draftUnsettledFailureRef.current = true;
      // A meaningful RAM mutation invalidates the pending owned-clear's
      // captured revision even when its author is unresolved/foreign and
      // the save is quarantined: the clear must never settle over it.
      draftDirtyRef.current = true;
      draftEditsRef.current += 1;
      setDraftStatus({
        state: 'failed',
        detail:
          capturedSubject === null
            ? 'The signed-in account is unresolved. This draft stays with its original author; nothing is saved right now.'
            : 'This draft belongs to a different signed-in account. Your keystrokes are visible but not saved.',
      });
      return;
    }
    scheduledSubjectRef.current = capturedSubject;
    draftDirtyRef.current = true;
    draftEditsRef.current += 1;
    const clearingOwnedWork = !hubDraftHasContent() && !!session?.stamp;
    // The newest snapshot of the work under its own author (null when that
    // author is unknown — then it is never written or offered). Emptying
    // owned saved work is a removal: the carrier keeps it as a tombstone.
    carrierCandidateRef.current = {
      owner: capturedSubject,
      snapshot: captureHubDraft(),
      edits: draftEditsRef.current,
      removal: clearingOwnedWork,
      session: session ?? null,
    };
    clearPendingRef.current = clearingOwnedWork;
    // The clear's durable target is captured NOW: an N1 save that advances
    // the session stamp afterwards makes the old clear's CAS a 'kept', never
    // a deletion of the newer revision. The stamp is gated non-null HERE:
    // an owned clear exists only over a real current revision — a stamp-less
    // session is never handed a fabricated one.
    const clearTarget = clearingOwnedWork && session?.stamp ? {csid: session.csid, stamp: session.stamp} : null;
    clearTargetRef.current = clearTarget;
    // The owned-clear INTENT carries its own captured author and verify
    // reader; a new meaningful edit supersedes it (content exists again —
    // the save path settles ownership truthfully instead).
    clearIntentRef.current = clearingOwnedWork && clearTarget ? {target: clearTarget, capturedSubject, verify} : null;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      draftSaveTimer.current = null;
      void (clearingOwnedWork && clearTarget
        ? clearOwnedDraftNow(clearTarget, capturedSubject, verify)
        : persistDraftNow(capturedSubject, verify));
    }, 2500);
  }
  React.useEffect(() => {
    scheduleDraftSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the edit surface is exactly the duplicate-void effect's
  }, [
    bForm,
    lForm,
    pForm,
    eForm,
    cForm,
    sForm,
    extraBroilerGroups,
    extraLayerGroups,
    extraPigGroups,
    bPhotos,
    lPhotos,
    pPhotos,
    cPhotos,
    shPhotos,
  ]);
  React.useEffect(() => {
    function onDraftHidden() {
      if (document.visibilityState === 'hidden') void flushDraftSave();
    }
    function onDraftPageHide() {
      void flushDraftSave();
    }
    document.addEventListener('visibilitychange', onDraftHidden);
    window.addEventListener('pagehide', onDraftPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onDraftHidden);
      window.removeEventListener('pagehide', onDraftPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable handlers over refs
  }, []);
  // The operator emptied the last meaningful field of OWNED saved work:
  // conditionally remove exactly the CAPTURED revision through the
  // photo-aware CAS, under the author captured at schedule time, with the
  // LIVE account verified before the transaction and at every in-tx
  // boundary (synchronously, via the auth memory — no await inside the
  // open transaction). Never a blind delete, never a pristine-mount write,
  // never a silent failure, and never custody retired over newer RAM
  // edits. An unresolved attempt RETAINS its intent: the next flush
  // retries it.
  async function clearOwnedDraftNow(
    target: {csid: string; stamp: LocalDraftStamp},
    capturedSubject: string | null,
    verify: () => Promise<string | null>,
  ): Promise<boolean> {
    if (clearInFlightPromiseRef.current) return clearInFlightPromiseRef.current; // join, never fork
    const run = (async (): Promise<boolean> => {
      clearPendingRef.current = false;
      const session = draftSessionRef.current;
      const editsAtClear = draftEditsRef.current;
      if (!session || session.csid !== target.csid) {
        draftDirtyRef.current = !!draftSessionRef.current;
        return false;
      }
      if (!capturedSubject) {
        draftUnsettledFailureRef.current = true;
        draftDirtyRef.current = true;
        setDraftStatus({state: 'failed', detail: 'The sign-in state is unresolved. Your edits are kept; retry.'});
        return false;
      }
      const live = await verify();
      if (live !== capturedSubject) {
        draftUnsettledFailureRef.current = true;
        draftDirtyRef.current = true;
        setDraftStatus({
          state: 'failed',
          detail: 'The signed-in account changed. Your edits are kept; nothing is saved under the new account.',
        });
        return false;
      }
      const result = await clearHubDraftCas(sb, {
        csid: target.csid,
        stamp: target.stamp,
        actorProfileIdOverride: capturedSubject,
        stillCurrent: () =>
          hubAliveRef.current &&
          draftSessionRef.current === session &&
          draftEditsRef.current === editsAtClear &&
          lastAuthenticatedUserId() === capturedSubject,
      });
      if (result === 'deleted' || result === 'absent') {
        // The scheduled intent has settled. Newer RAM edits survive with
        // FRESH post-delete custody (their durable base is gone, so the
        // next save starts clean); only an unchanged session at the
        // captured revision retires to idle.
        clearIntentRef.current = null;
        if (draftSessionRef.current === session) session.stamp = null;
        // An opened tombstone's intent is exactly this settled removal.
        void retireOpenedCarrier({kind: 'cleared', csid: target.csid});
        if (draftSessionRef.current === session && draftEditsRef.current === editsAtClear) {
          draftSessionRef.current = null;
          scheduledSubjectRef.current = null; // verified pristine transition releases ownership
          carrierCandidateRef.current = null; // the removal settled; nothing is left to carry
          draftDirtyRef.current = false;
          draftUnsettledFailureRef.current = false;
          setDraftStatus({state: 'idle'});
          const active = getActiveHubSession();
          if (active?.csid === target.csid) {
            clearActiveHubSession();
          }
        }
        // A newer unsaved edit exists (the token moved past the captured
        // revision): the deletion settled, but the caller must know the
        // newer content is still undrained — not a clean settlement.
        return draftEditsRef.current === editsAtClear;
      }
      if (result === 'kept') {
        // A newer revision owns the key: the intent is moot (nothing of
        // ours is left to remove) and the newer content stays.
        clearIntentRef.current = null;
        draftDirtyRef.current = true;
        const settledHere =
          draftSessionRef.current === session &&
          draftEditsRef.current === editsAtClear &&
          !draftSavingRef.current &&
          !hubDraftHasContent();
        if (
          settledHere &&
          session.stamp &&
          session.stamp.mountId === target.stamp.mountId &&
          session.stamp.revision === target.stamp.revision
        ) {
          // The captured revision was this session's own CURRENT one: a newer
          // writer (another tab of this account) superseded it. This session
          // holds no durable revision now — it never deletes or adopts the
          // newer one — so the emptied RAM and its own durability agree.
          session.stamp = null;
          draftDirtyRef.current = false;
          setDraftStatus({state: 'idle'});
          return true;
        }
        return false;
      }
      draftUnsettledFailureRef.current = true;
      setDraftStatus({state: 'failed', detail: 'Could not remove the saved draft. Retry.'});
      return false;
    })();
    clearInFlightPromiseRef.current = run;
    void run.finally(() => {
      if (clearInFlightPromiseRef.current === run) clearInFlightPromiseRef.current = null;
    });
    return run;
  }
  async function flushDraftSave(): Promise<boolean> {
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }
    if (doneRef.current) {
      if (clearInFlightPromiseRef.current) return clearInFlightPromiseRef.current;
      if (clearIntentRef.current) {
        const intent = clearIntentRef.current;
        return clearOwnedDraftNow(intent.target, intent.capturedSubject, intent.verify);
      }
      return true;
    }
    if (!hubAliveRef.current || recoveryOperation.current === 'submitting') return false;
    // Join an in-flight clear, settle a scheduled owned-clear (pending OR
    // previously failed — the intent is retained until it settles), else
    // save.
    if (clearInFlightPromiseRef.current) return clearInFlightPromiseRef.current;
    if (clearIntentRef.current) {
      const intent = clearIntentRef.current;
      return clearOwnedDraftNow(intent.target, intent.capturedSubject, intent.verify);
    }
    // Already durably written and unchanged since: nothing to do. Content
    // that was never written (or failed) attempts one save now — the CAS
    // save is idempotent for unchanged content.
    if (!draftDirtyRef.current && draftSessionRef.current?.stamp) return true;
    if (!draftDirtyRef.current && !hubDraftHasContent()) return true;
    if (!hubDraftHasContent() && !draftSavingRef.current) {
      // Emptied RAM, nothing in flight, no pending intent: there is nothing
      // to save. This session's OWN acknowledged revision, if it still holds
      // one, contradicts the emptied form — settle it through the owned-clear
      // CAS (exactly that stamp; a newer one is kept). Owning none, the RAM
      // and its durability already agree.
      const session = draftSessionRef.current;
      if (session?.stamp) {
        return clearOwnedDraftNow(
          {csid: session.csid, stamp: session.stamp},
          scheduledSubjectRef.current ?? lastAuthenticatedUserId(),
          verify,
        );
      }
      if (!draftUnsettledFailureRef.current) {
        draftDirtyRef.current = false;
        setDraftStatus({state: 'idle'});
        return true;
      }
    }
    // An unscheduled flush (navigation/settle of never-scheduled content)
    // binds the CURRENT live subject: it is the custody event.
    return persistDraftNow(scheduledSubjectRef.current ?? lastAuthenticatedUserId(), verify);
  }
  // Leaving the open form SETTLES the pending save first: the switch proceeds
  // only when the draft is durably committed (or nothing needs saving). A
  // held, failed or still-undrained save blocks the switch with the truthful
  // status and the answers stay on the form. The returned promise settles
  // when the decision did (callers may await it; event handlers ignore it).
  function switchForm(id: string | null): Promise<void> {
    return (async () => {
      const settled = await flushDraftSave();
      if (!settled || draftLeaveUnsafe()) {
        setDraftStatus({
          state: 'failed',
          detail: 'This draft is not saved yet. Retry the save before switching forms.',
        });
        return;
      }
      setActiveForm(id);
    })();
  }
  function draftLeaveUnsafe(): boolean {
    // A scheduled, in-flight or FAILED owned-clear is pending work; so is
    // dirty content with meaningful fields. Failure stays unsafe even when
    // the fields are empty — leaving would resurrect the supposedly
    // removed draft.
    return (
      clearIntentRef.current !== null ||
      clearInFlightPromiseRef.current !== null ||
      clearPendingRef.current ||
      draftUnsettledFailureRef.current ||
      (draftDirtyRef.current && hubDraftHasContent()) ||
      // An emptied form whose own saved revision, or a save still in flight,
      // holds the removed answers durably until its removal settles.
      (draftDirtyRef.current && (draftSavingRef.current || !!draftSessionRef.current?.stamp))
    );
  }
  useAppDraftGuard({
    ownerKey: 'webform-hub-drafts',
    isUnsafeToLeave: () => draftLeaveUnsafe(),
    settleBeforeLeave: () => (draftSavingRef.current ? Promise.resolve(false) : flushDraftSave()),
  });
  // Browser unload cannot await IndexedDB: a dirty unconfirmed draft or an
  // unsettled owned-clear prompts instead of silently losing the answers.
  React.useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (draftLeaveUnsafe()) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
  // TEARDOWN HAND-OFF. The App unmounts the Hub when the signed-in account
  // changes, and no guard can hold that: the owner's newest work — pending
  // answers, original photos, or an emptied form whose owned clear had not
  // settled — would end with the mount while only an older revision is
  // durable. The newest OWNED snapshot, still unsettled, goes to the carrier,
  // bound to the author captured when it was typed (never the live account).
  // The carrier joins this mount's in-flight save/clear (already not current)
  // before it reads anything. Nothing is cleared, sent or relabelled here.
  function releaseHubDraftToCarrier() {
    const candidate = carrierCandidateRef.current;
    carrierCandidateRef.current = null;
    // A coalesced save or clear can no longer run for a torn-down mount.
    if (draftSaveTimer.current) {
      clearTimeout(draftSaveTimer.current);
      draftSaveTimer.current = null;
    }

    const liveSubject = lastAuthenticatedUserId();
    const session = draftSessionRef.current;
    const currentCsid = session?.csid ?? localDraftCsid;
    const currentStamp = session?.stamp ?? null;
    const pendingOwner = scheduledSubjectRef.current ?? candidate?.owner ?? null;

    if (!doneRef.current) {
      if (
        candidate?.removal ||
        clearIntentRef.current !== null ||
        !hubDraftHasContent() ||
        (pendingOwner && liveSubject && pendingOwner !== liveSubject)
      ) {
        clearActiveHubSession();
      } else if (currentCsid && liveSubject && (!pendingOwner || pendingOwner === liveSubject)) {
        const unsettled =
          draftDirtyRef.current ||
          draftUnsettledFailureRef.current ||
          draftSavingRef.current ||
          clearPendingRef.current ||
          clearIntentRef.current !== null ||
          clearInFlightPromiseRef.current !== null;

        recordActiveHubSession({
          owner: liveSubject,
          csid: currentCsid,
          stamp: currentStamp,
          snapshot: captureHubDraft(),
          hasUnsettledWork: unsettled,
        });
      }
    }

    if (doneRef.current) return;
    if (!candidate) return;
    const unsettled =
      draftDirtyRef.current ||
      draftUnsettledFailureRef.current ||
      draftSavingRef.current ||
      clearPendingRef.current ||
      clearIntentRef.current !== null ||
      clearInFlightPromiseRef.current !== null;
    if (!unsettled) return;
    carryHubDraft({
      owner: candidate.owner,
      snapshot: candidate.snapshot,
      edits: candidate.edits,
      removal: candidate.removal,
      session: candidate.session ?? draftSessionRef.current,
      pending: [draftSavingPromiseRef.current, clearInFlightPromiseRef.current].filter(
        (work): work is Promise<boolean> => work !== null,
      ),
    });
  }
  React.useEffect(
    () => () => releaseHubDraftToCarrier(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads refs only; must run exactly once, at teardown
    [],
  );
  async function handleDoneEffect(): Promise<void> {
    const captured = submittedDraftRef.current;
    let hasRemainder = false;
    try {
      if (!captured) {
        // Submitted with no durable interim save: if the answers came from a
        // resumed legacy draft, the server now holds them and the stale local
        // copy must not stay resumable. (A stamped submit retires its own row
        // through the CAS clear below; a non-resume mount has no pending
        // retirement and this is a no-op.)
        void retireSupersededLegacyDraft({kind: 'settled-submit'});
        return;
      }
      const submittedKind = captured.formKind as keyof HubDraft['forms'];
      const current = captureHubDraft();
      const lastDisp = lastDispositionRef.current;
      const disp =
        lastDisp ??
        computeSuccessDisposition(
          submittedKind,
          (current.forms[submittedKind] || {}) as Record<string, unknown>,
          captured.formSnapshot,
          (freshFormsRef.current?.[submittedKind] || {}) as Record<string, unknown>,
          (current.extras as Record<string, unknown[]>)[submittedKind] || [],
          captured.extraSnapshot,
          (current.photos as Record<string, File[]>)[submittedKind] || [],
          captured.photoSnapshot,
        );
      const otherFormsHaveContent = (Object.keys(current.forms) as Array<keyof HubDraft['forms']>).some(
        (key) =>
          key !== submittedKind &&
          !isPristineForm(current.forms[key], freshFormsRef.current?.[key] || {}, draftExtrasRef.current[key]),
      );
      hasRemainder = otherFormsHaveContent || disp.hasNewerEdits;

      if (!hasRemainder) {
        clearActiveHubSession();
        if (!captured.csid || !captured.stamp) {
          void retireSupersededLegacyDraft({kind: 'settled-submit'});
          await finishSubmittedDraftClear('deleted', captured);
          return;
        }
        const session = draftSessionRef.current;
        if (!session || session.instanceId !== captured.instanceId) return;
        const attempt = ++session.attempt;
        const capturedOwner = captured.owner;
        const liveSubject = await readLiveHubSubject();
        if (!liveSubject || liveSubject !== capturedOwner) {
          draftUnsettledFailureRef.current = true;
          draftDirtyRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail: 'The signed-in account changed. Draft kept on device; not cleared under new account.',
          });
          return;
        }
        const result = await clearHubDraftCas(sb, {
          csid: captured.csid,
          stamp: captured.stamp,
          actorProfileIdOverride: capturedOwner,
          stillCurrent: () =>
            hubAliveRef.current &&
            draftSessionRef.current === session &&
            session.attempt === attempt &&
            lastAuthenticatedUserId() === capturedOwner,
        });
        await finishSubmittedDraftClear(result, captured);
      } else {
        const remainder: HubDraft = {
          ...current,
          done: false,
          doneState: 'synced',
          forms: {
            ...current.forms,
            [submittedKind]: disp.form as any,
          },
          extras: {
            ...current.extras,
            ...(submittedKind in current.extras ? {[submittedKind]: disp.extras} : {}),
          },
          photos: {
            ...current.photos,
            ...(submittedKind in current.photos ? {[submittedKind]: disp.photos} : {}),
          },
        };

        if (!captured.csid || !captured.stamp) {
          scheduleDraftSave();
          return;
        }

        const session = draftSessionRef.current;
        if (!session || session.instanceId !== captured.instanceId) return;
        const attempt = ++session.attempt;
        const revAtStart = draftEditsRef.current;
        const capturedOwner = captured.owner;

        const liveSubject = await readLiveHubSubject();
        if (!liveSubject || liveSubject !== capturedOwner) {
          draftUnsettledFailureRef.current = true;
          draftDirtyRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail: 'The signed-in account changed. Remainder draft kept on device; not saved under new account.',
          });
          return;
        }

        let result = await saveHubDraftCas(sb, remainder, {
          instanceId: session.instanceId,
          expectStamp: captured.stamp,
          expectedActor: capturedOwner,
          stillCurrent: () =>
            hubAliveRef.current &&
            draftSessionRef.current === session &&
            session.attempt === attempt &&
            lastAuthenticatedUserId() === capturedOwner,
        });

        let activeRemainder: HubDraft = remainder;
        let currentAttempt = attempt;
        if (result.kind === 'conflict' && hubAliveRef.current) {
          try {
            const latestCustody = await loadHubDraftCustody(sb, captured.csid);
            const liveOwner = await readLiveHubSubject();
            if (latestCustody?.stamp && liveOwner === capturedOwner && draftSessionRef.current === session) {
              const latestDraft = await loadHubDraft(sb, captured.csid);
              const reDisp = computeSuccessDisposition(
                submittedKind,
                ((latestDraft.forms as Record<string, Record<string, unknown>>)[submittedKind] || {}) as Record<
                  string,
                  unknown
                >,
                captured.formSnapshot,
                (freshFormsRef.current?.[submittedKind] || {}) as Record<string, unknown>,
                (latestDraft.extras as Record<string, unknown[]>)[submittedKind] || [],
                captured.extraSnapshot,
                (latestDraft.photos as Record<string, File[]>)[submittedKind] || [],
                captured.photoSnapshot,
              );
              const reconciledRemainder: HubDraft = {
                ...latestDraft,
                done: false,
                doneState: 'synced',
                forms: {
                  ...latestDraft.forms,
                  [submittedKind]: reDisp.form as any,
                },
                extras: {
                  ...latestDraft.extras,
                  ...(submittedKind in latestDraft.extras ? {[submittedKind]: reDisp.extras} : {}),
                },
                photos: {
                  ...latestDraft.photos,
                  ...(submittedKind in latestDraft.photos ? {[submittedKind]: reDisp.photos} : {}),
                },
              };
              currentAttempt = ++session.attempt;
              result = await saveHubDraftCas(sb, reconciledRemainder, {
                instanceId: session.instanceId,
                expectStamp: latestCustody.stamp,
                expectedActor: capturedOwner,
                stillCurrent: () =>
                  hubAliveRef.current &&
                  draftSessionRef.current === session &&
                  session.attempt === currentAttempt &&
                  lastAuthenticatedUserId() === capturedOwner,
              });
              if (result.kind === 'saved' || result.kind === 'unchanged') {
                activeRemainder = reconciledRemainder;
              }
            }
          } catch {
            // Keep original result if re-read fails
          }
        }

        const postLive = await readLiveHubSubject();
        const sameSessionAndOwner =
          postLive === capturedOwner && draftSessionRef.current === session && session.attempt === currentAttempt;
        const unchangedRam = sameSessionAndOwner && draftEditsRef.current === revAtStart;

        if (result.kind === 'saved') {
          if (sameSessionAndOwner) {
            session.stamp = result.stamp;
            setLocalDraftCsid(session.csid);
          }
          if (unchangedRam) {
            draftDirtyRef.current = false;
            setDraftStatus({state: 'saved', at: new Date().toLocaleTimeString()});
          } else {
            draftDirtyRef.current = true;
            if (sameSessionAndOwner) {
              scheduleDraftSave();
            }
          }
          recordActiveHubSession({
            owner: capturedOwner,
            csid: session.csid,
            stamp: session.stamp,
            snapshot: activeRemainder,
            hasUnsettledWork: draftDirtyRef.current,
          });
          carrierCandidateRef.current = {
            owner: capturedOwner,
            snapshot: activeRemainder,
            edits: draftEditsRef.current,
            removal: false,
            session: {
              instanceId: session.instanceId,
              csid: session.csid,
              stamp: session.stamp,
              attempt: session.attempt,
            },
          };
        } else if (result.kind === 'unchanged') {
          if (sameSessionAndOwner) {
            setLocalDraftCsid(session.csid);
          }
          if (unchangedRam) {
            draftDirtyRef.current = false;
            setDraftStatus({state: 'saved', at: new Date().toLocaleTimeString()});
          }
          recordActiveHubSession({
            owner: capturedOwner,
            csid: session.csid,
            stamp: session.stamp,
            snapshot: activeRemainder,
            hasUnsettledWork: draftDirtyRef.current,
          });
          carrierCandidateRef.current = {
            owner: capturedOwner,
            snapshot: activeRemainder,
            edits: draftEditsRef.current,
            removal: false,
            session: {
              instanceId: session.instanceId,
              csid: session.csid,
              stamp: session.stamp,
              attempt: session.attempt,
            },
          };
        } else if (result.kind === 'conflict') {
          draftDirtyRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail: 'Another tab updated this draft. Your remaining answers are still on this form.',
          });
        } else {
          draftDirtyRef.current = true;
          setDraftStatus({
            state: 'failed',
            detail: result.kind === 'failed' ? result.message : 'The session or draft is no longer current.',
          });
        }
      }
    } finally {
      if (submittedDraftRef.current === captured) {
        submittedDraftRef.current = null;
        programmaticResetRef.current = false;
        if (!hasRemainder) {
          carrierCandidateRef.current = null;
          draftDirtyRef.current = false;
          if (draftSaveTimer.current) {
            clearTimeout(draftSaveTimer.current);
            draftSaveTimer.current = null;
          }
        } else if (draftDirtyRef.current && hubAliveRef.current) {
          scheduleDraftSave();
        }
      }
    }
  }
  // A confirmed submit clears ONLY the exact revision it submitted, and only
  // retires local custody when that same session — with no newer edits — is
  // still the one holding the key. A revision typed since Submit is 'kept',
  // never removed by a late submit callback.
  React.useEffect(() => {
    if (!done) return;
    void handleDoneEffect();
  }, [done]);
  const draftStatusEl =
    draftStatus.state === 'idle' ? null : (
      <div
        role="status"
        data-hub-draft-status={draftStatus.state}
        style={{
          fontSize: 11,
          marginBottom: 8,
          color: draftStatus.state === 'failed' ? 'var(--danger)' : 'var(--ink-muted)',
        }}
      >
        {draftStatus.state === 'saving'
          ? 'Saving this draft on this device…'
          : draftStatus.state === 'saved'
            ? 'Draft saved on this device' +
              (draftStatus.at ? ' at ' + draftStatus.at : '') +
              '. Nothing is sent automatically.'
            : 'Draft NOT saved on this device' + (draftStatus.detail ? ' — ' + draftStatus.detail : '')}
      </div>
    );

  // Any change to what would be submitted voids a duplicate notice and any
  // pending update confirmation: both described the form as it was.
  React.useEffect(() => {
    confirmedDupIdsRef.current = null;
    if (dupHitsRef.current) {
      setDupHits(null);
      setErr('');
    }
  }, [
    bForm,
    lForm,
    pForm,
    eForm,
    cForm,
    sForm,
    extraBroilerGroups,
    extraLayerGroups,
    extraPigGroups,
    bPhotos,
    lPhotos,
    pPhotos,
    cPhotos,
    shPhotos,
  ]);

  // Shown under a date field holding any day but today, with a one-tap way
  // back to today. Never changes the date by itself.
  function renderUseToday<F extends {date: string}>(date: string, setForm: React.Dispatch<React.SetStateAction<F>>) {
    const today = todayStr();
    if (!date || date === today) return null;
    return (
      <div data-date-not-today="1" className="wcf-dup-help" style={{marginTop: 2}}>
        Not today’s date.
        <button
          disabled={submitting}
          type="button"
          data-use-today="1"
          className="wcf-use-today"
          onClick={() => setForm((f) => ({...f, date: today}))}
        >
          Use Today
        </button>
      </div>
    );
  }

  const wfBg = {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    padding: '1rem',
    fontFamily: 'inherit',
    color: '#1A1F24',
  };
  const logo = (
    <div style={{textAlign: 'center', marginBottom: 20}}>
      <a
        data-public-hub-main-link="dailys"
        href="/"
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: '#187A54',
          letterSpacing: -0.3,
          textDecoration: 'none',
        }}
      >
        WCF Planner
      </a>
    </div>
  );

  // Success screen — Phase 1D-B reads doneState ∈ {synced, queued, stuck}
  // for the included form-kinds. Layer + egg + multi-row direct paths set
  // doneState='synced' so existing copy is preserved.
  if (done)
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '2rem', textAlign: 'center'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <div style={{fontSize: 56, marginBottom: 12}}>
            {doneState === 'queued' ? '📡' : doneState === 'stuck' ? '⚠️' : '✅'}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: doneState === 'queued' ? '#92400e' : doneState === 'stuck' ? '#b91c1c' : '#187A54',
              marginBottom: 8,
            }}
          >
            {doneState === 'queued'
              ? 'Saved on this device'
              : doneState === 'stuck'
                ? 'Saved on this device — sync needs help'
                : 'Report Submitted!'}
          </div>
          {doneState === 'queued' && (
            <div
              data-submit-state="queued"
              style={{
                fontSize: 13,
                color: '#78716c',
                marginBottom: 22,
                lineHeight: 1.5,
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: 10,
                padding: '10px 14px',
                textAlign: 'left',
              }}
            >
              No connection right now. Daily report queued for <strong>{lastGroup}</strong> and will sync as soon as the
              device is back online.
            </div>
          )}
          {doneState === 'stuck' && (
            <div
              data-submit-state="stuck"
              style={{
                fontSize: 13,
                color: '#7f1d1d',
                marginBottom: 22,
                lineHeight: 1.5,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '10px 14px',
                textAlign: 'left',
              }}
            >
              Your daily report for <strong>{lastGroup}</strong> is saved on this device, but sync hit an error that
              won't fix itself by retrying. Open the stuck submissions panel to retry after a fix or discard.
            </div>
          )}
          {doneState === 'synced' && (
            <div style={{fontSize: 14, color: '#68707A', marginBottom: 24}}>
              <span data-submit-state="synced" style={{display: 'none'}}>
                synced
              </span>
              {lastGroup ? `${lastGroup} — ` : ''}saved successfully.
            </div>
          )}
          {anchorFailures.length > 0 && (
            <div
              data-anchor-state="failed"
              style={{
                fontSize: 13,
                color: '#7f1d1d',
                marginBottom: 22,
                lineHeight: 1.5,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '10px 14px',
                textAlign: 'left',
              }}
            >
              Your report is saved, but the hen count was not applied to{' '}
              <strong>{anchorFailures.map((a) => a.label).join(', ')}</strong>
              {anchorFailures.some((a) => a.reason === 'no-match' || a.reason === 'ambiguous-batch')
                ? ' — that housing could not be identified from the group name.'
                : ' — the housing count update was refused.'}{' '}
              The projected count keeps its previous anchor until this goes through.
              <div style={{marginTop: 8}}>
                <button
                  onClick={retryAnchors}
                  disabled={anchorRetrying}
                  data-anchor-retry="1"
                  style={{
                    padding: '8px 16px',
                    borderRadius: 10,
                    border: '1px solid #fecaca',
                    background: anchorRetrying ? '#fee2e2' : 'white',
                    color: '#7f1d1d',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: anchorRetrying ? 'not-allowed' : 'pointer',
                  }}
                >
                  {anchorRetrying ? 'Retrying…' : 'Retry hen count'}
                </button>
              </div>
            </div>
          )}
          <button
            disabled={submitting}
            onClick={resetAndAnother}
            style={{
              padding: '12px 28px',
              borderRadius: 10,
              border: 'none',
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
              width: '100%',
            }}
          >
            Submit Another
          </button>
          <button
            disabled={submitting}
            onClick={() => {
              switchForm(null);
              setDone(false);
            }}
            style={{
              padding: '12px 28px',
              borderRadius: 10,
              border: '1px solid #D2D6DB',
              background: 'white',
              color: '#1A1F24',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
            }}
          >
            Back to Forms
          </button>
        </div>
        {stuckModalEl}
      </div>
    );

  // Form selector
  if (!activeForm)
    return (
      <div style={wfBg}>
        {showAppSetup && <AppSetupModal onClose={() => setShowAppSetup(false)} />}
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <div style={{display: 'flex', justifyContent: 'center', marginBottom: 12}}>
            <button
              disabled={submitting}
              data-app-setup-trigger="1"
              onClick={() => setShowAppSetup(true)}
              style={{
                background: 'white',
                border: '1px solid #cfe6d8',
                borderRadius: 999,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 600,
                color: '#187A54',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              📲 App Setup
            </button>
          </div>
          {/* F047: hub picker cards are white with a gray border. The feature
              category is shown by the icon + black title; no colored card
              theme, heading, sub-text, or chevron. */}
          <div
            {...openableProps(function () {
              window.location.hash = '#addfeed';
              window.location.reload();
            })}
            className="hoverable-tile"
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '16px 18px',
              marginBottom: 10,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              border: '1px solid var(--border)',
            }}
          >
            <PlannerIcon iconKey="feed" size={32} />
            <div style={{flex: 1}}>
              <div style={{fontSize: 16, fontWeight: 700, color: 'var(--ink)'}}>Add Feed</div>
              <div style={{fontSize: 12, color: 'var(--ink-muted)'}}>Quick log feed added in the field</div>
              <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 2}}>
                {'Pig \u00b7 Broiler \u00b7 Layer \u00b7 Cattle \u00b7 Sheep'}
              </div>
            </div>
            <div style={{color: 'var(--ink-faint)', fontSize: 18}}>{'\u203a'}</div>
          </div>
          {!hideWeighIns && (
            <div
              {...openableProps(function () {
                window.location.hash = '#weighins';
                window.location.reload();
              })}
              className="hoverable-tile"
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 10,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                border: '1px solid var(--border)',
              }}
            >
              <PlannerIcon iconKey="weighins" size={32} />
              <div style={{flex: 1}}>
                <div style={{fontSize: 16, fontWeight: 700, color: 'var(--ink)'}}>Weigh-Ins</div>
                <div style={{fontSize: 12, color: 'var(--ink-muted)'}}>
                  Record weights for cattle, pigs, or broilers
                </div>
                <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 2}}>
                  {'Sessions auto-save \u00b7 resume on any device'}
                </div>
              </div>
              <div style={{color: 'var(--ink-faint)', fontSize: 18}}>{'\u203a'}</div>
            </div>
          )}
          {!hideCattleLog && (
            <div
              data-tile="cattle-log"
              {...openableProps(() => navigate('/cattle/log'))}
              className="hoverable-tile"
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 16,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                border: '1px solid var(--border)',
              }}
            >
              <PlannerIcon iconKey={ANIMAL_ICON_KEYS.cattle} size={32} />
              <div style={{flex: 1}}>
                <div style={{fontSize: 16, fontWeight: 700, color: 'var(--ink)'}}>Cattle Log</div>
                <div style={{fontSize: 12, color: 'var(--ink-muted)'}}>
                  Free-flowing notes and issues for the cattle program
                </div>
                <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 2}}>
                  {'Use #tag to link a cow \u00b7 @name to notify'}
                </div>
              </div>
              <div style={{color: 'var(--ink-faint)', fontSize: 18}}>{'\u203a'}</div>
            </div>
          )}
          <div
            data-tile="tasks"
            {...openableProps(() => navigate('/dailys/tasks'))}
            className="hoverable-tile"
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '16px 18px',
              marginBottom: 16,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              border: '1px solid var(--border)',
            }}
          >
            <PlannerIcon iconKey="checkmark" size={32} />
            <div style={{flex: 1}}>
              <div style={{fontSize: 16, fontWeight: 700, color: 'var(--ink)'}}>Submit a Task or a To Do</div>
              <div style={{fontSize: 12, lineHeight: 1.35, color: 'var(--ink-muted)'}}>
                Use this to assign tasks to a Wcf Planner user when there is a repair needed or anything that shouldn't
                be forgotten.
              </div>
            </div>
            <div style={{color: 'var(--ink-faint)', fontSize: 18}}>{'\u203a'}</div>
          </div>
          {[
            {id: 'broiler', iconKey: ANIMAL_ICON_KEYS.broiler, label: 'Broiler Daily Report', list: '/broiler/dailys'},
            {id: 'layer', iconKey: ANIMAL_ICON_KEYS.layer, label: 'Layer Daily Report', list: '/layer/dailys'},
            {id: 'egg', iconKey: ANIMAL_ICON_KEYS.egg, label: 'Egg Collection Report', list: '/layer/eggs'},
            {id: 'pig', iconKey: ANIMAL_ICON_KEYS.pig, label: 'Pig Daily Report', list: '/pig/dailys'},
            {id: 'cattle', iconKey: ANIMAL_ICON_KEYS.cattle, label: 'Cattle Daily Report', list: '/cattle/dailys'},
            {id: 'sheep', iconKey: ANIMAL_ICON_KEYS.sheep, label: 'Sheep Daily Report', list: '/sheep/dailys'},
          ].map((f) => (
            <div
              key={f.id}
              className="hoverable-tile"
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                border: '1px solid var(--border)',
              }}
            >
              <div
                {...openableProps(() => switchForm(f.id))}
                style={{flex: 1, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer'}}
              >
                <PlannerIcon iconKey={f.iconKey} size={22} />
                <div style={{flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--ink)'}}>{f.label}</div>
                {/* Inside the clickable region: as a sibling it kept the tile's
                    pointer cursor and hover lift while doing nothing. */}
                <div style={{color: 'var(--ink-faint)', fontSize: 18}}>{'›'}</div>
              </div>
              {/* The only entry point into a program's daily list/history that
                  does not run through that program's dashboard. It matters for a
                  farm_team user whose program_access omits this program: they may
                  read every program's daily reports (2026-09-08 decision) but the
                  program chip stays hidden because it targets the gated
                  dashboard, so without this the surface was reachable only by
                  typing the URL. */}
              {(dailyHistoryPrograms || []).includes(f.id) && (
                <button
                  disabled={submitting}
                  type="button"
                  data-daily-history-link={f.id}
                  onClick={() => navigate(f.list)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--ink-muted)',
                    cursor: 'pointer',
                  }}
                >
                  History
                </button>
              )}
            </div>
          ))}
        </div>
        {stuckModalEl}
      </div>
    );

  // ── BROILER FORM ──
  if (activeForm === 'broiler')
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            ‹ Back
          </button>
          <PlannerIconLabel
            iconKey={ANIMAL_ICON_KEYS.broiler}
            size={22}
            gap={8}
            style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}
          >
            Broiler Daily Report
          </PlannerIconLabel>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Date{reqStar('broiler-dailys', 'date')}</label>
                <input
                  disabled={submitting}
                  type="date"
                  value={bForm.date}
                  onChange={(e) => setBForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(bForm.date, setBForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Broiler Group{reqStar('broiler-dailys', 'batch_label')}</label>
                <select
                  disabled={submitting}
                  value={bForm.batchLabel}
                  onChange={(e) => setBForm((f) => ({...f, batchLabel: e.target.value}))}
                  style={inputStyle}
                >
                  <option value="">Select group...</option>
                  {bForm.batchLabel && !broilerGroups.includes(bForm.batchLabel) && (
                    <option value={bForm.batchLabel}>{bForm.batchLabel} (saved)</option>
                  )}
                  {broilerGroups.map((b) => (
                    <option key={b} value={b}>
                      {formatBroilerBatchLabel(b, broilerMeta)}
                    </option>
                  ))}
                </select>
              </div>
              {parseFloat(bForm.feedLbs) > 0 && (
                <div style={{gridColumn: '1/-1'}}>
                  <label style={labelStyle}>
                    Feed Type<span style={{color: '#dc2626', marginLeft: 2}}>*</span>
                  </label>
                  <Toggle
                    opts={['STARTER', 'GROWER']}
                    val={bForm.feedType}
                    onChange={(v) => setBForm((f) => ({...f, feedType: v}))}
                  />
                </div>
              )}
              <div>
                <label style={labelStyle}>Feed (lbs){reqStar('broiler-dailys', 'feed_lbs')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  step="0.1"
                  value={bForm.feedLbs || ''}
                  onChange={(e) => setBForm((f) => ({...f, feedLbs: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Grit (lbs){reqStar('broiler-dailys', 'grit_lbs')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  step="0.1"
                  value={bForm.gritLbs || ''}
                  onChange={(e) => setBForm((f) => ({...f, gritLbs: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
              <div>
                <label style={labelStyle}>Group moved?{reqStar('broiler-dailys', 'group_moved')}</label>
                <YN val={bForm.groupMoved} onChange={(v) => setBForm((f) => ({...f, groupMoved: v}))} />
              </div>
              <div>
                <label style={labelStyle}>Waterer checked?{reqStar('broiler-dailys', 'waterer_checked')}</label>
                <YN val={bForm.watererChecked} onChange={(v) => setBForm((f) => ({...f, watererChecked: v}))} />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div>
                <label style={labelStyle}>Mortalities{reqStar('broiler-dailys', 'mortality_count')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  value={bForm.mortalityCount || ''}
                  onChange={(e) => setBForm((f) => ({...f, mortalityCount: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              {parseInt(bForm.mortalityCount) > 0 && (
                <div>
                  <label style={labelStyle}>
                    Reason <span style={{color: '#dc2626'}}>*</span>
                  </label>
                  <input
                    disabled={submitting}
                    value={bForm.mortalityReason}
                    onChange={(e) => setBForm((f) => ({...f, mortalityReason: e.target.value}))}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={sectionStyle}>
            <label style={labelStyle}>Comments / Issues{reqStar('broiler-dailys', 'comments')}</label>
            <textarea
              disabled={submitting}
              value={bForm.comments}
              onChange={(e) => setBForm((f) => ({...f, comments: e.target.value}))}
              rows={3}
              placeholder="Type 0 if nothing to report"
              style={{...inputStyle, resize: 'vertical'}}
            />
          </div>
          {allowAddGroup('broiler-dailys') && (
            <div>
              {extraBroilerGroups.map((eg, ei) => (
                <div key={ei} style={{...sectionStyle, border: '1px solid var(--border)', marginTop: 0}}>
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}
                  >
                    <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ink)'}}>Additional Group {ei + 2}</div>
                    <button
                      disabled={submitting}
                      type="button"
                      onClick={() => setExtraBroilerGroups((p) => p.filter((_, i) => i !== ei))}
                      style={{background: 'none', border: 'none', color: '#68707A', cursor: 'pointer', fontSize: 18}}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Broiler Group{reqStar('broiler-dailys', 'batch_label')}</label>
                      <select
                        disabled={submitting}
                        value={eg.batchLabel || ''}
                        onChange={(e) =>
                          setExtraBroilerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, batchLabel: e.target.value} : g)),
                          )
                        }
                        style={inputStyle}
                      >
                        <option value="">Select group...</option>
                        {(recovery || localDraftCsid) && eg.batchLabel && !broilerGroups.includes(eg.batchLabel) && (
                          <option value={eg.batchLabel}>{eg.batchLabel} (saved)</option>
                        )}
                        {broilerGroups.map((b) => (
                          <option key={b} value={b}>
                            {formatBroilerBatchLabel(b, broilerMeta)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {parseFloat(eg.feedLbs) > 0 && (
                      <div style={{gridColumn: '1/-1'}}>
                        <label style={labelStyle}>
                          Feed Type<span style={{color: '#dc2626', marginLeft: 2}}>*</span>
                        </label>
                        <Toggle
                          opts={['STARTER', 'GROWER']}
                          val={eg.feedType || ''}
                          onChange={(v) =>
                            setExtraBroilerGroups((p) => p.map((g, i) => (i === ei ? {...g, feedType: v} : g)))
                          }
                        />
                      </div>
                    )}
                    <div>
                      <label style={labelStyle}>Feed (lbs){reqStar('broiler-dailys', 'feed_lbs')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.feedLbs || ''}
                        onChange={(e) =>
                          setExtraBroilerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, feedLbs: e.target.value} : g)),
                          )
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Grit (lbs){reqStar('broiler-dailys', 'grit_lbs')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.gritLbs || ''}
                        onChange={(e) =>
                          setExtraBroilerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, gritLbs: e.target.value} : g)),
                          )
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Group moved?{reqStar('broiler-dailys', 'group_moved')}</label>
                      <YN
                        val={eg.groupMoved !== false}
                        onChange={(v) =>
                          setExtraBroilerGroups((p) => p.map((g, i) => (i === ei ? {...g, groupMoved: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Waterer checked?{reqStar('broiler-dailys', 'waterer_checked')}</label>
                      <YN
                        val={eg.watererChecked !== false}
                        onChange={(v) =>
                          setExtraBroilerGroups((p) => p.map((g, i) => (i === ei ? {...g, watererChecked: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Mortalities{reqStar('broiler-dailys', 'mortality_count')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        value={eg.mortalityCount || ''}
                        onChange={(e) =>
                          setExtraBroilerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, mortalityCount: e.target.value} : g)),
                          )
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    {parseInt(eg.mortalityCount) > 0 && (
                      <div>
                        <label style={labelStyle}>
                          Mortality reason <span style={{color: '#dc2626'}}>*</span>
                        </label>
                        <input
                          disabled={submitting}
                          value={eg.mortalityReason || ''}
                          onChange={(e) =>
                            setExtraBroilerGroups((p) =>
                              p.map((g, i) => (i === ei ? {...g, mortalityReason: e.target.value} : g)),
                            )
                          }
                          style={inputStyle}
                        />
                      </div>
                    )}
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Comments / Issues{reqStar('broiler-dailys', 'comments')}</label>
                      <textarea
                        disabled={submitting}
                        value={eg.comments || ''}
                        onChange={(e) =>
                          setExtraBroilerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, comments: e.target.value} : g)),
                          )
                        }
                        rows={3}
                        placeholder="Type 0 if nothing to report"
                        style={{...inputStyle, resize: 'vertical'}}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                disabled={submitting}
                type="button"
                onClick={() =>
                  setExtraBroilerGroups((p) => [
                    ...p,
                    {batchLabel: '', feedLbs: '', gritLbs: '', mortalityCount: '', comments: ''},
                  ])
                }
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 10,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  color: 'var(--ink)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 12,
                }}
              >
                + Add Another Group
              </button>
            </div>
          )}
          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('poultry_dailys', submitBroiler)}
          <DailyPhotoCapture
            files={bPhotos}
            statuses={bPhotoStatuses}
            onChange={setBPhotos}
            disabled={submitting}
            offlineCapable={true}
          />
          <button
            onClick={submitBroiler}
            disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting
              ? `Submitting ${1 + extraBroilerGroups.filter((g) => g.batchLabel).length} report(s)…`
              : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );

  // ── LAYER FORM ──
  if (activeForm === 'layer')
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            ‹ Back
          </button>
          <PlannerIconLabel
            iconKey={ANIMAL_ICON_KEYS.layer}
            size={22}
            gap={8}
            style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}
          >
            Layer Daily Report
          </PlannerIconLabel>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Date{reqStar('layer-dailys', 'date')}</label>
                <input
                  disabled={submitting}
                  type="date"
                  value={lForm.date}
                  onChange={(e) => setLForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(lForm.date, setLForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Layer Group{reqStar('layer-dailys', 'batch_label')}</label>
                <select
                  disabled={submitting}
                  value={lForm.batchLabel}
                  onChange={(e) => setLForm((f) => ({...f, batchLabel: e.target.value}))}
                  style={inputStyle}
                >
                  <option value="">Select group...</option>
                  {(recovery || localDraftCsid) && lForm.batchLabel && !layerGroupNames.includes(lForm.batchLabel) && (
                    <option value={lForm.batchLabel}>{lForm.batchLabel} (saved)</option>
                  )}
                  {layerGroupNames.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
                {layerGroupsNotice}
              </div>
              {parseFloat(lForm.feedLbs) > 0 && (
                <div style={{gridColumn: '1/-1'}}>
                  <label style={labelStyle}>
                    Feed Type<span style={{color: '#dc2626', marginLeft: 2}}>*</span>
                  </label>
                  <Toggle
                    opts={['LAYER', 'STARTER', 'GROWER']}
                    val={lForm.feedType}
                    onChange={(v) => setLForm((f) => ({...f, feedType: v}))}
                  />
                </div>
              )}
              <div>
                <label style={labelStyle}>Feed (lbs){reqStar('layer-dailys', 'feed_lbs')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  step="0.1"
                  value={lForm.feedLbs || ''}
                  onChange={(e) => setLForm((f) => ({...f, feedLbs: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Grit (lbs){reqStar('layer-dailys', 'grit_lbs')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  step="0.1"
                  value={lForm.gritLbs || ''}
                  onChange={(e) => setLForm((f) => ({...f, gritLbs: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Current Hen Count{reqStar('layer-dailys', 'layer_count')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  value={lForm.layerCount || ''}
                  onChange={(e) => setLForm((f) => ({...f, layerCount: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
              <div>
                <label style={labelStyle}>Group moved?{reqStar('layer-dailys', 'group_moved')}</label>
                <YN val={lForm.groupMoved} onChange={(v) => setLForm((f) => ({...f, groupMoved: v}))} />
              </div>
              <div>
                <label style={labelStyle}>Waterer checked?{reqStar('layer-dailys', 'waterer_checked')}</label>
                <YN val={lForm.watererChecked} onChange={(v) => setLForm((f) => ({...f, watererChecked: v}))} />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div>
                <label style={labelStyle}>Mortalities{reqStar('layer-dailys', 'mortality_count')}</label>
                <input
                  disabled={submitting}
                  type="number"
                  min="0"
                  value={lForm.mortalityCount || ''}
                  onChange={(e) => setLForm((f) => ({...f, mortalityCount: e.target.value}))}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              {parseInt(lForm.mortalityCount) > 0 && (
                <div>
                  <label style={labelStyle}>
                    Reason <span style={{color: '#dc2626'}}>*</span>
                  </label>
                  <input
                    disabled={submitting}
                    value={lForm.mortalityReason}
                    onChange={(e) => setLForm((f) => ({...f, mortalityReason: e.target.value}))}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={sectionStyle}>
            <label style={labelStyle}>Comments / Issues{reqStar('layer-dailys', 'comments')}</label>
            <textarea
              disabled={submitting}
              value={lForm.comments}
              onChange={(e) => setLForm((f) => ({...f, comments: e.target.value}))}
              rows={3}
              placeholder="Type 0 if nothing to report"
              style={{...inputStyle, resize: 'vertical'}}
            />
          </div>
          {allowAddGroup('layer-dailys') && (
            <div>
              {extraLayerGroups.map((eg, ei) => (
                <div key={ei} style={{...sectionStyle, border: '1px solid var(--border)', marginTop: 0}}>
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}
                  >
                    <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ink)'}}>Additional Group {ei + 2}</div>
                    <button
                      disabled={submitting}
                      type="button"
                      onClick={() => setExtraLayerGroups((p) => p.filter((_, i) => i !== ei))}
                      style={{background: 'none', border: 'none', color: '#68707A', cursor: 'pointer', fontSize: 18}}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Layer Group{reqStar('layer-dailys', 'batch_label')}</label>
                      <select
                        disabled={submitting}
                        value={eg.batchLabel || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, batchLabel: e.target.value} : g)),
                          )
                        }
                        style={inputStyle}
                      >
                        <option value="">Select group...</option>
                        {(recovery || localDraftCsid) && eg.batchLabel && !layerGroupNames.includes(eg.batchLabel) && (
                          <option value={eg.batchLabel}>{eg.batchLabel} (saved)</option>
                        )}
                        {layerGroupNames.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </div>
                    {parseFloat(eg.feedLbs) > 0 && (
                      <div style={{gridColumn: '1/-1'}}>
                        <label style={labelStyle}>
                          Feed Type<span style={{color: '#dc2626', marginLeft: 2}}>*</span>
                        </label>
                        <Toggle
                          opts={['LAYER', 'STARTER', 'GROWER']}
                          val={eg.feedType || ''}
                          onChange={(v) =>
                            setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, feedType: v} : g)))
                          }
                        />
                      </div>
                    )}
                    <div>
                      <label style={labelStyle}>Feed (lbs){reqStar('layer-dailys', 'feed_lbs')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.feedLbs || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, feedLbs: e.target.value} : g)))
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Grit (lbs){reqStar('layer-dailys', 'grit_lbs')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.gritLbs || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, gritLbs: e.target.value} : g)))
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Hen Count{reqStar('layer-dailys', 'layer_count')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        value={eg.layerCount || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, layerCount: e.target.value} : g)),
                          )
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Group moved?{reqStar('layer-dailys', 'group_moved')}</label>
                      <YN
                        val={eg.groupMoved !== false}
                        onChange={(v) =>
                          setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, groupMoved: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Waterer checked?{reqStar('layer-dailys', 'waterer_checked')}</label>
                      <YN
                        val={eg.watererChecked !== false}
                        onChange={(v) =>
                          setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, watererChecked: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Mortalities{reqStar('layer-dailys', 'mortality_count')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        value={eg.mortalityCount || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, mortalityCount: e.target.value} : g)),
                          )
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    {parseInt(eg.mortalityCount) > 0 && (
                      <div>
                        <label style={labelStyle}>
                          Mortality reason <span style={{color: '#dc2626'}}>*</span>
                        </label>
                        <input
                          disabled={submitting}
                          value={eg.mortalityReason || ''}
                          onChange={(e) =>
                            setExtraLayerGroups((p) =>
                              p.map((g, i) => (i === ei ? {...g, mortalityReason: e.target.value} : g)),
                            )
                          }
                          style={inputStyle}
                        />
                      </div>
                    )}
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Comments / Issues{reqStar('layer-dailys', 'comments')}</label>
                      <textarea
                        disabled={submitting}
                        value={eg.comments || ''}
                        onChange={(e) =>
                          setExtraLayerGroups((p) => p.map((g, i) => (i === ei ? {...g, comments: e.target.value} : g)))
                        }
                        rows={3}
                        placeholder="Type 0 if nothing to report"
                        style={{...inputStyle, resize: 'vertical'}}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                disabled={submitting}
                type="button"
                onClick={() =>
                  setExtraLayerGroups((p) => [
                    ...p,
                    {batchLabel: '', feedLbs: '', gritLbs: '', layerCount: '', mortalityCount: ''},
                  ])
                }
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 10,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  color: 'var(--ink)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 12,
                }}
              >
                + Add Another Group
              </button>
            </div>
          )}
          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('layer_dailys', submitLayer)}
          <DailyPhotoCapture files={lPhotos} statuses={lPhotoStatuses} onChange={setLPhotos} disabled={submitting} />
          <button
            onClick={submitLayer}
            disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting
              ? `Submitting ${1 + extraLayerGroups.filter((g) => g.batchLabel).length} report(s)…`
              : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );

  // ── PIG FORM ──
  if (activeForm === 'pig') {
    // The authenticated bridge lookup, and nothing behind it. The old second
    // branch read WebformsConfigContext.wfGroups, whose ONLY writer was this
    // same bridge writing this same value back — so inside one mount it was
    // unreachable by construction, and across mounts it could only re-show a
    // PREVIOUS mount's option list after a failed read. That carry-over is
    // gone with the mirror; a failed lookup now shows an empty group list,
    // which is what the bridge's other pending/rejected reads already do.
    const pgGroups = pigGroupsFromDb;
    // "Days since last move" for one group label (owner request 2026-11-04),
    // counted from that group's latest group-moved = Yes report at or before
    // this report's date. '…' while the bridge read is pending; "Unavailable"
    // when that label's read failed (offline, for example). It never blocks a
    // submit. SOWS itself has none: each sow-group section shows its own.
    const renderDaysSinceMove = (label: unknown) => {
      const key = String(label || '').trim();
      if (!key || key.toUpperCase() === SOWS_GROUP_VALUE) return null;
      const history = pigContext?.moveHistory?.[key];
      const extraMoves = submittedMoves[key] || [];
      const combinedDates = history
        ? [...new Set([...extraMoves, ...history.dates])].sort((a, b) => b.localeCompare(a))
        : extraMoves.length > 0
          ? [...extraMoves].sort((a, b) => b.localeCompare(a))
          : null;
      const text =
        !pigContext && extraMoves.length === 0
          ? '…'
          : combinedDates
            ? daysSinceMoveText(daysSinceLastMove(combinedDates, pForm.date, history?.complete ?? true))
            : 'Unavailable';
      return (
        <div data-pig-days-since-move={key} style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 6}}>
          Days since last move: <b style={{color: 'var(--ink)'}}>{text}</b>
        </div>
      );
    };
    // SOWS: three sow-group sections, one report row each (owner request
    // 2026-11-04). A recovered queued pig row keeps the one-row form, so an
    // old combined 'SOWS' row is replaced exactly as it was queued.
    const sowsMode = isSowsSectionMode(pForm.batchLabel, recovery?.formKind === 'pig_dailys');
    const sowSections = sowSectionsOf(pForm);
    const setSowField = <K extends keyof SowSection>(index: number, field: K, value: SowSection[K]) =>
      setPForm((f) => ({...f, sowGroups: withSowSectionField(f, index, field, value)}));
    const renderSowSections = () => (
      <div data-pig-sow-sections>
        {SOW_GROUP_NUMBERS.map((n, i) => {
          const s = sowSections[i];
          const tags = pigContext?.sowTagsByGroup?.[n];
          return (
            <div key={n} style={sectionStyle} data-pig-sow-group={n}>
              <div style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>Sow Group {n}</div>
              <div data-pig-sow-tags={n} style={{fontSize: 12, color: 'var(--ink-muted)', marginBottom: 2}}>
                {!pigContext
                  ? 'Sow tags: …'
                  : !pigContext.sowTagsByGroup
                    ? 'Sow tags unavailable'
                    : tags && tags.length > 0
                      ? `Sows: ${tags.join(', ')}`
                      : 'No sows tagged in this group'}
              </div>
              {renderDaysSinceMove(sowGroupLabel(n))}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10}}>
                <div>
                  <label style={labelStyle}>Feed (lbs){reqStar('pig-dailys', 'feed_lbs')}</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    step="0.1"
                    value={s.feedLbs}
                    onChange={(e) => setSowField(i, 'feedLbs', e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Pig Count{reqStar('pig-dailys', 'pig_count')}</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    value={s.pigCount}
                    onChange={(e) => setSowField(i, 'pigCount', e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </div>
                <div style={{gridColumn: '1/-1'}}>
                  <label style={labelStyle}>Fence Voltage (kV){reqStar('pig-dailys', 'fence_voltage')}</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    step="0.1"
                    value={s.fenceVoltage}
                    onChange={(e) => setSowField(i, 'fenceVoltage', e.target.value)}
                    placeholder="0.0"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Group moved?{reqStar('pig-dailys', 'group_moved')}</label>
                  <YN val={s.groupMoved} onChange={(v) => setSowField(i, 'groupMoved', v)} />
                </div>
                <div>
                  <label style={labelStyle}>Nipple drinker moved?{reqStar('pig-dailys', 'nipple_drinker_moved')}</label>
                  <YN val={s.nippleDrinkerMoved} onChange={(v) => setSowField(i, 'nippleDrinkerMoved', v)} />
                </div>
                <div>
                  <label style={labelStyle}>
                    Nipple drinker working?{reqStar('pig-dailys', 'nipple_drinker_working')}
                  </label>
                  <YN val={s.nippleDrinkerWorking} onChange={(v) => setSowField(i, 'nippleDrinkerWorking', v)} />
                </div>
                <div>
                  <label style={labelStyle}>Troughs moved?{reqStar('pig-dailys', 'troughs_moved')}</label>
                  <YN val={s.troughsMoved} onChange={(v) => setSowField(i, 'troughsMoved', v)} />
                </div>
                <div style={{gridColumn: '1/-1'}}>
                  <label style={labelStyle}>Fence walked?{reqStar('pig-dailys', 'fence_walked')}</label>
                  <YN val={s.fenceWalked} onChange={(v) => setSowField(i, 'fenceWalked', v)} />
                </div>
                <div style={{gridColumn: '1/-1'}}>
                  <label style={labelStyle}>Issues / Notes{reqStar('pig-dailys', 'issues')}</label>
                  <textarea
                    disabled={submitting}
                    value={s.issues}
                    onChange={(e) => setSowField(i, 'issues', e.target.value)}
                    rows={2}
                    placeholder="Type 0 if nothing to report"
                    style={{...inputStyle, resize: 'vertical'}}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            ‹ Back
          </button>
          <PlannerIconLabel
            iconKey={ANIMAL_ICON_KEYS.pig}
            size={22}
            gap={8}
            style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}
          >
            Pig Daily Report
          </PlannerIconLabel>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Date{reqStar('pig-dailys', 'date')}</label>
                <input
                  disabled={submitting}
                  type="date"
                  value={pForm.date}
                  onChange={(e) => setPForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(pForm.date, setPForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Pig Group{reqStar('pig-dailys', 'batch_label')}</label>
                <select
                  disabled={submitting}
                  value={pForm.batchLabel}
                  onChange={(e) => {
                    const nextVal = e.target.value;
                    if (isSowsSectionMode(nextVal, recovery?.formKind === 'pig_dailys') && extraPigGroups.length > 0) {
                      setExtraPigGroups([]);
                      setErr('Additional groups were cleared: SOWS reports its three groups on its own.');
                    }
                    setPForm((f) => ({...f, batchLabel: nextVal}));
                  }}
                  style={inputStyle}
                >
                  <option value="">Select group...</option>
                  {pForm.batchLabel && !pgGroups.some((group) => group.value === pForm.batchLabel) && (
                    <option value={pForm.batchLabel}>{pForm.batchLabel} (saved)</option>
                  )}
                  {pgGroups.map((g) => (
                    <option key={String(g.value || '')} value={String(g.value || '')}>
                      {String(g.label || g.value || '')}
                    </option>
                  ))}
                </select>
                {renderDaysSinceMove(pForm.batchLabel)}
              </div>
              {!sowsMode && (
                <>
                  <div>
                    <label style={labelStyle}>Feed (lbs){reqStar('pig-dailys', 'feed_lbs')}</label>
                    <input
                      disabled={submitting}
                      type="number"
                      min="0"
                      step="0.1"
                      value={pForm.feedLbs || ''}
                      onChange={(e) => setPForm((f) => ({...f, feedLbs: e.target.value}))}
                      placeholder="0"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Pig Count{reqStar('pig-dailys', 'pig_count')}</label>
                    <input
                      disabled={submitting}
                      type="number"
                      min="0"
                      value={pForm.pigCount || ''}
                      onChange={(e) => setPForm((f) => ({...f, pigCount: e.target.value}))}
                      placeholder="0"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{gridColumn: '1/-1'}}>
                    <label style={labelStyle}>Fence Voltage (kV){reqStar('pig-dailys', 'fence_voltage')}</label>
                    <input
                      disabled={submitting}
                      type="number"
                      min="0"
                      step="0.1"
                      value={pForm.fenceVoltage || ''}
                      onChange={(e) => setPForm((f) => ({...f, fenceVoltage: e.target.value}))}
                      placeholder="0.0"
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          {sowsMode ? (
            renderSowSections()
          ) : (
            <>
              <div style={sectionStyle}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                  <div>
                    <label style={labelStyle}>Group moved?{reqStar('pig-dailys', 'group_moved')}</label>
                    <YN
                      val={groupMovedChoice(pForm.groupMoved)}
                      onChange={(v) => setPForm((f) => ({...f, groupMoved: v}))}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Nipple drinker moved?{reqStar('pig-dailys', 'nipple_drinker_moved')}
                    </label>
                    <YN
                      val={pForm.nippleDrinkerMoved}
                      onChange={(v) => setPForm((f) => ({...f, nippleDrinkerMoved: v}))}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Nipple drinker working?{reqStar('pig-dailys', 'nipple_drinker_working')}
                    </label>
                    <YN
                      val={pForm.nippleDrinkerWorking}
                      onChange={(v) => setPForm((f) => ({...f, nippleDrinkerWorking: v}))}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Troughs moved?{reqStar('pig-dailys', 'troughs_moved')}</label>
                    <YN val={pForm.troughsMoved} onChange={(v) => setPForm((f) => ({...f, troughsMoved: v}))} />
                  </div>
                  <div style={{gridColumn: '1/-1'}}>
                    <label style={labelStyle}>Fence walked?{reqStar('pig-dailys', 'fence_walked')}</label>
                    <YN val={pForm.fenceWalked} onChange={(v) => setPForm((f) => ({...f, fenceWalked: v}))} />
                  </div>
                </div>
              </div>
              <div style={sectionStyle}>
                <label style={labelStyle}>Issues / Notes{reqStar('pig-dailys', 'issues')}</label>
                <textarea
                  disabled={submitting}
                  value={pForm.issues}
                  onChange={(e) => setPForm((f) => ({...f, issues: e.target.value}))}
                  rows={3}
                  placeholder="Type 0 if nothing to report"
                  style={{...inputStyle, resize: 'vertical'}}
                />
              </div>
            </>
          )}
          {allowAddGroup('pig-dailys') && (!sowsMode || extraPigGroups.length > 0) && (
            <div>
              {extraPigGroups.map((eg, ei) => (
                <div key={ei} style={{...sectionStyle, border: '1px solid var(--border)', marginTop: 0}}>
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}
                  >
                    <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ink)'}}>Additional Group {ei + 2}</div>
                    <button
                      disabled={submitting}
                      type="button"
                      onClick={() => setExtraPigGroups((p) => p.filter((_, i) => i !== ei))}
                      style={{background: 'none', border: 'none', color: '#68707A', cursor: 'pointer', fontSize: 18}}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Pig Group{reqStar('pig-dailys', 'batch_label')}</label>
                      <select
                        disabled={submitting}
                        value={eg.batchLabel || ''}
                        onChange={(e) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, batchLabel: e.target.value} : g)))
                        }
                        style={inputStyle}
                      >
                        <option value="">Select group...</option>
                        {(recovery || localDraftCsid) &&
                          eg.batchLabel &&
                          !pgGroups.some((g) => String(g.value) === eg.batchLabel) && (
                            <option value={eg.batchLabel}>{eg.batchLabel} (saved)</option>
                          )}
                        {pgGroups
                          .filter((g) => String(g.value || '').trim() !== 'SOWS')
                          .map((g) => (
                            <option key={String(g.value || '')} value={String(g.value || '')}>
                              {String(g.label || g.value || '')}
                            </option>
                          ))}
                      </select>
                      {renderDaysSinceMove(eg.batchLabel)}
                    </div>
                    <div>
                      <label style={labelStyle}>Feed (lbs){reqStar('pig-dailys', 'feed_lbs')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.feedLbs || ''}
                        onChange={(e) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, feedLbs: e.target.value} : g)))
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Pig Count{reqStar('pig-dailys', 'pig_count')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        value={eg.pigCount || ''}
                        onChange={(e) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, pigCount: e.target.value} : g)))
                        }
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Fence Voltage (kV){reqStar('pig-dailys', 'fence_voltage')}</label>
                      <input
                        disabled={submitting}
                        type="number"
                        min="0"
                        step="0.1"
                        value={eg.fenceVoltage || ''}
                        onChange={(e) =>
                          setExtraPigGroups((p) =>
                            p.map((g, i) => (i === ei ? {...g, fenceVoltage: e.target.value} : g)),
                          )
                        }
                        placeholder="0.0"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Group moved?{reqStar('pig-dailys', 'group_moved')}</label>
                      <YN
                        val={groupMovedChoice(eg.groupMoved)}
                        onChange={(v) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, groupMoved: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Nipple drinker moved?{reqStar('pig-dailys', 'nipple_drinker_moved')}
                      </label>
                      <YN
                        val={eg.nippleDrinkerMoved !== false}
                        onChange={(v) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, nippleDrinkerMoved: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Nipple drinker working?{reqStar('pig-dailys', 'nipple_drinker_working')}
                      </label>
                      <YN
                        val={eg.nippleDrinkerWorking !== false}
                        onChange={(v) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, nippleDrinkerWorking: v} : g)))
                        }
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Troughs moved?{reqStar('pig-dailys', 'troughs_moved')}</label>
                      <YN
                        val={eg.troughsMoved !== false}
                        onChange={(v) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, troughsMoved: v} : g)))
                        }
                      />
                    </div>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Fence walked?{reqStar('pig-dailys', 'fence_walked')}</label>
                      <YN
                        val={eg.fenceWalked !== false}
                        onChange={(v) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, fenceWalked: v} : g)))
                        }
                      />
                    </div>
                    <div style={{gridColumn: '1/-1'}}>
                      <label style={labelStyle}>Issues / Notes{reqStar('pig-dailys', 'issues')}</label>
                      <textarea
                        disabled={submitting}
                        value={eg.issues || ''}
                        onChange={(e) =>
                          setExtraPigGroups((p) => p.map((g, i) => (i === ei ? {...g, issues: e.target.value} : g)))
                        }
                        rows={3}
                        placeholder="Type 0 if nothing to report"
                        style={{...inputStyle, resize: 'vertical'}}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {!sowsMode && (
                <button
                  disabled={submitting}
                  type="button"
                  onClick={() =>
                    setExtraPigGroups((p) => [
                      ...p,
                      {
                        batchLabel: '',
                        feedLbs: '',
                        pigCount: '',
                        fenceVoltage: '',
                        groupMoved: null,
                        nippleDrinkerMoved: true,
                        nippleDrinkerWorking: true,
                        troughsMoved: true,
                        fenceWalked: true,
                        issues: '',
                      },
                    ])
                  }
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 10,
                    border: '2px dashed var(--border)',
                    background: 'transparent',
                    color: 'var(--ink)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    marginBottom: 12,
                  }}
                >
                  + Add Another Group
                </button>
              )}
            </div>
          )}
          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('pig_dailys', submitPig)}
          {sowsMode && (
            <div data-pig-sow-photo-note style={{fontSize: 12, color: 'var(--ink-muted)', marginBottom: 6}}>
              Photos are attached to Sow Group 1&apos;s report.
            </div>
          )}
          <DailyPhotoCapture
            files={pPhotos}
            statuses={pPhotoStatuses}
            onChange={setPPhotos}
            disabled={submitting}
            offlineCapable={true}
          />
          <button
            onClick={submitPig}
            disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting
              ? `Submitting ${sowsMode ? SOW_GROUP_NUMBERS.length : 1 + extraPigGroups.filter((g) => g.batchLabel).length} report(s)…`
              : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );
  }

  // ── CATTLE FORM ──
  if (activeForm === 'cattle') {
    const CATTLE_HERD_OPTS = [
      {v: 'mommas', l: 'Mommas'},
      {v: 'backgrounders', l: 'Backgrounders'},
      {v: 'finishers', l: 'Finishers'},
      {v: 'bulls', l: 'Bulls'},
    ];
    const herdSelected = cForm.herd;
    // Feeds scoped to selected herd, minerals always shown for any herd
    const feedsForHerd = herdSelected
      ? cattleFeedInputs.filter((f) => f.category !== 'mineral' && (f.herd_scope || []).includes(herdSelected))
      : [];
    const mineralsAll = cattleFeedInputs.filter((f) => f.category === 'mineral');
    const unitFor = (feedId: string) => {
      const fi = cattleFeedInputs.find((x) => x.id === feedId);
      return fi ? fi.unit : '';
    };
    const showCreepToggle = herdSelected === 'mommas';
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            {'\u2039 Back'}
          </button>
          <div style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}>
            {renderCattleIconLabel('Cattle Daily Report', {size: 22})}
          </div>

          {/* Report info */}
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>
                  Date<span style={{color: '#b91c1c', marginLeft: 2}}>*</span>
                </label>
                <input
                  disabled={submitting}
                  type="date"
                  value={cForm.date}
                  onChange={(e) => setCForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(cForm.date, setCForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>
                  Cattle Group<span style={{color: '#b91c1c', marginLeft: 2}}>*</span>
                </label>
                <select
                  disabled={submitting}
                  value={cForm.herd}
                  onChange={(e) =>
                    setCForm((f) => ({
                      ...f,
                      herd: e.target.value,
                      feeds: [{feedId: '', qty: '', isCreep: false}],
                      minerals: [{feedId: '', lbs: ''}],
                    }))
                  }
                  style={inputStyle}
                >
                  <option value="">Select herd...</option>
                  {cForm.herd && !CATTLE_HERD_OPTS.some((herd) => herd.v === cForm.herd) && (
                    <option value={cForm.herd}>{cForm.herd} (saved)</option>
                  )}
                  {CATTLE_HERD_OPTS.map((h) => (
                    <option key={h.v} value={h.v}>
                      {h.l}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Feeds */}
          {herdSelected && (
            <div style={sectionStyle}>
              <div style={{fontSize: 13, fontWeight: 600, color: '#1A1F24', marginBottom: 10}}>Feed</div>
              {cForm.feeds.map((row, ri) => (
                <div
                  key={ri}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr auto',
                    gap: 8,
                    marginBottom: showCreepToggle ? 4 : 10,
                    alignItems: 'end',
                  }}
                >
                  <div>
                    {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Feed Type</label>}
                    <select
                      disabled={submitting}
                      value={row.feedId}
                      onChange={(e) =>
                        setCForm((f) => ({
                          ...f,
                          feeds: f.feeds.map((r, i) =>
                            i === ri
                              ? {...r, feedId: e.target.value, recoverySnapshot: undefined, recoveryMissingInput: false}
                              : r,
                          ),
                        }))
                      }
                      style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                    >
                      <option value="">Select feed...</option>
                      {withRecoveredInputChoice(feedsForHerd, row).map((ff) => (
                        <option key={ff.id} value={ff.id}>
                          {ff.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {ri === 0 && (
                      <label style={{...labelStyle, fontSize: 12}}>
                        {row.feedId ? 'Qty (' + (row.recoverySnapshot?.unit || unitFor(row.feedId)) + ')' : 'Qty'}
                      </label>
                    )}
                    <input
                      disabled={submitting}
                      type="number"
                      min="0"
                      step="0.1"
                      value={row.qty}
                      onChange={(e) =>
                        setCForm((f) => ({
                          ...f,
                          feeds: f.feeds.map((r, i) => (i === ri ? {...r, qty: e.target.value} : r)),
                        }))
                      }
                      placeholder="0"
                      style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                    />
                  </div>
                  <div>
                    {ri === 0 && <div style={{fontSize: 12, marginBottom: 4, opacity: 0}}>.</div>}
                    {cForm.feeds.length > 1 ? (
                      <button
                        disabled={submitting}
                        type="button"
                        onClick={() => setCForm((f) => ({...f, feeds: f.feeds.filter((_, i) => i !== ri)}))}
                        style={{
                          padding: '8px 10px',
                          border: '1px solid #D2D6DB',
                          borderRadius: 10,
                          background: 'white',
                          color: '#68707A',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 14,
                        }}
                      >
                        {'\u00d7'}
                      </button>
                    ) : (
                      <div style={{width: 38}} />
                    )}
                  </div>
                  {showCreepToggle && row.feedId && (
                    <label
                      style={{
                        gridColumn: '1/-1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                        color: '#7f1d1d',
                        cursor: 'pointer',
                        marginBottom: 6,
                        userSelect: 'none',
                      }}
                    >
                      <input
                        disabled={submitting}
                        type="checkbox"
                        checked={!!row.isCreep}
                        onChange={(e) =>
                          setCForm((f) => ({
                            ...f,
                            feeds: f.feeds.map((r, i) => (i === ri ? {...r, isCreep: e.target.checked} : r)),
                          }))
                        }
                        style={{cursor: 'pointer'}}
                      />
                      This was creep feed (for calves) {'\u2014'} counts for cost, not Mommas nutrition
                    </label>
                  )}
                </div>
              ))}
              <button
                disabled={submitting}
                type="button"
                onClick={() => setCForm((f) => ({...f, feeds: [...f.feeds, {feedId: '', qty: '', isCreep: false}]}))}
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 10,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  color: 'var(--ink)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginTop: 6,
                }}
              >
                + Add Feed
              </button>
            </div>
          )}

          {/* Minerals */}
          {herdSelected && (
            <div style={sectionStyle}>
              <div style={{fontSize: 13, fontWeight: 600, color: '#1A1F24', marginBottom: 10}}>Minerals</div>
              {cForm.minerals.map((row, ri) => (
                <div
                  key={ri}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr auto',
                    gap: 8,
                    marginBottom: 10,
                    alignItems: 'end',
                  }}
                >
                  <div>
                    {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Mineral</label>}
                    <select
                      disabled={submitting}
                      value={row.feedId}
                      onChange={(e) =>
                        setCForm((f) => ({
                          ...f,
                          minerals: f.minerals.map((r, i) =>
                            i === ri
                              ? {...r, feedId: e.target.value, recoverySnapshot: undefined, recoveryMissingInput: false}
                              : r,
                          ),
                        }))
                      }
                      style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                    >
                      <option value="">Select mineral...</option>
                      {withRecoveredInputChoice(mineralsAll, row).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Lbs</label>}
                    <input
                      disabled={submitting}
                      type="number"
                      min="0"
                      step="0.1"
                      value={row.lbs}
                      onChange={(e) =>
                        setCForm((f) => ({
                          ...f,
                          minerals: f.minerals.map((r, i) => (i === ri ? {...r, lbs: e.target.value} : r)),
                        }))
                      }
                      placeholder="0"
                      style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                    />
                  </div>
                  <div>
                    {ri === 0 && <div style={{fontSize: 12, marginBottom: 4, opacity: 0}}>.</div>}
                    {cForm.minerals.length > 1 ? (
                      <button
                        disabled={submitting}
                        type="button"
                        onClick={() => setCForm((f) => ({...f, minerals: f.minerals.filter((_, i) => i !== ri)}))}
                        style={{
                          padding: '8px 10px',
                          border: '1px solid #D2D6DB',
                          borderRadius: 10,
                          background: 'white',
                          color: '#68707A',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 14,
                        }}
                      >
                        {'\u00d7'}
                      </button>
                    ) : (
                      <div style={{width: 38}} />
                    )}
                  </div>
                </div>
              ))}
              <button
                disabled={submitting}
                type="button"
                onClick={() => setCForm((f) => ({...f, minerals: [...f.minerals, {feedId: '', lbs: ''}]}))}
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 10,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  color: 'var(--ink)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginTop: 6,
                }}
              >
                + Add Mineral
              </button>
            </div>
          )}

          {/* Daily checks */}
          {herdSelected && (
            <div style={sectionStyle}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                <div>
                  <label style={labelStyle}>Fence Voltage (kV)</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    step="0.1"
                    value={cForm.fenceVoltage}
                    onChange={(e) => setCForm((f) => ({...f, fenceVoltage: e.target.value}))}
                    placeholder="0.0"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Waterers checked?</label>
                  <YN val={cForm.waterChecked} onChange={(v) => setCForm((f) => ({...f, waterChecked: v}))} />
                </div>
              </div>
            </div>
          )}

          {/* Issues */}
          {herdSelected && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Issues / Comments</label>
              <textarea
                disabled={submitting}
                value={cForm.issues}
                onChange={(e) => setCForm((f) => ({...f, issues: e.target.value}))}
                rows={3}
                placeholder="Type 0 if nothing to report"
                style={{...inputStyle, resize: 'vertical'}}
              />
            </div>
          )}

          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('cattle_dailys', null)}

          <DailyPhotoCapture
            files={cPhotos}
            statuses={cPhotoStatuses}
            onChange={setCPhotos}
            disabled={submitting}
            offlineCapable={true}
          />
          <button
            onClick={submitCattle}
            disabled={submitting || !herdSelected}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting || !herdSelected ? 'not-allowed' : 'pointer',
              opacity: submitting || !herdSelected ? 0.5 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting ? 'Submitting\u2026' : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );
  }

  // ── EGG FORM ──
  if (activeForm === 'egg')
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            ‹ Back
          </button>
          <PlannerIconLabel
            iconKey={ANIMAL_ICON_KEYS.egg}
            size={22}
            gap={8}
            style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}
          >
            Egg Collection Report
          </PlannerIconLabel>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Date{reqStar('egg-dailys', 'date')}</label>
                <input
                  disabled={submitting}
                  type="date"
                  value={eForm.date}
                  onChange={(e) => setEForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(eForm.date, setEForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={{fontSize: 13, fontWeight: 600, color: '#1A1F24', marginBottom: 10}}>
              Egg Collection by Group
            </div>
            {[
              {n: 'g1n', c: 'g1c', label: 'Group 1'},
              {n: 'g2n', c: 'g2c', label: 'Group 2'},
              {n: 'g3n', c: 'g3c', label: 'Group 3'},
              {n: 'g4n', c: 'g4c', label: 'Group 4'},
            ].map(({n, c, label}) => (
              <div key={n} style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10}}>
                <div>
                  <label style={{...labelStyle, fontSize: 12}}>{label} Name</label>
                  <select
                    disabled={submitting}
                    value={eForm[n]}
                    onChange={(e) => setEForm((f) => ({...f, [n]: e.target.value}))}
                    style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                  >
                    <option value="">—</option>
                    {(recovery || localDraftCsid) && eForm[n] && !eggGroupNames.includes(eForm[n]) && (
                      <option value={eForm[n]}>{eForm[n]} (saved)</option>
                    )}
                    {eggGroupNames.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  {n === 'g1n' && layerGroupsNotice}
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: 12}}>Eggs</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    value={eForm[c]}
                    onChange={(e) => setEForm((f) => ({...f, [c]: e.target.value}))}
                    placeholder="0"
                    style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={sectionStyle}>
            <label style={labelStyle}>Dozens on Hand{reqStar('egg-dailys', 'dozens_on_hand')}</label>
            <input
              disabled={submitting}
              type="number"
              min="0"
              step="0.5"
              value={eForm.dozensOnHand || ''}
              onChange={(e) => setEForm((f) => ({...f, dozensOnHand: e.target.value}))}
              placeholder="0"
              style={inputStyle}
            />
          </div>
          <div style={sectionStyle}>
            <label style={labelStyle}>
              Dozens Allocated for Coastal Rancher{reqStar('egg-dailys', 'dozens_allocated_coastal_rancher')}
            </label>
            <input
              disabled={submitting}
              type="number"
              min="0"
              step="0.5"
              value={eForm.dozensAllocatedCoastalRancher ?? ''}
              onChange={(e) => setEForm((f) => ({...f, dozensAllocatedCoastalRancher: e.target.value}))}
              placeholder="0"
              style={inputStyle}
            />
          </div>
          <div style={sectionStyle}>
            <label style={labelStyle}>Comments{reqStar('egg-dailys', 'comments')}</label>
            <textarea
              disabled={submitting}
              value={eForm.comments}
              onChange={(e) => setEForm((f) => ({...f, comments: e.target.value}))}
              rows={3}
              placeholder="Type 0 if nothing to report"
              style={{...inputStyle, resize: 'vertical'}}
            />
          </div>
          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('egg_dailys', null)}
          <button
            onClick={submitEgg}
            disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );

  // ── SHEEP FORM ──
  if (activeForm === 'sheep')
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto'}}>
          {logo}
          {draftStatusEl}
          {recoveryBanner}
          <button
            disabled={submitting}
            onClick={() => switchForm(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#68707A',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            ‹ Back
          </button>
          <PlannerIconLabel
            iconKey={ANIMAL_ICON_KEYS.sheep}
            size={22}
            gap={8}
            style={{fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 16}}
          >
            Sheep Daily Report
          </PlannerIconLabel>
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>Date{reqStar('sheep-dailys', 'date')}</label>
                <input
                  disabled={submitting}
                  type="date"
                  value={sForm.date}
                  onChange={(e) => setSForm((f) => ({...f, date: e.target.value}))}
                  style={inputStyle}
                />
                {renderUseToday(sForm.date, setSForm)}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <LockedSubmitter name={lockedName} label="Team member" labelStyle={labelStyle} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={labelStyle}>
                  Flock<span style={{color: '#dc2626', marginLeft: 2}}>*</span>
                </label>
                <select
                  disabled={submitting}
                  value={sForm.flock}
                  onChange={(e) => setSForm((f) => ({...f, flock: e.target.value}))}
                  style={inputStyle}
                >
                  <option value="">Select flock...</option>
                  {sForm.flock && !['rams', 'ewes', 'feeders'].includes(sForm.flock) && (
                    <option value={sForm.flock}>{sForm.flock} (saved)</option>
                  )}
                  <option value="rams">Rams</option>
                  <option value="ewes">Ewes</option>
                  <option value="feeders">Feeders</option>
                </select>
              </div>
            </div>
          </div>
          {/* Feeds — multi-row picker; mirrors cattle. Filtered to sheep herd_scope. */}
          {sForm.flock &&
            (() => {
              const feedsForFlock = cattleFeedInputs.filter(
                (f) => f.category !== 'mineral' && (f.herd_scope || []).includes(sForm.flock),
              );
              const unitFor = (id: string) => {
                const fi = cattleFeedInputs.find((x) => x.id === id);
                return fi ? fi.unit : '';
              };
              return (
                <div style={sectionStyle}>
                  <div style={{fontSize: 13, fontWeight: 600, color: '#1A1F24', marginBottom: 10}}>Feed</div>
                  {sForm.feeds.map((row, ri) => (
                    <div
                      key={ri}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr auto',
                        gap: 8,
                        marginBottom: 10,
                        alignItems: 'end',
                      }}
                    >
                      <div>
                        {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Feed Type</label>}
                        <select
                          disabled={submitting}
                          value={row.feedId}
                          onChange={(e) =>
                            setSForm((f) => ({
                              ...f,
                              feeds: f.feeds.map((r, i) =>
                                i === ri
                                  ? {
                                      ...r,
                                      feedId: e.target.value,
                                      recoverySnapshot: undefined,
                                      recoveryMissingInput: false,
                                    }
                                  : r,
                              ),
                            }))
                          }
                          style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                        >
                          <option value="">Select feed...</option>
                          {withRecoveredInputChoice(feedsForFlock, row).map((ff) => (
                            <option key={ff.id} value={ff.id}>
                              {ff.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        {ri === 0 && (
                          <label style={{...labelStyle, fontSize: 12}}>
                            {row.feedId ? 'Qty (' + (row.recoverySnapshot?.unit || unitFor(row.feedId)) + ')' : 'Qty'}
                          </label>
                        )}
                        <input
                          disabled={submitting}
                          type="number"
                          min="0"
                          step="0.1"
                          value={row.qty}
                          onChange={(e) =>
                            setSForm((f) => ({
                              ...f,
                              feeds: f.feeds.map((r, i) => (i === ri ? {...r, qty: e.target.value} : r)),
                            }))
                          }
                          placeholder="0"
                          style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                        />
                      </div>
                      <div>
                        {ri === 0 && <div style={{fontSize: 12, marginBottom: 4, opacity: 0}}>.</div>}
                        {sForm.feeds.length > 1 ? (
                          <button
                            disabled={submitting}
                            type="button"
                            onClick={() => setSForm((f) => ({...f, feeds: f.feeds.filter((_, i) => i !== ri)}))}
                            style={{
                              padding: '8px 10px',
                              border: '1px solid #D2D6DB',
                              borderRadius: 10,
                              background: 'white',
                              color: '#68707A',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontSize: 14,
                            }}
                          >
                            {'×'}
                          </button>
                        ) : (
                          <div style={{width: 38}} />
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    disabled={submitting}
                    type="button"
                    onClick={() => setSForm((f) => ({...f, feeds: [...f.feeds, {feedId: '', qty: ''}]}))}
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 10,
                      border: '2px dashed var(--border)',
                      background: 'transparent',
                      color: 'var(--ink)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      marginTop: 6,
                    }}
                  >
                    + Add Feed
                  </button>
                </div>
              );
            })()}

          {/* Minerals — multi-row picker. */}
          {sForm.flock &&
            (() => {
              const mineralsForFlock = cattleFeedInputs.filter(
                (f) => f.category === 'mineral' && (f.herd_scope || []).includes(sForm.flock),
              );
              return (
                <div style={sectionStyle}>
                  <div style={{fontSize: 13, fontWeight: 600, color: '#1A1F24', marginBottom: 10}}>Minerals</div>
                  {sForm.minerals.map((row, ri) => (
                    <div
                      key={ri}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr auto',
                        gap: 8,
                        marginBottom: 10,
                        alignItems: 'end',
                      }}
                    >
                      <div>
                        {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Mineral</label>}
                        <select
                          disabled={submitting}
                          value={row.feedId}
                          onChange={(e) =>
                            setSForm((f) => ({
                              ...f,
                              minerals: f.minerals.map((r, i) =>
                                i === ri
                                  ? {
                                      ...r,
                                      feedId: e.target.value,
                                      recoverySnapshot: undefined,
                                      recoveryMissingInput: false,
                                    }
                                  : r,
                              ),
                            }))
                          }
                          style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                        >
                          <option value="">Select mineral...</option>
                          {withRecoveredInputChoice(mineralsForFlock, row).map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        {ri === 0 && <label style={{...labelStyle, fontSize: 12}}>Lbs</label>}
                        <input
                          disabled={submitting}
                          type="number"
                          min="0"
                          step="0.1"
                          value={row.lbs}
                          onChange={(e) =>
                            setSForm((f) => ({
                              ...f,
                              minerals: f.minerals.map((r, i) => (i === ri ? {...r, lbs: e.target.value} : r)),
                            }))
                          }
                          placeholder="0"
                          style={{...inputStyle, fontSize: 13, padding: '8px 10px'}}
                        />
                      </div>
                      <div>
                        {ri === 0 && <div style={{fontSize: 12, marginBottom: 4, opacity: 0}}>.</div>}
                        {sForm.minerals.length > 1 ? (
                          <button
                            disabled={submitting}
                            type="button"
                            onClick={() => setSForm((f) => ({...f, minerals: f.minerals.filter((_, i) => i !== ri)}))}
                            style={{
                              padding: '8px 10px',
                              border: '1px solid #D2D6DB',
                              borderRadius: 10,
                              background: 'white',
                              color: '#68707A',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontSize: 14,
                            }}
                          >
                            {'×'}
                          </button>
                        ) : (
                          <div style={{width: 38}} />
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    disabled={submitting}
                    type="button"
                    onClick={() => setSForm((f) => ({...f, minerals: [...f.minerals, {feedId: '', lbs: ''}]}))}
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 10,
                      border: '2px dashed var(--border)',
                      background: 'transparent',
                      color: 'var(--ink)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      marginTop: 6,
                    }}
                  >
                    + Add Mineral
                  </button>
                </div>
              );
            })()}
          <div style={sectionStyle}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
              {isEnabled('sheep-dailys', 'fence_voltage_kv') && (
                <div>
                  <label style={labelStyle}>Fence Voltage (kV){reqStar('sheep-dailys', 'fence_voltage_kv')}</label>
                  <input
                    disabled={submitting}
                    type="number"
                    min="0"
                    step="0.1"
                    value={sForm.fenceVoltageKv || ''}
                    onChange={(e) => setSForm((f) => ({...f, fenceVoltageKv: e.target.value}))}
                    placeholder="0.0"
                    style={inputStyle}
                  />
                </div>
              )}
              {isEnabled('sheep-dailys', 'waterers_working') && (
                <div>
                  <label style={labelStyle}>Waterers working?{reqStar('sheep-dailys', 'waterers_working')}</label>
                  <YN val={sForm.waterersWorking} onChange={(v) => setSForm((f) => ({...f, waterersWorking: v}))} />
                </div>
              )}
            </div>
          </div>
          {isEnabled('sheep-dailys', 'comments') && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Issues / Comments{reqStar('sheep-dailys', 'comments')}</label>
              <textarea
                disabled={submitting}
                value={sForm.comments}
                onChange={(e) => setSForm((f) => ({...f, comments: e.target.value}))}
                rows={3}
                placeholder="Type 0 if nothing to report"
                style={{...inputStyle, resize: 'vertical'}}
              />
            </div>
          )}
          {err && (
            <div
              style={{
                color: '#b91c1c',
                fontSize: 13,
                marginBottom: 10,
                padding: '8px 12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              {err}
            </div>
          )}
          {renderDuplicateNotice('sheep_dailys', null)}
          <DailyPhotoCapture
            files={shPhotos}
            statuses={shPhotoStatuses}
            onChange={setShPhotos}
            disabled={submitting}
            offlineCapable={true}
          />
          <button
            onClick={submitSheep}
            disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 10,
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
        {stuckModalEl}
      </div>
    );
};

export default WebformHub;
